import React, { FunctionComponent, useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { CircleAlert, CircleCheck, CircleX, Clock, Download, Mail, MailX, MessageSquare } from 'lucide-react';
import { styles } from '../../../../styles/themes/style';
import {
  ICampaign, ICampaignLead, ICampaignLeadStatusCounts, ICampaignMessagingStats, ILeadPreferences,
  CampaignLeadStatus, CampaignLeadSource, CampaignStatus, STEP_STATUS, DEFAULT_SEQUENCE,
  OUTCOME_LEAD_STATUSES, STATUS_COLORS, LEAD_STATUS_LABELS, LEAD_STATUS_HINTS, emptyLeadStatusCounts, emptyMessagingStats, EMPTY_LEAD_PREFERENCES,
  CAMPAIGN_START_NO_LEADS_HINT, sumEnrolledLeads, REPLY_STATUS_FOR_STEP,
  resolveCampaignSequence, getUpcomingTextStep, leadHasStepContentOverride, ISequenceStep,
  SuppressionType,
} from '../../../../interfaces/models/campaign';
import { useCampaignsRequest, IProxyAccountDetails } from '../../../../services/apis/campaigns/useCampaignsRequest';
import { useAppActions } from '../../../../stores/application/useAppActions';
import Column from '../../../../components/Column';
import Row from '../../../../components/Row';
import Text from '../../../../components/Text';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import SvgFragment from '../../../../fragments/SvgFragment';
import { AppRoutes } from '../../../../interfaces/store/appStore';
import { PROXY_UI_POLL_MS, SIM_EVENT } from '../../hooks/useCampaignSimulation';
import CampaignAccountLimitBanner from './CampaignAccountLimitBanner';
import CampaignAccountReconnectBanner from './CampaignAccountReconnectBanner';
import CampaignEmailCoverage from './CampaignEmailCoverage';
import CampaignStartPanel from './CampaignStartPanel';
import { CAMPAIGN_WIZARD_STEP_RECIPIENTS } from './CampaignWizardStepper';
import CampaignLeadSidebar, { leadSuppressionTitle } from './CampaignLeadSidebar';
import PerformanceFilters, { ALL_TIME_RANGE, IDateRange, LEAD_DATE_PRESETS } from './performance/PerformanceFilters';
import { IPerformanceMetrics, metricsForSequence, PerformanceMetric } from './performance/performanceMetrics';
import { download, toCsv } from './performance/exportAnalytics';
import LeadMessageEditModal from './LeadMessageEditModal';
import CampaignContactsFilterBar from './CampaignContactsFilterBar';
import SelectAllCheckbox from '../../../../components/SelectAllCheckbox';
import Checkbox from '../../../../components/Checkbox';
import TooltipWrapper from '../../../../components/TooltipWrapper';
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers';
import { useAppStore } from '../../../../stores/application/useAppStore';
import { useAuthStore } from '../../../../stores/auth/useAuthStore';
import { useConnectionsHandlers } from '../../../../services/actions/connections/useConnectionsHandlers';
import { useConnectionsStore } from '../../../../stores/connections/useConnectionsStore';
import { useConnectionsActions } from '../../../../stores/connections/useConnectionsActions';
import { campaignMobile } from '../../utils/campaignBreakpoints';
import { parseCsvText } from '../../utils/campaignCsvUtils';
import { parseLinkedinPostInput } from '../../utils/linkedinPostUrl';
import { commentersToLeadRows, fetchAllPostCommenters, fetchAllPostCommentersUnipile } from '../../utils/postCommenters';
import { TSalesNavLeadPreview } from '../../utils/salesNavListQuery';
import { fetchSalesNavListLeads, fetchSalesNavUrlLeadsUnipile, formatSnListProgress } from '../../utils/salesNavListFetch';
import { classicLinkedInProfileUrl, partitionSnLeadsForImport } from '../../utils/salesNavListLeads';
import { splitInMailCreditBalance, summarizeInMailCredits } from '../../utils/inMailCredits';
import { useExtensionRequest } from '../../../../services/apis/extension/useExtensionRequest';
import { describeContactsEnrollment, filterEnrollableCampaigns } from '../../../../utils/campaignContactUtils';
import LeadsPagination from './LeadsPagination';

const DEFAULT_LEADS_PAGE_SIZE = 20;

// Targeting filters are clicked in bursts — a role, then a location, then a company size.
// Each change re-queries the provider, so we wait for the burst to settle instead of
// paying for every intermediate combination.
const PREFS_REFETCH_DEBOUNCE_MS = 1100;

// Rejecting candidates happens in runs of clicks — refill once the run stops, not per row.
const PROSPECT_REFILL_DEBOUNCE_MS = 700;

/** Why a candidate search stopped — see runProspectSearch. */
type ProspectSearchOutcome = 'ok' | 'superseded' | 'failed' | 'skipped';

// Row ceiling for one CSV import — the only cap on CSV enrollment. Independent of
// campaign.leadLimit, which governs LeadSearch fetching. Mirrors CSV_MAX_ROWS on the API.
const CSV_MAX_ROWS = 20000;

function isValidSalesNavigatorSearchUrl(value: string): boolean {
  try {
    const url = new URL(value.trim());
    return /(^|\.)linkedin\.com$/i.test(url.hostname)
      && /^\/sales\/search\/people\/?$/i.test(url.pathname)
      && (!!url.searchParams.get('query') || !!url.searchParams.get('recentSearchId'));
  } catch {
    return false;
  }
}

// ─── Status config ───────────────────────────────────────────────────────────

const STATUS_LABELS = LEAD_STATUS_LABELS;
const STATUS_CFG = Object.fromEntries(
  (Object.keys(STATUS_LABELS) as CampaignLeadStatus[]).map(s => [s, {
    label: STATUS_LABELS[s], bg: STATUS_COLORS[s].bg, color: STATUS_COLORS[s].color, dot: STATUS_COLORS[s].color,
  }]),
) as Record<CampaignLeadStatus, { label: string; bg: string; color: string; dot: string }>;

/** Failed leads, and suppressed ones whose suppression-list entry is gone (the API checks). */
const isRetryable = (l: ICampaignLead) => l.status === 'failed' || l.status === 'suppressed';

const SOURCE_CFG: Record<CampaignLeadSource, { label: string; color: string; bg: string }> = {
  leadsearch: { label: 'LeadSearch', color: styles.colors.primary600, bg: styles.colors.primary100 },
  contacts:   { label: 'LD Contact', color: styles.colors.green600,   bg: styles.colors.green200   },
  csv:        { label: 'CSV',        color: styles.colors.orange600,   bg: styles.colors.yellow200  },
  posts:      { label: 'Post',       color: styles.colors.primary600, bg: styles.colors.primary100 },
  sn_lists:   { label: 'SN List',    color: styles.colors.primary600, bg: styles.colors.primary100 },
};

// ─── Source tabs ─────────────────────────────────────────────────────────────
type SourceTab = 'all' | 'leadsearch' | 'contacts' | 'csv' | 'posts' | 'sn_lists';

// ─── Status picker ───────────────────────────────────────────────────────────
// Note: the backend may return legacy statuses (e.g. older deployments) that are
// not in the current CampaignLeadStatus union. Never crash the UI for that.
const StatusPicker: React.FC<{
  value: CampaignLeadStatus | string;
  onChange: (s: CampaignLeadStatus) => void;
  /** Hover text of a suppressed lead's badge: which list, and why. */
  suppressedTitle?: string;
}> = ({ value, onChange, suppressedTitle }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (!wrapRef.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const cfg =
    (STATUS_CFG as Record<string, { label: string; bg: string; color: string; dot: string }>)[value] ??
    {
      label: typeof value === 'string' && value.trim() ? value : 'Unknown',
      bg: STATUS_COLORS.pending.bg,
      color: STATUS_COLORS.pending.color,
      dot: STATUS_COLORS.pending.color,
    };
  const handleOpen = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: r.bottom + 4, left: r.left });
    }
    setOpen(o => !o);
  };
  // Set only by the suppression list and left only through Retry — nothing to pick here.
  if (value === 'suppressed') {
    return (
      <StatusWrap>
        <StatusBadgeBtn type="button" $bg={cfg.bg} $color={cfg.color} disabled title={suppressedTitle || 'On the suppression list'}>
          <StatusDot $color={cfg.dot} />{cfg.label}
        </StatusBadgeBtn>
      </StatusWrap>
    );
  }
  return (
    <StatusWrap ref={wrapRef}>
      <StatusBadgeBtn ref={btnRef} type="button" $bg={cfg.bg} $color={cfg.color} onClick={handleOpen}>
        <StatusDot $color={cfg.dot} />{cfg.label}<StatusChevron>▾</StatusChevron>
      </StatusBadgeBtn>
      {open && (
        <StatusMenu style={{ top: menuPos.top, left: menuPos.left }}>
          {OUTCOME_LEAD_STATUSES.map(s => (
            <StatusMenuItem key={s} $active={s === value} onClick={() => { onChange(s); setOpen(false); }}>
              <StatusDot $color={STATUS_CFG[s].dot} />{STATUS_CFG[s].label}
            </StatusMenuItem>
          ))}
        </StatusMenu>
      )}
    </StatusWrap>
  );
};

// Bulk header control — same outcome options as per-row StatusPicker.
const BulkStatusHeaderPicker: React.FC<{
  disabled?: boolean;
  onSelect: (s: CampaignLeadStatus) => void;
}> = ({ disabled, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (!wrapRef.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const handleOpen = () => {
    if (disabled) return;
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: r.bottom + 4, left: r.left });
    }
    setOpen(o => !o);
  };
  return (
    <StatusWrap ref={wrapRef}>
      <StatusBadgeBtn
        ref={btnRef}
        type="button"
        $bg={styles.colors.black100}
        $color={styles.colors.black500}
        onClick={handleOpen}
        disabled={disabled}
      >
        Change status<StatusChevron>▾</StatusChevron>
      </StatusBadgeBtn>
      {open && (
        <StatusMenu style={{ top: menuPos.top, left: menuPos.left }}>
          {OUTCOME_LEAD_STATUSES.map(s => (
            <StatusMenuItem key={s} $active={false} onClick={() => { onSelect(s); setOpen(false); }}>
              <StatusDot $color={STATUS_CFG[s].dot} />{STATUS_CFG[s].label}
            </StatusMenuItem>
          ))}
        </StatusMenu>
      )}
    </StatusWrap>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
interface Props {
  campaign: ICampaign & { _autoFetch?: boolean };
  onCampaignUpdate: (updated: ICampaign) => void;
  onClose: () => void;
  fullPage?: boolean;
  highlightStartPanel?: boolean;
  /** Wizard recipients step — single source panel, no tab bar. */
  embedded?: boolean;
  embeddedSource?: Exclude<SourceTab, 'all'>;
  /** Live wizard prefs (may differ from saved campaign until draft save). Drives auto-fetch. */
  liveLeadPreferences?: ILeadPreferences;
  onEnrolledTotalChange?: (total: number) => void;
  /** The campaign page draws its own header (name, Edit) above the tabs. */
  hideHeader?: boolean;
  /** Opens filtered to one analytics metric's leads — the analytics cards' arrow. */
  initialActivity?: { metric: PerformanceMetric; range: IDateRange } | null;
}

const CampaignLeadsView: FunctionComponent<Props> = ({
  campaign, onCampaignUpdate, onClose, fullPage = false, highlightStartPanel = false,
  embedded = false, embeddedSource, liveLeadPreferences, onEnrolledTotalChange, hideHeader = false,
  initialActivity = null,
}) => {
  const navigate = useNavigate();
  const { getCampaigns, getCampaignLeads, fetchAndEnrollLeads, searchProspects, enrollFromProspects, checkEnrolledIds, searchContacts, enrollFromContacts, searchSalesNavigator, importPostCommenters, enrollFromCsv, updateLeadStatus, removeLead, bulkRemoveLeads, retryLead, bulkRetryLeads, bulkResendInMail, bulkUpdateLeadStatus, updateLeadEmail, updateCampaign, getProxyAccounts } = useCampaignsRequest();
  const { getProfilePostComments, resolveLinkedinPostUrns, getSalesLists, searchLeadsSN } = useExtensionRequest();
  const { setToast } = useAppActions();
  const { setDestructiveModal, setConfirmationModal, setMoveToCampaignModal, setAddToSuppressionListModal, hideModal } = useAppHandlers();
  const { getMappedParameters, checkPostCommentersAccessHandler } = useConnectionsHandlers();
  const { setConnectionsFilterScope } = useConnectionsActions();
  const { store: { campaignConnectionsFilter } } = useConnectionsStore();
  const [campaignFilterActive, setCampaignFilterActive] = useState(false);

  useLayoutEffect(() => {
    setConnectionsFilterScope('campaign');
    setCampaignFilterActive(true);
    return () => setConnectionsFilterScope('contacts');
  }, []);
  const { store: { extensionInstalled } } = useAppStore();
  const { store: { isLoggedToLinkedin, user, trial } } = useAuthStore();

  // Campaigns are already plan-gated; only trial needs a shared post-import quota UI.
  type TPostCommentersAccess = {
    allowed?: boolean;
    reason?: string;
    isTrial?: boolean;
    limit?: number | null;
    remaining?: number | null;
    message?: string | null;
  };
  const [postCommentersAccess, setPostCommentersAccess] = useState<TPostCommentersAccess | null>(null);

  useEffect(() => {
    if (!trial) {
      setPostCommentersAccess(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const result = await checkPostCommentersAccessHandler();
      if (cancelled) return;
      const access = result?.postCommentersAccess;
      if (access?.isTrial) setPostCommentersAccess(access);
    })();
    return () => { cancelled = true; };
  }, [trial]); // eslint-disable-line react-hooks/exhaustive-deps

  const postsTrialLimitReached = !!trial
    && postCommentersAccess?.isTrial
    && postCommentersAccess?.reason === 'trial_limit';
  const postsTrialHint = postCommentersAccess?.isTrial
    && postCommentersAccess.limit != null
    && postCommentersAccess.remaining != null
    && !postsTrialLimitReached
    ? `${postCommentersAccess.remaining} of ${postCommentersAccess.limit} trial imports left`
    : null;
  const isOwner = !!user?._id && String(user._id) === String(campaign.createdBy);

  // Other campaigns for "Move to…". Loaded with the page so the dropdown is ready
  // when it opens, instead of fetching only after the click.
  const [moveTargetCampaigns, setMoveTargetCampaigns] = useState<ICampaign[]>([]);
  const [moveTargetsReady, setMoveTargetsReady] = useState(false);
  const getCampaignsRef = useRef(getCampaigns);
  getCampaignsRef.current = getCampaigns;

  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    setMoveTargetsReady(false);
    setMoveTargetCampaigns([]);
    (async () => {
      const data = await getCampaignsRef.current('get-campaigns-move-targets');
      if (cancelled || !Array.isArray(data?.campaigns)) return;
      setMoveTargetCampaigns(
        filterEnrollableCampaigns(data.campaigns, user?._id)
          .filter(item => item._id !== campaign._id),
      );
      setMoveTargetsReady(true);
    })();
    return () => { cancelled = true; };
  }, [isOwner, campaign._id, user?._id]);
  const isProxyCampaign = (campaign.executionMethod ?? 'extension') === 'proxy';
  const hasUnipileAccount = (campaign.unipileConfig?.accountIds?.length ?? 0) > 0;
  /** Extension can load SN saved lists + search URL (Voyager). Prefer this when available, even on proxy campaigns. */
  const snExtensionReady = !!extensionInstalled && !!isLoggedToLinkedin;
  /** Proxy without extension still imports search URLs via Unipile. */
  const canSnUrlViaUnipile = isProxyCampaign && hasUnipileAccount;
  const canSnImportUrl = snExtensionReady || canSnUrlViaUnipile;
  const showSnListsUi = !isProxyCampaign || !!extensionInstalled;

  // Hidden only in the wizard, where the job is assembling a recipient list and every
  // lead is still "Pending". On the campaign page it stays regardless of campaign status:
  // a draft is where the user reviews and prunes the list, and marking a lead there is a
  // legitimate edit.
  const showLeadStatus = !embedded;

  const ownerActionTooltip = isOwner
    ? ''
    : `Only ${campaign.createdByName || 'the campaign creator'} can edit this campaign or add leads.`;
  const guardOwner = (): boolean => {
    if (isOwner) return true;
    setToast({ type: 'error', message: ownerActionTooltip });
    return false;
  };
  // Non-creators are confined to the read-only "In Campaign" view — never the add-lead source tabs.
  React.useEffect(() => {
    if (!isOwner) setSourceTab('all');
  }, [isOwner, campaign._id]);

  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!fullPage) return;
    if (!campaign.runTimer?.running) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [fullPage, campaign.runTimer?.running]);

  const runElapsedMs = React.useMemo(() => {
    const rt = campaign.runTimer;
    if (!rt) return 0;
    const base = Math.max(0, rt.elapsedMs || 0);
    if (!rt.running || !rt.startedAt) return base;
    const started = new Date(rt.startedAt).getTime();
    if (!Number.isFinite(started)) return base;
    return base + Math.max(0, nowMs - started);
  }, [campaign.runTimer, nowMs]);

  const runTimeLabel = React.useMemo(() => {
    const ms = Math.max(0, runElapsedMs);
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    const pad = (n: number) => String(n).padStart(2, '0');
    return days > 0
      ? `${days}d ${pad(hours)}:${pad(mins)}:${pad(secs)}`
      : `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
  }, [runElapsedMs]);

  // ── Table state
  const [leads, setLeads]               = useState<ICampaignLead[]>([]);
  const [total, setTotal]               = useState(0);
  const [statusCounts, setStatusCounts] = useState<ICampaignLeadStatusCounts>(() => ({
    ...emptyLeadStatusCounts(),
    ...(campaign.statusCounts ?? {}),
  }));
  const [messagingStats, setMessagingStats] = useState<ICampaignMessagingStats>(() => ({
    ...emptyMessagingStats(),
    ...(campaign.messagingStats ?? {}),
  }));
  const [resendingInMail, setResendingInMail] = useState(false);
  // ── LeadSearch picker: candidates from the provider, none of them enrolled yet
  const [leadSearchSubTab, setLeadSearchSubTab] = useState<'search' | 'enrolled'>('search');
  const [prospectResults, setProspectResults] = useState<any[]>([]);
  const [prospectsSearched, setProspectsSearched] = useState(false);
  const [searchingProspects, setSearchingProspects] = useState(false);
  const [selectedProspectIds, setSelectedProspectIds] = useState<Set<string>>(new Set());
  const [prospectPage, setProspectPage] = useState(1);
  const [prospectPageSize, setProspectPageSize] = useState(DEFAULT_LEADS_PAGE_SIZE);
  const [prospectNameFilter, setProspectNameFilter] = useState('');
  const [searchError, setSearchError] = useState<string | null>(null);
  const [lastSearchStats, setLastSearchStats] = useState<{
    skippedConnections: number;
    skippedSuppressed: number;
    alreadyEnrolled: number;
    hasMore: boolean;
    exhausted: boolean;
    remaining: number;
  } | null>(null);
  // Candidates the user threw out. Sent with every search so the API skips them and
  // returns replacements instead — dropping 20 brings 20 new people, not 20 empty slots.
  const [dismissedProspectIds, setDismissedProspectIds] = useState<Set<string>>(new Set());
  const [loading, setLoading]           = useState(true);
  // Table paging. `leadsPageRef` mirrors the page so `load` can refresh the page in view
  // without taking it as a dependency — background refreshes (fill progress, sim ticks)
  // call `load` with the arguments captured when they were registered.
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsPageSize, setLeadsPageSize] = useState(DEFAULT_LEADS_PAGE_SIZE);
  const leadsPageRef = useRef(1);
  leadsPageRef.current = leadsPage;
  const leadsPageSizeRef = useRef(leadsPageSize);
  leadsPageSizeRef.current = leadsPageSize;
  const [leadsSourceHelperShown, setLeadsSourceHelperShown] = useState(false);
  const [leadsSourceHelperDismissed, setLeadsSourceHelperDismissed] = useState(false);
  const [statusFilter, setStatusFilter] = useState<CampaignLeadStatus | 'all'>('all');

  /*
    The campaign page's Leads tab filters by what happened to a lead (the analytics
    metrics: sent, accepted, replied …) in a period, instead of by where it stands now.
    Kept in a ref as well, so every existing load() call carries it without new args.
  */
  const activityMode = fullPage && hideHeader && !embedded;
  const [activityMetric, setActivityMetric] = useState<PerformanceMetric | null>(initialActivity?.metric ?? null);
  const [activityRange, setActivityRange] = useState<IDateRange>(initialActivity?.range ?? ALL_TIME_RANGE);
  const [activityCounts, setActivityCounts] = useState<IPerformanceMetrics | null>(null);
  const activityParams = React.useMemo(() => {
    if (!activityMode) return {};
    return {
      ...(activityMetric && { activity: activityMetric }),
      ...(activityRange.from && { from: activityRange.from, to: activityRange.to }),
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    };
  }, [activityMode, activityMetric, activityRange]);
  const activityParamsRef = useRef(activityParams);
  activityParamsRef.current = activityParams;
  // Select-all bulk actions must hit only the leads the filter shows, never the whole campaign.
  const bulkActivityParams = activityMetric ? activityParams : {};
  // An empty table under a filter means "nothing matches", not "add some leads".
  const filteredEmpty = activityMode && (!!activityMetric || statusFilter !== 'all');
  const [leadSearchInput, setLeadSearchInput] = useState('');
  const [leadSearch, setLeadSearch] = useState('');

  // ── Source tabs
  const [sourceTab, setSourceTab] = useState<SourceTab>(embedded && embeddedSource ? embeddedSource : 'all');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (embedded && embeddedSource) setSourceTab(embeddedSource);
  }, [embedded, embeddedSource]);

  const [stopConfirm, setStopConfirm]     = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [selectedLead, setSelectedLead]   = useState<ICampaignLead | null>(null);
  const [messageEdit, setMessageEdit]     = useState<{
    lead: ICampaignLead; stepIndex: number; step: ISequenceStep;
  } | null>(null);

  // Proxy account pool (id → live details) so the sidebar can show which LinkedIn
  // account handles a lead by name, and flag accounts no longer in the pool.
  const [proxyAccounts, setProxyAccounts] = useState<Record<string, IProxyAccountDetails>>({});
  // getProxyAccounts from useCampaignsRequest is not referentially stable — do not
  // put it in deps or every re-render (e.g. run-timer tick) refetches forever.
  const refreshProxyAccounts = useCallback(async () => {
    if (campaign.executionMethod !== 'proxy') return;
    const data = await getProxyAccounts(campaign._id);
    const list: IProxyAccountDetails[] = data?.accounts ?? [];
    if (list.length) setProxyAccounts(Object.fromEntries(list.map(a => [a.accountId, a])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign._id, campaign.executionMethod]);

  useEffect(() => {
    void refreshProxyAccounts();
  }, [refreshProxyAccounts]);

  // When paid InMail count goes up (cloud executor / silent poll), refresh Unipile
  // credit balances — avoid a separate timer; open-profile sends do not spend credits.
  const prevSnInmailRef = useRef<number | null>(null);
  useEffect(() => {
    if (campaign.executionMethod !== 'proxy') return;
    const n = messagingStats.snInmail ?? 0;
    if (prevSnInmailRef.current === null) {
      prevSnInmailRef.current = n;
      return;
    }
    if (n > prevSnInmailRef.current) {
      void refreshProxyAccounts();
    }
    prevSnInmailRef.current = n;
  }, [campaign.executionMethod, messagingStats.snInmail, refreshProxyAccounts]);

  // Leads with an in-flight delete request — guards against rapid double-clicks
  // firing duplicate DELETEs (which 404 and corrupt the local counters).
  const removingIdsRef = React.useRef<Set<string>>(new Set());
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());

  // Bulk selection for lead table / enrolled lists
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [isAllLeadsSelected, setIsAllLeadsSelected] = useState(false);
  const [bulkRemoving, setBulkRemoving] = useState(false);
  const [bulkRetrying, setBulkRetrying] = useState(false);
  const [bulkStatusChanging, setBulkStatusChanging] = useState(false);
  const retryingIdsRef = React.useRef<Set<string>>(new Set());
  const [retryingIds, setRetryingIds] = useState<Set<string>>(new Set());

  // ── Sub-tabs for contacts / csv / posts
  const [contactsSubTab, setContactsSubTab] = useState<'search' | 'enrolled'>('search');
  const [csvSubTab, setCsvSubTab]           = useState<'import' | 'enrolled'>('import');
  const [postsSubTab, setPostsSubTab]       = useState<'import' | 'enrolled'>('import');
  const [snListsSubTab, setSnListsSubTab]   = useState<'import' | 'enrolled'>('import');

  // Inline email editing. Manual entry costs nothing, so it needs no confirmation —
  // it is also the only way to fix an address the provider got wrong.
  const [editingEmailFor, setEditingEmailFor] = React.useState<string | null>(null);
  const [emailDraft, setEmailDraft] = React.useState('');

  const startEditEmail = (lead: ICampaignLead) => {
    setEditingEmailFor(lead._id);
    setEmailDraft(lead.email || '');
  };

  const saveLeadEmail = async (lead: ICampaignLead) => {
    const next = emailDraft.trim();
    if (next === (lead.email || '')) { setEditingEmailFor(null); return; }

    const res: any = await updateLeadEmail(campaign._id, lead._id, next, lead.emailType || undefined);
    const updated = res?.data?.lead;

    if (!updated) {
      setToast({ type: 'error', message: res?.message || 'Could not save that email address.' });
      return;
    }
    setEditingEmailFor(null);
    setLeads(prev => prev.map(l => (l._id === lead._id ? { ...l, ...updated } : l)));
  };

  const sequenceHasEmailStep = React.useMemo(
    () => (campaign.sequence ?? []).some(s => s.type === 'email'),
    [campaign.sequence],
  );

  // ── Funnel statuses — derived from campaign sequence order
  // Engagement statuses (left of the divider): pending → per-step statuses, with
  // an 'accepted' milestone right after an invite, and 'replied' (only when the
  // sequence includes a Direct Message step) before the final-outcome group.
  const funnelStatuses: CampaignLeadStatus[] = React.useMemo(() => {
    const seq = campaign.sequence?.length ? campaign.sequence : DEFAULT_SEQUENCE;
    const seen = new Set<CampaignLeadStatus>(['pending']);
    const result: CampaignLeadStatus[] = ['pending'];
    for (const step of seq) {
      const s = STEP_STATUS[step.type];
      if (s && !seen.has(s)) { seen.add(s); result.push(s); }
      if (step.type === 'connection_request' && !seen.has('accepted')) {
        seen.add('accepted'); result.push('accepted');
      }
      // Parked Open Profile skips — filterable list for InMail resend
      if (step.type === 'sn_message' && !seen.has('skipped_not_open')) {
        seen.add('skipped_not_open'); result.push('skipped_not_open');
      }
      // The reply stage sits immediately after the step that can produce it, so each
      // channel reads as send → reply. Collecting all the sends and then all the
      // replies breaks that pairing and makes the two channels hard to follow.
      const reply = REPLY_STATUS_FOR_STEP[step.type];
      if (reply && !seen.has(reply)) { seen.add(reply); result.push(reply); }
    }
    return result;
  }, [campaign.sequence]);

  // The analytics cards this campaign's sequence produces, as filter chips.
  const activityMetrics = React.useMemo(
    () => metricsForSequence((campaign.sequence?.length ? campaign.sequence : DEFAULT_SEQUENCE).map(st => st.type)),
    [campaign.sequence],
  );
  // What the table is narrowed to, for its subtitle: "Connections accepted · Awaiting reply".
  const activeActivityLabel = activityMode
    ? [
      activityMetric ? activityMetrics.find(m => m.key === activityMetric)?.label : undefined,
      statusFilter !== 'all' ? STATUS_CFG[statusFilter].label : undefined,
    ].filter(Boolean).join(' · ')
    : '';

  // ── LeadSearch
  const effectiveLeadPrefs = liveLeadPreferences ?? campaign.leadPreferences ?? EMPTY_LEAD_PREFERENCES;
  const hasPreferences = Object.values(effectiveLeadPrefs)
    .some(v => (Array.isArray(v) ? v.length > 0 : !!v));

  // ── Contacts state
  const [contactResults, setContactResults]     = useState<any[]>([]);
  const [contactTotal, setContactTotal]         = useState(0);
  const [contactPage, setContactPage]           = useState(1);
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set());
  const [contactsSearched, setContactsSearched]     = useState(false);
  const [searchingContacts, setSearchingContacts]   = useState(false);
  const connectionsFilterRef = useRef(campaignConnectionsFilter);
  connectionsFilterRef.current = campaignConnectionsFilter;

  // ── CSV state
  const [csvText, setCsvText]   = useState('');
  const fileInputRef            = useRef<HTMLInputElement>(null);

  // ── Posts URL state
  const [postUrl, setPostUrl] = useState('');
  const [postsImportStatus, setPostsImportStatus] = useState('');
  type TSnList = { id: string; name: string; entityCount?: number; listSource?: string };
  const [snLists, setSnLists] = useState<TSnList[]>([]);
  const [snListsLoading, setSnListsLoading] = useState(false);
  const [snListsLoaded, setSnListsLoaded] = useState(false);
  const [selectedSnListId, setSelectedSnListId] = useState<string | null>(null);
  const [snSearchUrl, setSnSearchUrl] = useState('');
  const [snImportSource, setSnImportSource] = useState<'list' | 'url' | null>(null);
  const [snPreviewLoading, setSnPreviewLoading] = useState(false);
  const [snPreviewProfiles, setSnPreviewProfiles] = useState<TSalesNavLeadPreview[]>([]);
  const [snPreviewTotal, setSnPreviewTotal] = useState<number | null>(null);
  const [snPreviewListId, setSnPreviewListId] = useState<string | null>(null);
  const [snPreviewStatus, setSnPreviewStatus] = useState('');
  const [snPreviewPage, setSnPreviewPage] = useState(0);
  const snFetchGenRef = useRef(0);
  const parsedPostInput = parseLinkedinPostInput(postUrl);
  const postUrlValid = !!parsedPostInput?.ugcPostId;
  const snSearchUrlValid = isValidSalesNavigatorSearchUrl(snSearchUrl);

  React.useEffect(() => {
    const id = window.setTimeout(() => setLeadSearch(leadSearchInput.trim()), 300);
    return () => window.clearTimeout(id);
  }, [leadSearchInput]);

  // ─── Load table
  const loadSeqRef = useRef(0);
  const load = useCallback(async (
    filter: CampaignLeadStatus | 'all' = 'all',
    src: SourceTab = 'all',
    search = leadSearch,
    opts?: { silent?: boolean; page?: number },
  ) => {
    // Every load shares one request slug, so typing in the search box aborts the previous
    // one, which then resolves to `undefined`. Without this guard that abort still cleared
    // `loading` while `leads` was empty — and the table flashed its empty state between
    // the keystroke and the results.
    const mySeq = ++loadSeqRef.current;
    if (!opts?.silent) setLoading(true);
    const pageSize = leadsPageSizeRef.current;
    const requestedPage = opts?.page ?? leadsPageRef.current;
    const params: any = { limit: pageSize, page: requestedPage };
    if (filter !== 'all') params.status = filter;
    if (src !== 'all') params.source = src;
    if (search) params.search = search;
    Object.assign(params, activityParamsRef.current);
    if (activityMode) params.activityCounts = 1;
    const data = await getCampaignLeads(campaign._id, params);
    if (mySeq !== loadSeqRef.current) return;   // superseded — the newer load owns the table
    if (data) {
      const newLeads = data.leads ?? [];
      const newTotal = data.total ?? 0;
      const pageCount = Math.max(1, Math.ceil(newTotal / pageSize));

      // The page fell off the end — leads were removed, or the fill was reset while the
      // user sat on page 4. Land on the last page that still exists instead of an empty
      // table with page controls pointing nowhere.
      if (requestedPage > pageCount && newTotal > 0) {
        setLeadsPage(pageCount);
        leadsPageRef.current = pageCount;
        void load(filter, src, search, { ...opts, page: pageCount });
        return;
      }

      setLeads(newLeads);
      setTotal(newTotal);
      setStatusCounts({ ...emptyLeadStatusCounts(), ...(data.statusCounts ?? {}) });
      setMessagingStats({ ...emptyMessagingStats(), ...(data.messagingStats ?? {}) });
      if (data.activityCounts) setActivityCounts(data.activityCounts);
      setSelectedLead(prev => prev ? (newLeads.find((l: ICampaignLead) => l._id === prev._id) ?? null) : null);
    }
    if (!opts?.silent) setLoading(false);
  }, [campaign._id, leadSearch, activityMode]);

  // Selection deliberately survives paging: "select all" means all leads, not the twenty
  // that happen to be on screen, and individually ticked ids stay ticked when the user
  // comes back to their page.
  const goToLeadsPage = useCallback((next: number) => {
    setLeadsPage(next);
    leadsPageRef.current = next;
    void load(statusFilter, sourceTab === 'all' ? 'all' : sourceTab as any, leadSearch, { page: next });
  }, [load, leadSearch, sourceTab, statusFilter]);

  const changeLeadsPageSize = useCallback((size: number) => {
    setLeadsPageSize(size);
    leadsPageSizeRef.current = size;
    // Row 1 of the old paging is rarely row 1 of the new one — start over rather than
    // guess which page the user was looking at.
    setLeadsPage(1);
    leadsPageRef.current = 1;
    void load(statusFilter, sourceTab === 'all' ? 'all' : sourceTab as any, leadSearch, { page: 1 });
  }, [load, leadSearch, sourceTab, statusFilter]);

  useEffect(() => {
    setLeads([]);
    setSelectedLeadIds([]);
    setIsAllLeadsSelected(false);
    setLeadsPage(1);
    leadsPageRef.current = 1;
    void load(statusFilter, sourceTab === 'all' ? 'all' : sourceTab as any, leadSearch, { page: 1 });
  }, [statusFilter, sourceTab, leadSearch, load, activityParams]);

  // Refresh when simulation tick advances leads in this campaign (debounced)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handler = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void load(statusFilter, sourceTab === 'all' ? 'all' : sourceTab as any, leadSearch, { silent: true });
      }, 1500);
    };
    window.addEventListener(SIM_EVENT, handler);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(SIM_EVENT, handler);
    };
  }, [load, statusFilter, sourceTab, leadSearch]);

  // Proxy campaigns are advanced by the cloud executor / Unipile webhooks — no SIM_EVENT.
  // Poll while the campaign is running so lead statuses stay fresh without a full reload.
  useEffect(() => {
    if (campaign.executionMethod !== 'proxy') return;
    if (campaign.status !== 'active' && campaign.status !== 'paused') return;
    const src = sourceTab === 'all' ? 'all' : sourceTab as any;
    const id = window.setInterval(() => {
      void load(statusFilter, src, leadSearch, { silent: true });
    }, PROXY_UI_POLL_MS);
    return () => window.clearInterval(id);
  }, [campaign.executionMethod, campaign.status, load, statusFilter, sourceTab, leadSearch]);

  // Arriving straight from campaign creation: open the picker so the first search runs
  // without the user having to find the tab. (Under the old model this auto-enrolled.)
  useEffect(() => {
    if ((campaign as any)._autoFetch) {
      setSourceTab('leadsearch');
      setLeadSearchSubTab('search');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Handlers: LeadSearch
  //
  // LeadSearch mirrors the LD Contacts flow: the filters run a *search* that enrolls
  // nothing, the user picks from the results, and "Add to campaign" enrols the picks.
  // Nothing is written to the campaign until the user asks for it, so changing a filter
  // is free and needs neither a "Fetch leads" nor a "Reset & re-fetch" button.
  const leadImportLimit = campaign.leadLimit ?? 50;

  const prospectSeqRef = useRef(0);

  // No limit is sent: the API returns exactly as many candidates as the campaign still
  // needs, skipping the ones already enrolled and the ones thrown out below.
  //
  // The outcome is reported rather than a bare boolean: "superseded" has to be retried by
  // the caller (another search took the slot), while "failed" and "skipped" must not be,
  // or a dead provider would spin the retry forever.
  const runProspectSearch = useCallback(async (prefs: ILeadPreferences): Promise<ProspectSearchOutcome> => {
    const hasPrefs = Object.values(prefs).some(v => (Array.isArray(v) ? v.length > 0 : !!v));
    if (!hasPrefs) {
      setProspectResults([]);
      setProspectsSearched(false);
      setSearchingProspects(false);
      setSearchError(null);
      return 'skipped';
    }
    // The wizard renders this panel before its draft campaign has an id. Searching then
    // hits /campaigns//leads/search-prospects and fails silently — wait to be re-run.
    if (!campaign._id) return 'skipped';

    // Filter clicks arrive in bursts and every search shares one request slug, so an
    // in-flight search is aborted and resolves to `undefined`. Only the newest search may
    // write results — an aborted one is expected, not a failure worth a toast.
    const mySeq = ++prospectSeqRef.current;
    setSearchingProspects(true);
    setSearchError(null);
    const data = await searchProspects(campaign._id, {
      leadPreferences: prefs,
      exclude: Array.from(dismissedProspectIdsRef.current),
    });
    if (mySeq !== prospectSeqRef.current) return 'superseded';

    setSearchingProspects(false);
    // A failed search used to leave the panel spinning forever, because "no results yet"
    // and "the request died" looked identical from here. Say so, and let the user retry.
    if (!data?.leads) {
      setSearchError('Could not load leads for these filters.');
      return 'failed';
    }

    setProspectResults(data.leads);
    setProspectsSearched(true);
    setProspectPage(1);
    // Keep ticks on candidates that survived the refill; the rest are gone anyway.
    const returned = new Set<string>(data.leads.map((l: any) => l.linkedinId || l.publicIdentifier));
    setSelectedProspectIds(prev => new Set(Array.from(prev).filter(id => returned.has(id))));
    setLastSearchStats({
      skippedConnections: data.skippedConnections ?? 0,
      skippedSuppressed: data.skippedSuppressed ?? 0,
      alreadyEnrolled: data.alreadyEnrolled ?? 0,
      hasMore: !!data.hasMore,
      exhausted: !!data.exhausted,
      remaining: data.remaining ?? 0,
    });
    return 'ok';
  }, [campaign._id, searchProspects]);

  // Mirrors for the debounced refill and the search payload — read at timer time, not at
  // render time, so they must not be captured in a closure.
  const dismissedProspectIdsRef = useRef(dismissedProspectIds);
  dismissedProspectIdsRef.current = dismissedProspectIds;
  const prospectResultsRef = useRef(prospectResults);
  prospectResultsRef.current = prospectResults;
  const totalRef = useRef(total);
  totalRef.current = total;
  const sourceTabRef = useRef(sourceTab);
  sourceTabRef.current = sourceTab;
  const leadImportLimitRef = useRef(leadImportLimit);
  leadImportLimitRef.current = leadImportLimit;

  /**
   * Throw a candidate out of the shortlist. The row goes immediately; a debounced refill
   * then asks the API for replacements, so the list stays at the size the campaign needs
   * even after the user rejects a dozen people in a row.
   */
  const refillTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Top the shortlist back up when it holds fewer candidates than the campaign still needs
   * — after leads are deleted on the other tab, or after some were added or thrown out.
   * Debounced, because deletions arrive in bursts and each refill is a provider search.
   */
  const scheduleProspectRefill = useCallback(() => {
    if (sourceTabRef.current !== 'leadsearch') return;
    const room = leadImportLimitRef.current - totalRef.current;
    if (room <= 0) return;                                   // campaign is full
    if (prospectResultsRef.current.length >= room) return;    // already enough to choose from

    if (refillTimerRef.current) clearTimeout(refillTimerRef.current);
    refillTimerRef.current = setTimeout(() => {
      refillTimerRef.current = null;
      void runProspectSearchRef.current(effectiveLeadPrefsRef.current);
    }, PROSPECT_REFILL_DEBOUNCE_MS);
  }, []);

  const scheduleProspectRefillRef = useRef(scheduleProspectRefill);
  scheduleProspectRefillRef.current = scheduleProspectRefill;

  const dismissProspect = (key: string) => {
    setDismissedProspectIds(prev => {
      const next = new Set(prev);
      next.add(key);
      dismissedProspectIdsRef.current = next;
      return next;
    });
    const shortened = prospectResultsRef.current.filter(p => (p.linkedinId || p.publicIdentifier) !== key);
    prospectResultsRef.current = shortened;   // the refill check below reads this
    setProspectResults(shortened);
    setSelectedProspectIds(prev => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

    scheduleProspectRefill();
  };

  useEffect(() => () => {
    if (refillTimerRef.current) clearTimeout(refillTimerRef.current);
  }, []);

  const effectiveLeadPrefsRef = useRef(effectiveLeadPrefs);
  effectiveLeadPrefsRef.current = effectiveLeadPrefs;
  const runProspectSearchRef = useRef(runProspectSearch);
  runProspectSearchRef.current = runProspectSearch;

  /**
   * Re-ask the API which of the candidates on screen are still in the campaign, and repaint
   * the "In campaign" flags from the answer. Without this, leads deleted on the other tab
   * stayed greyed out here — offering no way to add them back short of a new search.
   */
  const syncProspectEnrollment = useCallback(async () => {
    const ids = prospectResultsRef.current
      .map(p => p.linkedinId || p.publicIdentifier)
      .filter(Boolean);
    if (!ids.length) return;

    const data = await checkEnrolledIds(campaign._id, ids);
    if (!data?.enrolled) return;

    const enrolled = new Set<string>(data.enrolled);
    setProspectResults(prev => prev.map(p => {
      const isEnrolled = enrolled.has(p.linkedinId) || enrolled.has(p.publicIdentifier);
      return p.alreadyEnrolled === isEnrolled ? p : { ...p, alreadyEnrolled: isEnrolled };
    }));
    // A candidate that is no longer enrolled becomes selectable again, but nothing should
    // silently end up selected on its way back.
    setSelectedProspectIds(prev => {
      const next = new Set(prev);
      enrolled.forEach(id => next.delete(id));
      return next;
    });
  }, [campaign._id, checkEnrolledIds]);

  const syncProspectEnrollmentRef = useRef(syncProspectEnrollment);
  syncProspectEnrollmentRef.current = syncProspectEnrollment;

  /** Candidates minus the name filter. Names only, matching the enrolled-leads search on
   *  the other tab, which the API also runs against name/firstName/lastName. */
  const filteredProspects = React.useMemo(() => {
    const q = prospectNameFilter.trim().toLowerCase();
    if (!q) return prospectResults;
    return prospectResults.filter(p =>
      [p.name, p.firstName, p.lastName, p.publicIdentifier]
        .map(fieldText)
        .join(' ')
        .toLowerCase()
        .includes(q));
  }, [prospectResults, prospectNameFilter]);

  const prospectPageRows = React.useMemo(() => {
    const start = (prospectPage - 1) * prospectPageSize;
    return filteredProspects.slice(start, start + prospectPageSize);
  }, [filteredProspects, prospectPage, prospectPageSize]);

  const prospectKey = (p: any) => p.linkedinId || p.publicIdentifier;
  const selectableProspects = filteredProspects.filter(p => !p.alreadyEnrolled);
  const allProspectsSelected = selectableProspects.length > 0
    && selectableProspects.every(p => selectedProspectIds.has(prospectKey(p)));

  const toggleProspect = (key: string) => setSelectedProspectIds(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Same three-way selection as the lead table: this page, everything the filter matches,
  // or clear. Already-enrolled candidates are never selectable.
  const selectProspectsOnPage = () => setSelectedProspectIds(prev => {
    const next = new Set(prev);
    prospectPageRows.filter(p => !p.alreadyEnrolled).forEach(p => next.add(prospectKey(p)));
    return next;
  });

  const selectAllProspects = () =>
    setSelectedProspectIds(new Set(selectableProspects.map(prospectKey)));

  const clearProspectSelection = () => setSelectedProspectIds(new Set());

  const selectableOnPage = prospectPageRows.filter(p => !p.alreadyEnrolled);
  const selectableOnPageCount = selectableOnPage.length;
  const prospectsOnPageSelected = selectableOnPageCount > 0
    && selectableOnPage.every(p => selectedProspectIds.has(prospectKey(p)));

  const addableCount = Math.max(0, leadImportLimit - total);

  const handleAddProspects = async () => {
    if (!guardOwner()) return;
    if (!selectedProspectIds.size) return;

    const picked = prospectResults.filter(p => selectedProspectIds.has(prospectKey(p)) && !p.alreadyEnrolled);
    if (!picked.length) return;

    setSubmitting(true);
    const data = await enrollFromProspects(campaign._id, picked);
    setSubmitting(false);

    if (data?.enrolled === undefined) {
      setToast({ type: 'error', message: 'Could not add the selected leads. Please try again.' });
      return;
    }

    const parts: string[] = [];
    if (data.enrolled > 0)         parts.push(`${data.enrolled} lead${data.enrolled === 1 ? '' : 's'} added`);
    if (data.alreadyEnrolled > 0)  parts.push(`${data.alreadyEnrolled} already in campaign`);
    if (data.suppressed > 0)       parts.push(`${data.suppressed} on the suppression list`);
    if (data.skippedOverLimit > 0) parts.push(`${data.skippedOverLimit} over the ${data.leadLimit} limit`);
    setToast({
      type: data.enrolled > 0 ? 'success' : 'info',
      message: parts.join(' · ') || 'Nothing to add.',
    });

    // Drop the added rows straight away, then refill: the picker's job is to hold what the
    // campaign still needs, so after adding 30 of 200 it should show the remaining 170.
    const addedKeys = new Set(picked.map(prospectKey));
    setProspectResults(prev => prev.filter(p => !addedKeys.has(prospectKey(p))));
    setSelectedProspectIds(new Set());
    void load(statusFilter, sourceTab === 'all' ? 'all' : sourceTab as any, leadSearch, { page: 1 });
    void runProspectSearchRef.current(effectiveLeadPrefsRef.current);
  };

  // Coming back to the picker: reconcile with whatever happened on the other tab, and top
  // the shortlist back up if leads were removed while we were away.
  useEffect(() => {
    if (sourceTab !== 'leadsearch' || leadSearchSubTab !== 'search') return;
    void syncProspectEnrollmentRef.current();
    scheduleProspectRefillRef.current();
  }, [sourceTab, leadSearchSubTab, total]);

  // Auto-search whenever the targeting filters settle. Nothing is enrolled, so this is
  // safe to run on every change — it only refreshes what the picker offers.
  const prefsSearchBaselineRef = useRef<string | null>(null);
  const prefsSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leadPrefsKey = JSON.stringify(effectiveLeadPrefs);
  const leadPrefsKeyRef = useRef(leadPrefsKey);
  leadPrefsKeyRef.current = leadPrefsKey;

  /**
   * Search for the filters as they are *now*, and make sure the newest filters always end
   * up searched. A search can lose its slot to a competing one (a refill, an add, a tab
   * switch — they share one request slug), which previously left the click applied to
   * nothing: the results still belonged to the old filters. A superseded search
   * reschedules itself; a failed one does not, or a dead provider would retry forever.
   */
  const runFilterSearch = useCallback(async () => {
    const searchedKey = leadPrefsKeyRef.current;
    const outcome = await runProspectSearchRef.current(effectiveLeadPrefsRef.current);

    if (outcome === 'ok') prefsSearchBaselineRef.current = searchedKey;

    const stale = leadPrefsKeyRef.current !== prefsSearchBaselineRef.current;
    if (stale && outcome !== 'failed' && outcome !== 'skipped' && !prefsSearchTimerRef.current) {
      prefsSearchTimerRef.current = setTimeout(() => {
        prefsSearchTimerRef.current = null;
        void runFilterSearchRef.current();
      }, PREFS_REFETCH_DEBOUNCE_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runFilterSearchRef = useRef(runFilterSearch);
  runFilterSearchRef.current = runFilterSearch;

  useEffect(() => {
    prefsSearchBaselineRef.current = null;
  }, [campaign._id]);

  useEffect(() => {
    if (sourceTab !== 'leadsearch') return;
    // No id yet (wizard draft still saving) — the id landing re-runs this effect.
    if (!campaign._id) return;
    if (prefsSearchBaselineRef.current === leadPrefsKey) return;

    if (prefsSearchTimerRef.current) clearTimeout(prefsSearchTimerRef.current);
    prefsSearchTimerRef.current = setTimeout(() => {
      prefsSearchTimerRef.current = null;
      void runFilterSearchRef.current();
    }, PREFS_REFETCH_DEBOUNCE_MS);

    return () => {
      if (prefsSearchTimerRef.current) {
        clearTimeout(prefsSearchTimerRef.current);
        prefsSearchTimerRef.current = null;
      }
    };
  }, [sourceTab, leadPrefsKey, campaign._id]);

  // ─── Handlers: Contacts
  // Contact searches are highly race-prone: a single filter change can fire several
  // searches back-to-back (filter update + quick-action normalization), and clearing a
  // filter fires another. They all share one request slug, so sendRequest aborts the
  // earlier in-flight request — which resolves to `undefined`. We guard against two bugs:
  //   1. A bogus "Search failed" toast from an aborted/superseded request.
  //   2. Stale results being shown after the filter changed (e.g. a quick-filter request
  //      that lands after the user already cleared the filter).
  // To do that, every search records both a sequence number and a snapshot of the filter
  // it ran with; on return we discard the result unless it is still the most recent search
  // AND the filter has not changed since it started. Aborted/stale results are dropped
  // silently — no toast — because they are expected, not real failures.
  const searchContactsSeqRef = useRef(0);
  const clearContactResults = useCallback(() => {
    searchContactsSeqRef.current++;
    setSearchingContacts(false);
    setContactResults([]);
    setContactTotal(0);
    setContactPage(1);
    setSelectedContactIds(new Set());
    setContactsSearched(false);
  }, []);

  const handleSearchContacts = useCallback(async (page = 1) => {
    const mySeq = ++searchContactsSeqRef.current;
    const requestedFilterSig = JSON.stringify(connectionsFilterRef.current);
    setSearchingContacts(true);
    const filters = getMappedParameters({ page, pageSize: 50 }, connectionsFilterRef.current);
    const data = await searchContacts(campaign._id, { ...filters, page, pageSize: 50 });

    // Superseded by a newer search, or the filter changed while this request was in flight.
    // Either way the result is stale — drop it silently (no results, no toast).
    if (mySeq !== searchContactsSeqRef.current) return;
    if (JSON.stringify(connectionsFilterRef.current) !== requestedFilterSig) return;

    setSearchingContacts(false);
    if (data?.contacts !== undefined) {
      if (page === 1) { setContactResults(data.contacts); setSelectedContactIds(new Set()); }
      else setContactResults(prev => [...prev, ...data.contacts]);
      setContactTotal(data.total ?? 0);
      setContactPage(page);
      setContactsSearched(true);
    }
    // `data === undefined` here means the request was aborted/canceled (same-slug supersede)
    // rather than a genuine failure — surfacing a toast would be misleading, so we stay quiet.
  }, [campaign._id, getMappedParameters, searchContacts]);

  const toggleContact = (id: string) =>
    setSelectedContactIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const toggleAllContacts = () => {
    const eligibleIds = contactResults.filter(c => !c.alreadyEnrolled && !c.suppressed).map(c => c.connectionId);
    setSelectedContactIds(prev => prev.size === eligibleIds.length ? new Set() : new Set(eligibleIds));
  };

  const handleEnrollContacts = async () => {
    if (!selectedContactIds.size) return;
    if (!guardOwner()) return;
    setSubmitting(true);
    const data = await enrollFromContacts(campaign._id, Array.from(selectedContactIds));
    setSubmitting(false);
    if (data?.enrolled !== undefined) {
      setToast(describeContactsEnrollment(data));
      setSelectedContactIds(new Set());
      void handleSearchContacts(1);
      void load(statusFilter, sourceTab === 'all' ? 'all' : sourceTab as any);
    } else {
      setToast({ type: 'error', message: 'Enroll failed.' });
    }
  };

  // ─── Handlers: CSV
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { if (ev.target?.result) setCsvText(String(ev.target.result)); };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleEnrollCsv = async () => {
    const { rows } = parseCsvText(csvText);
    if (!rows.length) { setToast({ type: 'warning', message: 'No valid publicIdentifiers found.' }); return; }
    if (rows.length > CSV_MAX_ROWS) {
      setToast({
        type: 'warning',
        message: `${rows.length.toLocaleString()} rows detected — a single import is limited to ${CSV_MAX_ROWS.toLocaleString()}. Split the file and import it in parts.`,
      });
      return;
    }
    if (!guardOwner()) return;
    setSubmitting(true);
    const data = await enrollFromCsv(campaign._id, rows);
    setSubmitting(false);
    if (data?.enrolled !== undefined) {
      // Every bucket is shown: the counts add up to the rows sent, so a shortfall in
      // "In Campaign" is always explained instead of leaving the user to guess.
      const n = (v: number) => v.toLocaleString();
      const parts: string[] = [];
      if (data.enrolled > 0)        parts.push(`${n(data.enrolled)} leads imported`);
      if (data.alreadyEnrolled > 0) parts.push(`${n(data.alreadyEnrolled)} already in campaign`);
      if (data.duplicateRows > 0)   parts.push(`${n(data.duplicateRows)} duplicate rows`);
      if (data.invalidRows > 0)     parts.push(`${n(data.invalidRows)} without identifier`);
      if (data.suppressed > 0)      parts.push(`${n(data.suppressed)} on the suppression list`);
      if (data.insertFailed > 0)    parts.push(`${n(data.insertFailed)} rejected`);
      setToast({ type: data.enrolled > 0 ? 'success' : 'info', message: parts.join(' · ') || 'No new leads found.' });
      if (data.enrolled > 0) setCsvText('');
      void load(statusFilter, sourceTab === 'all' ? 'all' : sourceTab as any);
    } else {
      setToast({ type: 'error', message: 'Import failed.' });
    }
  };

  const handleImportPostCommentators = async () => {
    const url = postUrl.trim();
    const parsed = parseLinkedinPostInput(url);
    if (!parsed?.ugcPostId) {
      setToast({ type: 'warning', message: 'Paste a valid LinkedIn post URL (must include an activity or ugcPost id).' });
      return;
    }
    if (!guardOwner()) return;
    if (postsTrialLimitReached) {
      setToast({type: 'error', message: postCommentersAccess?.message || 'Your trial post import limit has been reached. Upgrade to Growth for unlimited imports.'});
      return;
    }

    if (isProxyCampaign) {
      if (!hasUnipileAccount) {
        setToast({ type: 'error', message: 'Connect a LinkedIn account to this campaign before importing post commenters.' });
        return;
      }
    } else {
      if (!extensionInstalled) {
        setToast({ type: 'error', message: 'Install the LeadDelta extension to import post commenters.' });
        return;
      }
      if (!isLoggedToLinkedin) {
        setToast({ type: 'error', message: 'Log in to LinkedIn in the extension first.' });
        return;
      }
    }

    setSubmitting(true);
    setPostsImportStatus('Scanning post comments…');
    try {
      const { commenters, error } = isProxyCampaign
        ? await fetchAllPostCommentersUnipile({
          postUrl: url,
          importPage: async (params) => {
            const data = await importPostCommenters(campaign._id, params);
            if (!data) return false;
            return {
              commenters: Array.isArray(data.commenters) ? data.commenters : [],
              cursor: data.cursor ?? null,
              socialId: data.socialId ?? null,
              totalItems: data.totalItems ?? null,
              commentCounter: data.commentCounter ?? null,
            };
          },
          onProgress: (loaded, page, total) => {
            const totalLabel = total != null ? ` / ${total}` : '';
            setPostsImportStatus(`Scanning comments… ${loaded}${totalLabel} found (page ${page})`);
          },
        })
        : await fetchAllPostCommenters(
          (params) => getProfilePostComments(params),
          parsed.ugcPostId,
          {
            postUrl: url,
            preferredId: parsed.id,
            resolvePostUrns: (params) => resolveLinkedinPostUrns(params),
            onProgress: (loaded, page) => {
              setPostsImportStatus(`Scanning comments… ${loaded} found (page ${page})`);
            },
          },
        );

      if (error) {
        setToast({ type: 'error', message: error });
        return;
      }

      const rows = commentersToLeadRows(commenters);
      if (!rows.length) {
        setToast({ type: 'info', message: 'No person commenters found on this post.' });
        return;
      }

      setPostsImportStatus(`Importing ${rows.length} commenters…`);
      const data = await enrollFromCsv(campaign._id, rows, { source: 'posts' });
      if (data?.enrolled !== undefined) {
        const parts: string[] = [];
        if (data.enrolled > 0)        parts.push(`${data.enrolled} commenters imported`);
        if (data.alreadyEnrolled > 0) parts.push(`${data.alreadyEnrolled} already in campaign`);
        if (data.suppressed > 0)      parts.push(`${data.suppressed} on the suppression list`);
        if (data.skippedLimit > 0)    parts.push(`${data.skippedLimit} skipped (lead limit reached)`);
        if (data.insertFailed > 0)    parts.push(`${data.insertFailed} failed to save`);
        setToast({ type: data.enrolled > 0 ? 'success' : 'info', message: parts.join(' · ') || 'No new leads found.' });
        if (data.enrolled > 0) {
          setPostUrl('');
          setPostsSubTab('enrolled');
          if (trial) {
            checkPostCommentersAccessHandler()
              .then((result: any) => {
                const access = result?.postCommentersAccess;
                if (access?.isTrial) setPostCommentersAccess(access);
              })
              .catch(() => {});
          }
        }
        void load(statusFilter, 'posts');
      } else {
        setToast({ type: 'error', message: 'Import failed.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', message: e?.message || 'Failed to import commenters.' });
    } finally {
      setSubmitting(false);
      setPostsImportStatus('');
    }
  };

  const handleLoadSnLists = async () => {
    if (!extensionInstalled) {
      setToast({ type: 'error', message: 'Install the LeadDelta extension to load Sales Navigator lists.' });
      return;
    }
    if (!isLoggedToLinkedin) {
      setToast({ type: 'error', message: 'Log in to LinkedIn in the extension first.' });
      return;
    }
    setSnListsLoading(true);
    try {
      const raw = await getSalesLists();
      if (!raw || raw === false) {
        setSnLists([]);
        setSnListsLoaded(true);
        setToast({
          type: 'error',
          message: 'Could not load Sales Navigator lists. Make sure you have a Sales Navigator seat and the extension is logged in.',
        });
        return;
      }
      const payload = raw?.result ?? raw;
      let elements: TSnList[] = [];
      if (Array.isArray(payload?.elements)) {
        elements = payload.elements;
      } else if (Array.isArray(payload)) {
        elements = payload;
      }
      setSnLists(
        elements
          .map((el: any) => ({
            id: String(el?.id ?? ''),
            name: String(el?.name ?? 'Untitled list'),
            entityCount: typeof el?.entityCount === 'number' ? el.entityCount : undefined,
            listSource: el?.listSource ? String(el.listSource) : undefined,
          }))
          .filter(el => el.id),
      );
      setSnListsLoaded(true);
      if (!elements.length) {
        setToast({ type: 'info', message: 'No Sales Navigator lead lists found for this account.' });
      }
    } catch (e: any) {
      setSnLists([]);
      setSnListsLoaded(true);
      setToast({ type: 'error', message: e?.message || 'Could not load Sales Navigator lists.' });
    } finally {
      setSnListsLoading(false);
    }
  };

  // Auto-load SN lists when the tab is opened (extension path — Unipile has no saved lists).
  useEffect(() => {
    if (!showSnListsUi) return;
    if (sourceTab !== 'sn_lists') return;
    if (snListsLoaded || snListsLoading) return;
    void handleLoadSnLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceTab, showSnListsUi]);

  const handleCancelSnListFetch = () => {
    snFetchGenRef.current += 1;
    setSnPreviewLoading(false);
    setSubmitting(false);
    setSnImportSource(null);
    const list = snPreviewListId ? snLists.find(l => l.id === snPreviewListId) : undefined;
    const total = (typeof list?.entityCount === 'number' ? list.entityCount : null) ?? snPreviewTotal;
    setSnPreviewStatus(snPreviewProfiles.length
      ? `Stopped · ${formatSnListProgress(snPreviewProfiles.length, total)}`
      : '');
  };

  const handleImportSnSource = async ({
    listId,
    listName = '',
    queryUrl,
    expectedTotal = null,
  }: {
    listId?: string;
    listName?: string;
    queryUrl?: string;
    expectedTotal?: number | null;
  }) => {
    if (!guardOwner()) return;

    // Saved lists always need the extension. Search URL: prefer extension when ready,
    // otherwise Unipile on proxy campaigns.
    const useUnipile = isProxyCampaign && !!queryUrl && !listId && !snExtensionReady;

    if (useUnipile) {
      if (!hasUnipileAccount) {
        setToast({
          type: 'error',
          message: 'Connect a LinkedIn account to this campaign, or install the LeadDelta extension to import Sales Navigator leads.',
        });
        return;
      }
    } else {
      if (!extensionInstalled) {
        setToast({
          type: 'error',
          message: listId
            ? 'Install the LeadDelta extension to import Sales Navigator lists.'
            : 'Install the LeadDelta extension (or connect a LinkedIn account for cloud URL import).',
        });
        return;
      }
      if (!isLoggedToLinkedin) {
        setToast({ type: 'error', message: 'Log in to LinkedIn in the extension first.' });
        return;
      }
    }

    const gen = ++snFetchGenRef.current;
    setSnImportSource(queryUrl ? 'url' : 'list');
    setSubmitting(true);
    setSnPreviewLoading(true);
    setSnPreviewProfiles([]);
    setSnPreviewPage(0);
    setSnPreviewListId(listId || null);
    setSnPreviewTotal(expectedTotal);
    setSnPreviewStatus(`Loading… ${formatSnListProgress(0, expectedTotal)}`);

    try {
      const onProgress = ({ page, loaded, totalDisplay, profiles: loadedProfiles, willContinue, nextDelayMs }: {
        page: number;
        loaded: number;
        totalDisplay: number | null;
        profiles: TSalesNavLeadPreview[];
        willContinue?: boolean;
        nextDelayMs?: number;
      }) => {
        if (snFetchGenRef.current !== gen) return;
        setSnPreviewPage(page);
        setSnPreviewProfiles(loadedProfiles);
        const total = expectedTotal ?? totalDisplay;
        setSnPreviewTotal(total);
        setSnPreviewStatus(
          formatSnListProgress(loaded, total, {
            prefix: 'Loading…',
            waitingSec: willContinue && nextDelayMs != null
              ? Math.round(nextDelayMs / 1000)
              : undefined,
          }),
        );
      };

      const result = useUnipile
        ? await fetchSalesNavUrlLeadsUnipile({
          queryUrl: queryUrl!,
          maxLeads: expectedTotal ?? undefined,
          paginate: true,
          searchPage: async (params) => {
            const data = await searchSalesNavigator(campaign._id, params);
            if (!data) return false;
            return {
              profiles: Array.isArray(data.profiles) ? data.profiles : [],
              totalDisplay: data.totalDisplay ?? null,
              cursor: data.cursor ?? null,
            };
          },
          shouldCancel: () => snFetchGenRef.current !== gen,
          onProgress,
        })
        : await fetchSalesNavListLeads({
          listId,
          listName,
          queryUrl,
          maxLeads: expectedTotal ?? undefined,
          paginate: true,
          searchLeadsSN,
          shouldCancel: () => snFetchGenRef.current !== gen,
          onProgress,
        });

      if (snFetchGenRef.current !== gen) return;

      const profiles = result.profiles;
      setSnPreviewProfiles(profiles);
      const doneTotal = expectedTotal ?? result.totalDisplay;
      setSnPreviewTotal(doneTotal);
      setSnPreviewLoading(false);

      if (result.cancelled) {
        setToast({ type: 'info', message: 'Import cancelled.' });
        setSnPreviewStatus(`Stopped · ${formatSnListProgress(profiles.length, doneTotal)}`);
        return;
      }
      if (result.error && !profiles.length) {
        setToast({ type: 'error', message: result.error });
        setSnPreviewStatus('');
        return;
      }
      if (result.error) {
        setToast({ type: 'warning', message: `${result.error} Importing ${profiles.length} loaded leads.` });
      }

      const partition = partitionSnLeadsForImport(profiles);
      const { rows } = partition;
      if (!rows.length) {
        const skipParts: string[] = [];
        if (partition.skippedSelf > 0) skipParts.push(`${partition.skippedSelf} you`);
        if (partition.skippedNoId > 0) skipParts.push(`${partition.skippedNoId} without id`);
        setToast({
          type: 'info',
          message: skipParts.length
            ? `No leads to import · ${skipParts.join(' · ')}`
            : `No Sales Navigator leads to import from this ${queryUrl ? 'search' : 'list'}.`,
        });
        setSnPreviewStatus('');
        return;
      }

      const enrollTotal = doneTotal ?? rows.length;
      setSnPreviewStatus(`Importing… ${formatSnListProgress(rows.length, enrollTotal)}`);
      const data = await enrollFromCsv(campaign._id, rows, { source: 'sn_lists' });
      if (snFetchGenRef.current !== gen) return;

      if (data?.enrolled !== undefined) {
        const parts: string[] = [];
        if (data.enrolled > 0) {
          const degreeBits: string[] = [];
          if (partition.firstDegree > 0) degreeBits.push(`${partition.firstDegree} 1st`);
          if (partition.outreachDegrees > 0) degreeBits.push(`${partition.outreachDegrees} 2nd/3rd`);
          if (partition.unknownDegree > 0) degreeBits.push(`${partition.unknownDegree} unknown degree`);
          const degreeHint = degreeBits.length ? ` (${degreeBits.join(', ')})` : '';
          parts.push(`${data.enrolled} imported${degreeHint}`);
        }
        if (partition.skippedSelf > 0) {
          parts.push(`${partition.skippedSelf} skipped (you)`);
        }
        if (data.alreadyEnrolled > 0) parts.push(`${data.alreadyEnrolled} already in campaign`);
        if (data.duplicateRows > 0)   parts.push(`${data.duplicateRows} duplicates`);
        if (data.invalidRows > 0)     parts.push(`${data.invalidRows} without identifier`);
        if (data.suppressed > 0)      parts.push(`${data.suppressed} on the suppression list`);
        if (data.insertFailed > 0) {
          const why = data.rejectedSample?.[0]?.reason
            ? ` (${String(data.rejectedSample[0].reason).slice(0, 80)})`
            : '';
          parts.push(`${data.insertFailed} failed to save${why}`);
        }
        setToast({ type: data.enrolled > 0 ? 'success' : 'info', message: parts.join(' · ') || 'No new leads found.' });
        if (data.enrolled > 0) {
          if (queryUrl) setSnSearchUrl('');
          setSnListsSubTab('enrolled');
          void load(statusFilter, 'sn_lists');
        }
        setSnPreviewStatus(
          data.enrolled > 0
            ? `Done · ${data.enrolled} imported`
            : `Done · nothing new imported`,
        );
      } else {
        setToast({ type: 'error', message: 'Import failed.' });
        setSnPreviewStatus('');
      }
    } catch (e: any) {
      if (snFetchGenRef.current !== gen) return;
      setToast({ type: 'error', message: e?.message || `Failed to import Sales Navigator ${queryUrl ? 'search' : 'list'}.` });
      setSnPreviewStatus('');
    } finally {
      if (snFetchGenRef.current === gen) {
        setSubmitting(false);
        setSnPreviewLoading(false);
        setSnImportSource(null);
      }
    }
  };

  const handleImportSnList = async () => {
    if (!selectedSnListId) {
      setToast({ type: 'warning', message: 'Select a Sales Navigator list first.' });
      return;
    }
    const list = snLists.find(l => l.id === selectedSnListId);
    await handleImportSnSource({
      listId: selectedSnListId,
      listName: list?.name || '',
      expectedTotal: typeof list?.entityCount === 'number' ? list.entityCount : null,
    });
  };

  const handleImportSnSearchUrl = async () => {
    const queryUrl = snSearchUrl.trim();
    if (!isValidSalesNavigatorSearchUrl(queryUrl)) {
      setToast({ type: 'warning', message: 'Paste a valid Sales Navigator people search URL.' });
      return;
    }
    await handleImportSnSource({ queryUrl });
  };

  // ─── Lead table handlers
  const handleStatusChange = async (lead: ICampaignLead, status: CampaignLeadStatus) => {
    if (status === lead.status) return;
    if (!OUTCOME_LEAD_STATUSES.includes(status)) return;
    if (!guardOwner()) return;
    const data = await updateLeadStatus(campaign._id, lead._id, status);
    if (data?.lead) {
      setLeads(prev => prev.map(l => l._id === lead._id ? data.lead : l));
      // `?? 0` guards counters for legacy/unknown statuses not in the counts object.
      setStatusCounts(prev => ({
        ...prev,
        [lead.status]: Math.max(0, (prev[lead.status] ?? 0) - 1),
        [status]: (prev[status] ?? 0) + 1,
      }));
    } else {
      setToast({ type: 'error', message: (data as any)?.message || 'Could not update lead status.' });
    }
  };

  const handleRemove = async (lead: ICampaignLead) => {
    if (!guardOwner()) return;
    // Ignore repeat clicks while a delete for this lead is already in flight.
    if (removingIdsRef.current.has(lead._id)) return;
    removingIdsRef.current.add(lead._id);
    setRemovingIds(new Set(removingIdsRef.current));

    try {
      const res = await removeLead(campaign._id, lead._id);
      // Only mutate local counters once the server confirms the deletion, so a
      // failed call (e.g. lead already gone) never drives Total below zero.
      if (res?.success) {
        setLeads(prev => prev.filter(l => l._id !== lead._id));
        setTotal(t => Math.max(0, t - 1));
        setStatusCounts(prev => ({ ...prev, [lead.status]: Math.max(0, prev[lead.status] - 1) }));
        setSelectedLeadIds(prev => prev.filter(id => id !== lead._id));
        if (selectedLead?._id === lead._id) setSelectedLead(null);
        // The freed slot has to come back as a candidate, not stay an empty seat.
        void syncProspectEnrollmentRef.current();
        scheduleProspectRefillRef.current();
      } else {
        setToast({ type: 'error', message: 'Could not remove lead. Please refresh and try again.' });
      }
    } finally {
      removingIdsRef.current.delete(lead._id);
      setRemovingIds(new Set(removingIdsRef.current));
    }
  };

  const applyRetryStatusCounts = (
    prev: typeof statusCounts,
    from: CampaignLeadStatus,
    restoredTo: CampaignLeadStatus,
  ) => ({
    ...prev,
    [from]: Math.max(0, (prev[from] ?? 0) - 1),
    [restoredTo]: (prev[restoredTo] ?? 0) + 1,
  });

  const handleRetry = async (lead: ICampaignLead) => {
    if (!guardOwner()) return;
    // A suppressed lead comes back the same way, once its suppression-list entry is gone
    // (the API refuses while it is still listed and says which entry).
    if (lead.status !== 'failed' && lead.status !== 'suppressed') return;
    if (retryingIdsRef.current.has(lead._id)) return;
    retryingIdsRef.current.add(lead._id);
    setRetryingIds(new Set(retryingIdsRef.current));

    try {
      const res = await retryLead(campaign._id, lead._id);
      const restoredTo = res?.data?.restoredToStatus as CampaignLeadStatus | undefined;
      if (res?.success && res?.data?.lead && restoredTo) {
        setLeads(prev => prev.map(l => (l._id === lead._id ? res.data.lead : l)));
        setStatusCounts(prev => applyRetryStatusCounts(prev, lead.status, restoredTo));
        if (selectedLead?._id === lead._id) setSelectedLead(res.data.lead);
        setToast({
          type: 'success',
          message: `Lead restored to ${STATUS_LABELS[restoredTo] || restoredTo} for retry.`,
        });
      } else {
        setToast({ type: 'error', message: res?.message || 'Retry failed.' });
      }
    } finally {
      retryingIdsRef.current.delete(lead._id);
      setRetryingIds(new Set(retryingIdsRef.current));
    }
  };

  /**
   * Sidebar "Add to suppression list": the person, their company's domain or their
   * address, prefilled in the same modal Settings uses — where the user picks the list
   * and is told how many leads it would take out before anything is saved.
   */
  const handleSuppressFromLead = (type: SuppressionType, value: string) => {
    setAddToSuppressionListModal({
      initialType: type,
      initialValue: value,
      source: 'lead',
      // Reloads the table and counts, and the open sidebar picks up the new status.
      onAdded: () => { void load(statusFilter, sourceTab === 'all' ? 'all' : sourceTab as any); },
    });
  };

  const bulkRetryCount = React.useMemo(() => {
    if (isAllLeadsSelected) {
      if (statusFilter === 'failed' || statusFilter === 'suppressed') return total;
      return statusCounts.failed ?? 0;
    }
    return leads.filter(l => selectedLeadIds.includes(l._id) && isRetryable(l)).length;
  }, [isAllLeadsSelected, statusFilter, total, statusCounts.failed, leads, selectedLeadIds]);

  const handleBulkRetry = async () => {
    if (!guardOwner()) return;
    if (bulkRetryCount === 0 || bulkRetrying) return;

    setBulkRetrying(true);
    try {
      const payload = isAllLeadsSelected
        ? {
            all: true,
            // "All" retries failed leads unless the suppressed view is the one open.
            ...(statusFilter === 'suppressed' && { status: 'suppressed' as const }),
            ...(currentLeadSourceFilter() && { source: currentLeadSourceFilter() }),
            ...(leadSearch && { search: leadSearch }),
            ...bulkActivityParams,
          }
        : {
            leadIds: leads
              .filter(l => selectedLeadIds.includes(l._id) && isRetryable(l))
              .map(l => l._id),
          };

      const res = await bulkRetryLeads(campaign._id, payload);
      const retried = res?.data?.retried ?? 0;
      const stillSuppressed = res?.data?.stillSuppressed ?? 0;
      const skipped = Math.max(0, (res?.data?.skipped ?? 0) - stillSuppressed);
      const listedNote = stillSuppressed
        ? ` ${stillSuppressed} still on the suppression list — remove ${stillSuppressed === 1 ? 'its entry' : 'their entries'} first.`
        : '';

      if (retried > 0) {
        setToast({
          type: 'success',
          message: `${retried} lead${retried === 1 ? '' : 's'} queued for retry.${skipped ? ` ${skipped} skipped.` : ''}${listedNote}`,
        });
        clearLeadSelection();
        void load(statusFilter, sourceTab === 'all' ? 'all' : sourceTab as any);
      } else {
        setToast({ type: 'info', message: `No leads were retried.${listedNote}` });
      }
    } finally {
      setBulkRetrying(false);
    }
  };

  const skippedNotOpenCount = statusCounts.skipped_not_open ?? 0;
  const hasSnMessageStep = React.useMemo(
    () => (campaign.sequence ?? []).some(s => s.type === 'sn_message'),
    [campaign.sequence],
  );

  const stateChips = React.useMemo(() => {
    const types = new Set((campaign.sequence?.length ? campaign.sequence : DEFAULT_SEQUENCE).map(st => st.type));
    return [
      ...(types.has('connection_request') ? [{ status: 'invite_sent' as CampaignLeadStatus, Icon: Clock }] : []),
      ...(types.has('message') || types.has('sn_message') ? [{ status: 'message_sent' as CampaignLeadStatus, Icon: MessageSquare }] : []),
      ...(types.has('email') ? [{ status: 'email_sent' as CampaignLeadStatus, Icon: Mail }] : []),
      ...(hasSnMessageStep ? [{ status: 'skipped_not_open' as CampaignLeadStatus, Icon: MailX }] : []),
      { status: 'converted' as CampaignLeadStatus, Icon: CircleCheck },
      { status: 'closed_lost' as CampaignLeadStatus, Icon: CircleX },
      { status: 'failed' as CampaignLeadStatus, Icon: CircleAlert },
    ];
  }, [campaign.sequence, hasSnMessageStep]);

  const inMailCreditSummary = React.useMemo(
    () => summarizeInMailCredits(Object.values(proxyAccounts)),
    [proxyAccounts],
  );
  const inMailCreditBalance = inMailCreditSummary ? splitInMailCreditBalance(inMailCreditSummary) : null;

  const bulkResendInMailCount = React.useMemo(() => {
    if (!isProxyCampaign || !hasSnMessageStep) return 0;
    if (isAllLeadsSelected && statusFilter === 'skipped_not_open') return skippedNotOpenCount;
    // Only the leads the user picked — nothing is queued just because the tab is open.
    return leads.filter(l => selectedLeadIds.includes(l._id) && l.status === 'skipped_not_open').length;
  }, [isProxyCampaign, hasSnMessageStep, isAllLeadsSelected, statusFilter, skippedNotOpenCount, selectedLeadIds, leads]);

  const handleBulkResendInMail = async () => {
    if (!guardOwner()) return;
    if (bulkResendInMailCount === 0 || resendingInMail) return;
    setResendingInMail(true);
    try {
      const payload = isAllLeadsSelected && statusFilter === 'skipped_not_open'
        ? { all: true, ...bulkActivityParams }
        : {
            leadIds: leads
              .filter(l => selectedLeadIds.includes(l._id) && l.status === 'skipped_not_open')
              .map(l => l._id),
          };

      const res: any = await bulkResendInMail(campaign._id, payload);
      const data = res?.data ?? res;
      const queued = data?.queued ?? data?.ok ?? 0;
      if (queued > 0) {
        setToast({
          type: 'success',
          message: `Queued ${queued} lead${queued === 1 ? '' : 's'} for InMail — cloud will send with normal pacing.`,
        });
      } else {
        setToast({
          type: 'info',
          message: 'No skipped leads were queued for InMail.',
        });
      }
      clearLeadSelection();
      void load(statusFilter === 'skipped_not_open' ? 'pending' : statusFilter, sourceTab === 'all' ? 'all' : sourceTab as any);
      if (isProxyCampaign) {
        void refreshProxyAccounts();
      }
    } finally {
      setResendingInMail(false);
    }
  };

  const bulkStatusCount = isAllLeadsSelected ? total : selectedLeadIds.length;

  const handleBulkStatusPick = (status: CampaignLeadStatus) => {
    if (!guardOwner()) return;
    if (bulkStatusCount === 0 || bulkStatusChanging) return;

    setConfirmationModal({
      headingText: 'Change lead status',
      descriptionText: `Change status for ${bulkStatusCount} selected lead${bulkStatusCount === 1 ? '' : 's'} to ${STATUS_LABELS[status]}?`,
      onCancelHandler: () => hideModal(),
      onConfirmHandler: async () => {
        hideModal();
        setBulkStatusChanging(true);
        try {
          const payload = isAllLeadsSelected
            ? {
                all: true,
                status,
                ...(statusFilter !== 'all' && { filterStatus: statusFilter }),
                ...(currentLeadSourceFilter() && { source: currentLeadSourceFilter() }),
                ...(leadSearch && { search: leadSearch }),
                ...bulkActivityParams,
              }
            : {
                leadIds: selectedLeadIds,
                status,
              };

          const res = await bulkUpdateLeadStatus(campaign._id, payload);
          const updated = res?.data?.updated ?? 0;
          const skipped = res?.data?.skipped ?? 0;

          if (res?.success && updated > 0) {
            setToast({
              type: 'success',
              message: `${updated} lead${updated === 1 ? '' : 's'} marked as ${STATUS_LABELS[status]}.${skipped ? ` ${skipped} skipped.` : ''}`,
            });
            clearLeadSelection();
            void load(statusFilter, sourceTab === 'all' ? 'all' : sourceTab as any);
          } else if (res?.success) {
            setToast({ type: 'info', message: 'No leads were updated.' });
          } else {
            setToast({ type: 'error', message: res?.message || 'Could not update lead status.' });
          }
        } finally {
          setBulkStatusChanging(false);
        }
      },
      confirmBtnTitle: 'Change status',
    });
  };

  const clearLeadSelection = () => {
    setSelectedLeadIds([]);
    setIsAllLeadsSelected(false);
  };

  const allOnPageSelected = isAllLeadsSelected || (leads.length > 0 && leads.every(l => selectedLeadIds.includes(l._id)));

  const selectLeadOnPageHandler = () => {
    const idsOnPage = leads.map(l => l._id);
    setSelectedLeadIds(prev => [...prev, ...idsOnPage.filter(id => !prev.includes(id))]);
    setIsAllLeadsSelected(false);
  };

  const selectAllLeadsHandler = () => {
    setSelectedLeadIds(leads.map(l => l._id));
    setIsAllLeadsSelected(true);
  };

  const selectLeadCheckboxHandler = (id: string) => {
    setIsAllLeadsSelected(false);
    setSelectedLeadIds(prev => (
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    ));
  };

  const bulkDeleteCount = isAllLeadsSelected ? total : selectedLeadIds.length;

  /*
    Export: the selected leads, or — with nothing selected — everything the filters show.
    It changes nothing, so unlike Move / Remove it doesn't need a selection. Selection
    survives paging, so the leads are fetched rather than read off the current page.
  */
  const [exportingLeads, setExportingLeads] = useState(false);
  const exportCount = isAllLeadsSelected ? total : (selectedLeadIds.length || total);
  const handleExportLeads = async () => {
    if (exportingLeads || exportCount === 0) return;
    setExportingLeads(true);
    try {
      const pageSize = 500;
      const all: ICampaignLead[] = [];
      for (let page = 1; ; page += 1) {
        const params: any = { limit: pageSize, page, ...activityParamsRef.current };
        if (statusFilter !== 'all') params.status = statusFilter;
        if (currentLeadSourceFilter()) params.source = currentLeadSourceFilter();
        if (leadSearch) params.search = leadSearch;
        const data: any = await getCampaignLeads(campaign._id, params, { scope: 'export' });
        if (!data) throw new Error('request failed');
        all.push(...(data.leads ?? []));
        if (all.length >= (data.total ?? 0) || !(data.leads ?? []).length) break;
      }
      const picked = isAllLeadsSelected || selectedLeadIds.length === 0
        ? all
        : all.filter(l => selectedLeadIds.includes(l._id));

      const header = ['Name', 'Title', 'Company', 'Location', 'LinkedIn URL', 'Email', 'Status', 'Source', 'Enrolled'];
      const rows = picked.map(l => [
        l.name || l.publicIdentifier || '',
        l.title || '',
        l.company || '',
        l.location || '',
        classicLinkedInProfileUrl(l) || '',
        l.email || '',
        STATUS_LABELS[l.status] || l.status || '',
        (l.source && SOURCE_CFG[l.source]?.label) || l.source || '',
        l.enrolledAt ? new Date(l.enrolledAt).toISOString().slice(0, 10) : '',
      ]);
      const slugify = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const stem = [slugify(campaign.name || 'campaign'), activeActivityLabel ? slugify(activeActivityLabel) : '', new Date().toISOString().slice(0, 10)]
        .filter(Boolean).join('_');
      // The BOM makes Excel read names with accents as UTF-8.
      download(new Blob(['\uFEFF', toCsv([header, ...rows])], { type: 'text/csv;charset=utf-8' }), `${stem}.csv`);
    } catch {
      setToast({ type: 'error', message: 'Could not export the leads. Please try again.' });
    } finally {
      setExportingLeads(false);
    }
  };

  const currentLeadSourceFilter = (): string | undefined => {
    if (sourceTab === 'all') return undefined;
    if (sourceTab === 'leadsearch' || sourceTab === 'contacts' || sourceTab === 'csv' || sourceTab === 'posts' || sourceTab === 'sn_lists') {
      return sourceTab;
    }
    return undefined;
  };

  const handleBulkRemove = () => {
    if (bulkDeleteCount === 0 || bulkRemoving) return;
    if (!guardOwner()) return;

    setDestructiveModal({
      headingText: 'Remove leads',
      descriptionText: `Are you sure you want to remove ${bulkDeleteCount} lead${bulkDeleteCount === 1 ? '' : 's'} from this campaign?`,
      // The modal's own default is "Delete"; everything else in this flow says remove, and
      // the leads are removed from the campaign, not deleted from the workspace.
      confirmBtnTitle: 'Remove leads',
      onCancelHandler: () => hideModal(),
      onConfirmHandler: async () => {
        hideModal();
        setBulkRemoving(true);
        try {
          const payload = isAllLeadsSelected
            ? {
                all: true,
                ...(statusFilter !== 'all' && { status: statusFilter }),
                ...(currentLeadSourceFilter() && { source: currentLeadSourceFilter() }),
                ...(leadSearch && { search: leadSearch }),
                ...bulkActivityParams,
              }
            : { leadIds: selectedLeadIds };

          const res = await bulkRemoveLeads(campaign._id, payload);
          if (res?.success) {
            const deleted = res?.data?.deleted ?? bulkDeleteCount;
            setToast({
              type: 'success',
              message: `${deleted} lead${deleted === 1 ? '' : 's'} removed from campaign.`,
            });
            clearLeadSelection();
            if (selectedLead && (isAllLeadsSelected || selectedLeadIds.includes(selectedLead._id))) {
              setSelectedLead(null);
            }
            void load(statusFilter, sourceTab === 'all' ? 'all' : sourceTab as any);
            // Repaint the search results, then refill the slots the deletion freed.
            void syncProspectEnrollmentRef.current();
            scheduleProspectRefillRef.current();
          } else {
            setToast({ type: 'error', message: 'Could not remove selected leads. Please try again.' });
          }
        } finally {
          setBulkRemoving(false);
        }
      },
    });
  };

  const refreshLeadsAfterMove = () => {
    clearLeadSelection();
    setSelectedLead(null);
    void load(statusFilter, sourceTab === 'all' ? 'all' : sourceTab as any);
    void syncProspectEnrollmentRef.current();
    scheduleProspectRefillRef.current();
  };

  const handleLeadsMoved = (result: { moved: number; alreadyEnrolled: number; suppressed: number }) => {
    const { moved, alreadyEnrolled, suppressed } = result;
    const listed = suppressed > 0 ? ` ${suppressed} on the suppression list stayed here.` : '';
    if (moved > 0) {
      const skipped = alreadyEnrolled > 0
        ? ` ${alreadyEnrolled} already in that campaign.`
        : '';
      setToast({
        type: 'success',
        message: `Moved ${moved} lead${moved === 1 ? '' : 's'}.${skipped}${listed} They start from the beginning.`,
      });
      refreshLeadsAfterMove();
      return;
    }
    if (suppressed > 0) {
      setToast({ type: 'info', message: `No leads were moved.${listed}` });
      return;
    }
    if (alreadyEnrolled > 0) {
      setToast({
        type: 'info',
        message: alreadyEnrolled === 1
          ? 'That lead is already in the selected campaign.'
          : 'Those leads are already in the selected campaign.',
      });
      return;
    }
    setToast({ type: 'info', message: 'No leads were moved.' });
  };

  const openMoveLeads = (singleLead?: ICampaignLead) => {
    if (!guardOwner()) return;
    const count = singleLead ? 1 : bulkDeleteCount;
    if (count === 0) return;

    setMoveToCampaignModal({
      sourceCampaignId: campaign._id,
      count,
      campaigns: moveTargetCampaigns,
      campaignsReady: moveTargetsReady,
      onMoved: handleLeadsMoved,
      ...(singleLead
        ? { leadIds: [singleLead._id] }
        : isAllLeadsSelected
          ? {
              all: true,
              ...(statusFilter !== 'all' && { status: statusFilter }),
              ...(currentLeadSourceFilter() && { source: currentLeadSourceFilter() }),
              ...(leadSearch && { search: leadSearch }),
              ...bulkActivityParams,
            }
          : { leadIds: selectedLeadIds }),
    });
  };

  const selectionProps: LeadsSelectionProps = {
    allOnPageSelected,
    pageSize: leadsPageSize,
    total,
    paginated: leads.length,
    isAllSelected: isAllLeadsSelected,
    selectedCount: selectedLeadIds.length,
    bulkDeleteCount,
    bulkRemoving,
    bulkRetryCount,
    bulkRetrying,
    onSelectAll: selectAllLeadsHandler,
    onSelectPage: selectLeadOnPageHandler,
    onClearSelection: clearLeadSelection,
    onBulkRemove: handleBulkRemove,
    onBulkMove: () => openMoveLeads(),
    onBulkRetry: handleBulkRetry,
  };

  const enrolledTotal = React.useMemo(() => sumEnrolledLeads(statusCounts), [statusCounts]);

  useEffect(() => {
    if (embedded) onEnrolledTotalChange?.(enrolledTotal);
  }, [embedded, enrolledTotal, onEnrolledTotalChange]);

  // Pin the source helper once we've confirmed no leads — stays visible across tab switches.
  useEffect(() => {
    if (fullPage && enrolledTotal === 0 && !loading) {
      setLeadsSourceHelperShown(true);
    }
  }, [fullPage, enrolledTotal, loading]);

  useEffect(() => {
    if (enrolledTotal > 0) {
      setLeadsSourceHelperShown(false);
      setLeadsSourceHelperDismissed(false);
    }
  }, [enrolledTotal]);

  useEffect(() => {
    setLeadsSourceHelperShown(false);
    setLeadsSourceHelperDismissed(false);
  }, [campaign._id]);

  const handleCampaignStatusChange = async (status: CampaignStatus) => {
    if (!guardOwner()) return;
    setStatusChanging(true);
    try {
      const data = await updateCampaign(campaign._id, { status });
      if (data?.campaign) {
        onCampaignUpdate({ ...data.campaign, statusCounts: campaign.statusCounts });
        if (status === 'active') {
          setToast({
            type: 'success',
            message: enrolledTotal === 0
              ? 'Campaign is live — outreach starts as soon as you add leads.'
              : 'Campaign is live — outreach has started.',
          });
        } else if (status === 'paused') {
          setToast({ type: 'success', message: 'Campaign paused.' });
        }
      } else {
        setToast({ type: 'error', message: (data as any)?.message || 'Could not update campaign status.' });
      }
    } finally {
      setStatusChanging(false);
    }
  };

  // Shown for empty drafts too — a campaign with no leads can go live and wait for them.
  const showStartPanel = fullPage && isOwner && campaign.status === 'draft';
  const showLeadsSourceHelper = isOwner && leadsSourceHelperShown && enrolledTotal === 0 && !leadsSourceHelperDismissed;
  // Starting with no leads is allowed — the campaign goes live and waits for them.
  const startWithoutLeads = enrolledTotal === 0 && campaign.status !== 'active' && campaign.status !== 'canceled';

  const campaignSequence = resolveCampaignSequence(campaign.sequence);
  const sequenceHasConnectionRequest = campaignSequence.some(s => s.type === 'connection_request');
  const emailStepPreference = campaignSequence.find(s => s.type === 'email')?.emailPreference;
  // This is the post-launch counterpart to the wizard's preflight card. It stays
  // available when new leads are enrolled so enrichment remains an explicit purchase.
  const showEmailCoverage = fullPage
    && isOwner
    && sequenceHasEmailStep
    && enrolledTotal > 0
    && campaign.status !== 'canceled';
  const panelBodyScrollable = showLeadsSourceHelper || showStartPanel || showEmailCoverage;
  /** Campaign page Leads tab: slim status chips (see CompactFunnel). */
  const compactFunnel = fullPage && hideHeader;
  // Queue InMail only belongs to the "InMail not sent" status — that's the only list it can act on.
  const showResendInMail = isProxyCampaign && hasSnMessageStep && statusFilter === 'skipped_not_open';

  const handleEditLeadMessage = useCallback((lead: ICampaignLead) => {
    if (!guardOwner()) return;
    const upcoming = getUpcomingTextStep(lead, campaignSequence);
    if (!upcoming) return;
    setMessageEdit({ lead, stepIndex: upcoming.stepIndex, step: upcoming.step });
  }, [campaignSequence, isOwner]);

  const handleLeadMessageSaved = useCallback((updated: ICampaignLead) => {
    setLeads(prev => prev.map(l => (l._id === updated._id ? updated : l)));
    if (selectedLead?._id === updated._id) setSelectedLead(updated);
  }, [selectedLead?._id]);

  const goToTargetingStep = useCallback(() => {
    if (embedded) {
      document.getElementById('wizard-targeting-prefs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    navigate(AppRoutes.CAMPAIGN_EDIT.replace(':id', campaign._id), {
      state: { wizardStep: CAMPAIGN_WIZARD_STEP_RECIPIENTS },
    });
  }, [embedded, campaign._id, navigate]);

  // Simulation runs globally in CampaignSection via useCampaignSimulation hook

  const { rows: csvRows, skipped: csvSkipped } = parseCsvText(csvText);
  const eligibleContacts = contactResults.filter(c => !c.alreadyEnrolled && !c.suppressed);
  const allSelected = eligibleContacts.length > 0 && selectedContactIds.size === eligibleContacts.length;

  const atLeadImportLimit = sourceTab === 'leadsearch' && total >= leadImportLimit;

  // One bar, rendered under whichever list is on screen (table, mobile cards, or the
  // enrolled list of a non-LeadSearch source) — they all page the same `leads` state.
  const leadsPaginationBar = total > 0 ? (
    <LeadsPagination
      page={leadsPage}
      pageSize={leadsPageSize}
      total={total}
      loading={loading}
      onPageChange={goToLeadsPage}
      onPageSizeChange={changeLeadsPageSize}
    />
  ) : null;

  // Nothing added yet: the "Added to campaign" tab is one empty state, so the name-search
  // row would only draw two stray dividers around it.
  const embeddedLeadSearchNoLeads =
    sourceTab === 'leadsearch' && leadSearchSubTab === 'enrolled' && total === 0 && !leadSearch;

  // The lead table renders its own header with the search box in it (same slot as the
  // picker's), so these tabs must not also draw the standalone search row above it.
  const showLeadListSearch =
    ((embedded && sourceTab === 'contacts' && contactsSubTab === 'enrolled')
    || (embedded && sourceTab === 'csv' && csvSubTab === 'enrolled')
    || (embedded && sourceTab === 'posts' && postsSubTab === 'enrolled')
    || (embedded && sourceTab === 'sn_lists' && snListsSubTab === 'enrolled'))
    && !embeddedLeadSearchNoLeads;

  const leadsSearchBox = (
    <LeadsSearchWrap>
      <SearchIconBox aria-hidden><SvgFragment type="search" /></SearchIconBox>
      <LeadsSearchInput
        type="search"
        value={leadSearchInput}
        onChange={e => setLeadSearchInput(e.target.value)}
        placeholder="Search by name…"
        aria-label="Search leads by name"
      />
      {leadSearchInput && (
        <LeadsSearchClear type="button" onClick={() => setLeadSearchInput('')} aria-label="Clear search">×</LeadsSearchClear>
      )}
    </LeadsSearchWrap>
  );

  // ─── Render ──────────────────────────────────────────────────────────────

  const inner = (
    <Panel $fullPage={fullPage || embedded} $embedded={embedded}>

        {!embedded && (
          <>
        <CampaignAccountLimitBanner campaign={campaign} />
        {/* The campaign page shows it above its tabs, so Analytics sees it too. */}
        {!hideHeader && <CampaignAccountReconnectBanner campaign={campaign} proxyAccounts={proxyAccounts} />}
          </>
        )}

        {/* Header */}
        {fullPage && !embedded && !hideHeader && (
        <PanelHeader alignItems spaceBetween>
          <Row alignItems gap="12px">
            {fullPage ? (
              <BackBtn type="button" onClick={onClose} aria-label="Back to campaigns">
                <SvgFragment type="arrowLeft" />
              </BackBtn>
            ) : (
              <HeaderIcon><SvgFragment type="sideCampaignsLucide" /></HeaderIcon>
            )}
            <Column gap="2px">
              <Text $bold style={{ fontSize: '15px', color: styles.colors.black600 }}>{campaign.name}</Text>
              <Text $paragraph3 style={{ color: styles.colors.black300 }}>
                {campaign.createdByName ? `Campaign leads · By ${campaign.createdByName}` : 'Campaign leads'}
              </Text>
            </Column>
          </Row>
          <Row alignItems gap="6px">
            {fullPage && !stopConfirm && (
              <>
                {campaign.status !== 'draft' && (
                  <RunTimerPill $running={!!campaign.runTimer?.running}>
                    <RunTimerDot $running={!!campaign.runTimer?.running} />
                    <span>{runTimeLabel}</span>
                  </RunTimerPill>
                )}
                <TooltipWrapper tooltipText={ownerActionTooltip || 'Pause the campaign to edit settings.'} placement="top">
                  <TooltipBtnWrap>
                    <HeaderActionBtn type="button" $variant="edit"
                      disabled={!isOwner || campaign.status === 'active' || campaign.status === 'canceled'}
                      onClick={() => navigate(AppRoutes.CAMPAIGN_EDIT.replace(':id', campaign._id))}
                    >
                      Edit
                    </HeaderActionBtn>
                  </TooltipBtnWrap>
                </TooltipWrapper>
                {!showStartPanel && (
                  <TooltipWrapper
                    tooltipText={ownerActionTooltip || (startWithoutLeads ? CAMPAIGN_START_NO_LEADS_HINT : '')}
                    placement="top"
                  >
                    <TooltipBtnWrap>
                      <HeaderActionBtn type="button" $variant="success"
                        disabled={!isOwner || campaign.status === 'active' || campaign.status === 'canceled' || statusChanging}
                        onClick={() => handleCampaignStatusChange('active')}
                      >
                        <SvgFragment type="campaignPlay" />
                        {statusChanging ? 'Starting…' : (campaign.status === 'paused' ? 'Resume' : 'Start')}
                      </HeaderActionBtn>
                    </TooltipBtnWrap>
                  </TooltipWrapper>
                )}
                <TooltipWrapper tooltipText={ownerActionTooltip || 'Temporarily stops outreach. Resume anytime.'} placement="top">
                  <TooltipBtnWrap>
                    <HeaderActionBtn type="button" $variant="warning"
                      disabled={!isOwner || campaign.status !== 'active'}
                      onClick={() => handleCampaignStatusChange('paused')}
                    >
                      <SvgFragment type="campaignPause" />
                      Pause
                    </HeaderActionBtn>
                  </TooltipBtnWrap>
                </TooltipWrapper>
                <TooltipWrapper tooltipText={ownerActionTooltip || 'Permanently cancels the campaign. Cannot be restarted.'} placement="top">
                  <TooltipBtnWrap>
                    <HeaderActionBtn type="button" $variant="ghost"
                      disabled={!isOwner || campaign.status === 'canceled'}
                      onClick={() => setStopConfirm(true)}
                    >
                      <SvgFragment type="campaignStop" />
                      Stop
                    </HeaderActionBtn>
                  </TooltipBtnWrap>
                </TooltipWrapper>
              </>
            )}
            {fullPage && stopConfirm && (
              <>
                <HintText>Stop campaign?</HintText>
                <HeaderActionBtn type="button" $variant="danger" onClick={async () => { await handleCampaignStatusChange('canceled'); setStopConfirm(false); }}>Confirm</HeaderActionBtn>
                <HeaderActionBtn type="button" $variant="ghost" onClick={() => setStopConfirm(false)}>Cancel</HeaderActionBtn>
              </>
            )}
            {!fullPage && <CloseBtn type="button" onClick={onClose}><SvgFragment type="close" /></CloseBtn>}
          </Row>
        </PanelHeader>
        )}

        <PanelBody $scrollable={panelBodyScrollable}>

        {!embedded && showLeadsSourceHelper && (
          <LeadsSourceHelper>
            <LeadsSourceHelperHeader>
              <LeadsSourceHelperTitle>How do you want to add leads?</LeadsSourceHelperTitle>
              <LeadsSourceHelperClose
                type="button"
                aria-label="Dismiss"
                onClick={() => setLeadsSourceHelperDismissed(true)}
              >
                <SvgFragment type="close" />
              </LeadsSourceHelperClose>
            </LeadsSourceHelperHeader>
            <LeadsSourceHelperList>
              <li>
                <strong>LeadSearch</strong> — Import new prospects that match your targeting filters (title, location, company size, etc.).
              </li>
              <li>
                <strong>LD Contacts</strong> — Add people already saved in your LeadDelta network.
              </li>
              <li>
                <strong>CSV Import</strong> — Upload a list of LinkedIn profile URLs.
              </li>
              <li>
                <strong>Post URL</strong> — Import commenters from a LinkedIn post.
              </li>
              <li>
                <strong>{isProxyCampaign ? 'Sales Navigator' : 'Sales Navigator lists'}</strong>
                {' — '}
                {isProxyCampaign
                  ? 'Import from a Sales Navigator search URL (cloud account or extension). With the extension you can also import saved lead lists.'
                  : 'Import people from a Sales Navigator lead list or search URL.'}
              </li>
            </LeadsSourceHelperList>
            {sequenceHasConnectionRequest && (
              <LeadsSourceHelperNote>
                Your sequence includes a connection request. <strong>LeadSearch skips people already in your LD Contacts</strong> — use LD Contacts for existing connections.
              </LeadsSourceHelperNote>
            )}
            <LeadsSourceHelperActions>
              <LeadsSourceHelperLink type="button" onClick={() => setSourceTab('leadsearch')}>LeadSearch</LeadsSourceHelperLink>
              <LeadsSourceHelperSep>·</LeadsSourceHelperSep>
              <LeadsSourceHelperLink type="button" onClick={() => { setSourceTab('contacts'); setContactsSubTab('search'); }}>LD Contacts</LeadsSourceHelperLink>
              <LeadsSourceHelperSep>·</LeadsSourceHelperSep>
              <LeadsSourceHelperLink type="button" onClick={() => { setSourceTab('csv'); setCsvSubTab('import'); }}>CSV Import</LeadsSourceHelperLink>
              <LeadsSourceHelperSep>·</LeadsSourceHelperSep>
              <LeadsSourceHelperLink type="button" onClick={() => { setSourceTab('posts'); setPostsSubTab('import'); }}>Post URL</LeadsSourceHelperLink>
              <LeadsSourceHelperSep>·</LeadsSourceHelperSep>
              <LeadsSourceHelperLink type="button" onClick={() => { setSourceTab('sn_lists'); setSnListsSubTab('import'); }}>
                {isProxyCampaign ? 'Sales Navigator' : 'Sales Navigator lists'}
              </LeadsSourceHelperLink>
              <LeadsSourceHelperSep>·</LeadsSourceHelperSep>
              <LeadsSourceHelperLink type="button" onClick={goToTargetingStep}>Edit targeting filters</LeadsSourceHelperLink>
            </LeadsSourceHelperActions>
          </LeadsSourceHelper>
        )}

        {!embedded && showStartPanel && (
          <CampaignStartPanel
            campaign={campaign}
            totalLeads={enrolledTotal}
            proxyAccounts={proxyAccounts}
            extensionInstalled={extensionInstalled}
            isLoggedToLinkedin={isLoggedToLinkedin}
            onStart={() => handleCampaignStatusChange('active')}
            starting={statusChanging}
            highlighted={highlightStartPanel}
          />
        )}

        {showEmailCoverage && (
          <EmailCoverageWrap>
            <CampaignEmailCoverage
              campaignId={campaign._id}
              preference={emailStepPreference}
              refreshKey={enrolledTotal}
              onEnriched={() => {
                const src = sourceTab === 'all' ? 'all' : sourceTab as SourceTab;
                void load(statusFilter, src, leadSearch, { silent: true });
              }}
            />
          </EmailCoverageWrap>
        )}

        {activityMode && (
          <ActivityBar>
            <ActivityChips>
              {/* The period applies to the history chips, so it leads the row. */}
              <PerformanceFilters
                chip
                range={activityRange}
                onRangeChange={setActivityRange}
                campaigns={[]}
                campaignIds={[]}
                onCampaignIdsChange={() => {}}
                identities={[]}
                identityIds={[]}
                onIdentityIdsChange={() => {}}
                showCampaignFilter={false}
                presets={LEAD_DATE_PRESETS}
              />
              {activityMetrics.map(m => {
                const active = activityMetric === m.key;
                return (
                  <ActivityChip
                    key={m.key}
                    type="button"
                    $active={active}
                    $color={m.color}
                    aria-pressed={active}
                    title={active ? 'Show all leads' : 'Show these leads'}
                    onClick={() => { setStatusFilter('all'); setActivityMetric(prev => (prev === m.key ? null : m.key)); }}
                  >
                    <ActivityChipIcon style={{ background: m.iconBg, borderColor: m.iconBorder, color: m.color }}>
                      <m.Icon size={14} strokeWidth={1.5} />
                    </ActivityChipIcon>
                    <span>{m.label}</span>
                    <ActivityChipCount>{activityCounts ? activityCounts[m.key] : '–'}</ActivityChipCount>
                  </ActivityChip>
                );
              })}
              {/* States rather than analytics numbers: who is still waiting, the list Queue InMail
                  works on, and the outcomes (Retry lives on Failed). Counted now, whatever the
                  period — the divider keeps them apart from the history chips. One chip at a time. */}
              <ChipDivider aria-hidden />
              {stateChips.map(({ status: st, Icon }) => {
                const active = statusFilter === st;
                const tone = STATUS_COLORS[st];
                return (
                  <ActivityChip
                    key={st}
                    type="button"
                    $active={active}
                    $color={tone.color}
                    aria-pressed={active}
                    title={active ? 'Show all leads' : LEAD_STATUS_HINTS[st]}
                    onClick={() => {
                      setActivityMetric(null);
                      setStatusFilter(prev => (prev === st ? 'all' : st));
                    }}
                  >
                    <ActivityChipIcon style={{ background: tone.bg, borderColor: tone.color, color: tone.color }}>
                      <Icon size={14} strokeWidth={1.5} />
                    </ActivityChipIcon>
                    <span>{STATUS_CFG[st].label}</span>
                    {/* Held back until the first load, so the whole row fills in together. */}
                    <ActivityChipCount>{activityCounts ? (statusCounts?.[st] ?? 0) : '–'}</ActivityChipCount>
                  </ActivityChip>
                );
              })}
            </ActivityChips>
          </ActivityBar>
        )}

        {!embedded && !activityMode && (
        <CompactFunnel $compact={compactFunnel}>
        {/* The counts are where leads are now, not totals — say so before anyone compares them with analytics. */}
        <FunnelHeading>
          Where leads are now
          <TooltipWrapper
            tooltipText="Each lead is counted once, at the step it's on now. Leads move along as steps are sent, so totals over time are in Campaign analytics. Click a status to filter the list."
            placement="top"
          >
            <FunnelHeadingInfo aria-label="About these counts">?</FunnelHeadingInfo>
          </TooltipWrapper>
        </FunnelHeading>
        <FunnelRow>
          {funnelStatuses.map((s, i) => (
            <React.Fragment key={s}>
              {i > 0 && <FunnelArrow>›</FunnelArrow>}
              <TooltipWrapper tooltipText={LEAD_STATUS_HINTS[s]} placement="top">
                <FunnelItem $active={statusFilter === s} onClick={() => setStatusFilter(prev => prev === s ? 'all' : s)}>
                  <FunnelCount $color={STATUS_CFG[s].dot}>{statusCounts?.[s] ?? 0}</FunnelCount>
                  <FunnelLabel>{STATUS_CFG[s].label}</FunnelLabel>
                </FunnelItem>
              </TooltipWrapper>
            </React.Fragment>
          ))}
          <OutcomeGroup>
            {/* Suppressed joins the outcomes only once the campaign has some — most never will. */}
            {(statusCounts?.suppressed || statusFilter === 'suppressed'
              ? [...OUTCOME_LEAD_STATUSES, 'suppressed' as CampaignLeadStatus]
              : OUTCOME_LEAD_STATUSES
            ).map((s, i) => (
              <React.Fragment key={s}>
                {i > 0 && <FunnelArrow $subtle>·</FunnelArrow>}
                <TooltipWrapper tooltipText={LEAD_STATUS_HINTS[s]} placement="top">
                  <FunnelItem $active={statusFilter === s} onClick={() => setStatusFilter(prev => prev === s ? 'all' : s)}>
                    <FunnelCount $color={STATUS_CFG[s].dot}>{statusCounts?.[s] ?? 0}</FunnelCount>
                    <FunnelLabel>{STATUS_CFG[s].label}</FunnelLabel>
                  </FunnelItem>
                </TooltipWrapper>
              </React.Fragment>
            ))}
          </OutcomeGroup>
          <TotalItem $active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>
            <FunnelCount $color={styles.colors.primary600}>{enrolledTotal}</FunnelCount>
            <FunnelLabel>Total</FunnelLabel>
          </TotalItem>
        </FunnelRow>
        </CompactFunnel>
        )}

        {/* Send totals live in Campaign analytics and "InMail not sent" is a status above;
            Queue InMail sits with the other bulk actions in the table header. */}
        {!embedded && hasSnMessageStep && inMailCreditBalance && (
          <MessagingStatsRow>
            {inMailCreditBalance && (
              <MessagingStat $accent>
                <MessagingStatVal>{inMailCreditBalance.value}</MessagingStatVal>
                <MessagingStatLabel>InMail credits ({inMailCreditBalance.detail})</MessagingStatLabel>
              </MessagingStat>
            )}
          </MessagingStatsRow>
        )}

        {/* Source tabs + controls */}
        <SourceArea $flush={embeddedLeadSearchNoLeads}>
          {/* Leads are added from Edit (the campaign is paused for it); a draft still adds them here. */}
          {!embedded && !(activityMode && campaign.status !== 'draft') && (
          <SourceTabBar>
            {([
              { id: 'all',        icon: 'sideContactsLucide',     label: 'In Campaign' },
              { id: 'leadsearch', icon: 'sideLeadSearchLucide',   label: 'LeadSearch' },
              { id: 'contacts',   icon: 'sideContactsLucide',     label: 'LD Contacts' },
              { id: 'csv',        icon: 'sideCsvEnrichLucide',    label: 'CSV Import' },
              { id: 'posts',      icon: 'messageSquareTextLucide', label: 'Post URL' },
              { id: 'sn_lists',   icon: 'list',                   label: isProxyCampaign ? 'Sales Navigator' : 'Sales Navigator lists' },
            ] as { id: SourceTab; icon: string; label: string }[])
              // Non-creators may only view enrolled leads — adding leads is restricted to the owner.
              .filter(tab => isOwner || tab.id === 'all')
              .map(tab => (
              <SrcTab key={tab.id} type="button" $active={sourceTab === tab.id} onClick={() => setSourceTab(tab.id)}>
                <SvgFragment type={tab.icon} />{tab.label}
              </SrcTab>
            ))}
          </SourceTabBar>
          )}

          {showLeadListSearch && (
            <LeadsSearchRow alignItems gap="8px">
              {leadsSearchBox}
              {leadSearch && !loading && (
                <HintText>{total} match{total === 1 ? '' : 'es'}</HintText>
              )}
            </LeadsSearchRow>
          )}

          {/* The In Campaign tab has no add-lead controls — the table's own header says what it lists. */}
          {sourceTab !== 'all' && (
          <SrcPanel>
            {/* LeadSearch — search the provider, pick, add. Same two-tab shape as LD Contacts. */}
            {sourceTab === 'leadsearch' && (
              <>
                <SubTabBar>
                  <SubTab $active={leadSearchSubTab === 'search'} onClick={() => setLeadSearchSubTab('search')}>
                    Search results
                  </SubTab>
                  <SubTab $active={leadSearchSubTab === 'enrolled'} onClick={() => setLeadSearchSubTab('enrolled')}>
                    Added to campaign{total > 0 ? <SubTabCount>{total}</SubTabCount> : null}
                  </SubTab>
                </SubTabBar>

                {leadSearchSubTab === 'search' && (
                  <ProspectsPanel>
                    <ProspectsHeader>
                      <Column gap="2px">
                        <ProspectsTitle>
                          {searchingProspects
                            ? 'Searching…'
                            : atLeadImportLimit
                              ? 'Campaign limit reached'
                              : prospectsSearched
                                ? `${filteredProspects.length.toLocaleString()} lead${filteredProspects.length === 1 ? '' : 's'} found`
                                : 'Set your targeting filters'}
                        </ProspectsTitle>
                        <ProspectsSub>
                          {atLeadImportLimit
                            ? `${total} / ${leadImportLimit} leads added to the campaign`
                            : !prospectsSearched
                              ? 'Results appear here as soon as the filters above match someone.'
                              : `${total} / ${leadImportLimit} leads added to the campaign`
                                + (lastSearchStats?.exhausted && filteredProspects.length < addableCount
                                  ? ' · LeadSearch has no more matches for these filters'
                                  : '')}
                        </ProspectsSub>
                      </Column>

                      <ProspectsHeaderRight>
                        <ProspectsSearchWrap>
                          <SearchIconBox aria-hidden><SvgFragment type="search" /></SearchIconBox>
                          {/* Narrows the results already on screen by name — it does not
                              run a new provider search. */}
                          <LeadsSearchInput
                            type="search"
                            value={prospectNameFilter}
                            onChange={e => { setProspectNameFilter(e.target.value); setProspectPage(1); }}
                            placeholder="Search by name…"
                            aria-label="Search these results by name"
                          />
                        </ProspectsSearchWrap>
                        <AddToCampaignBtn
                          type="button"
                          onClick={handleAddProspects}
                          disabled={submitting || selectedProspectIds.size === 0 || atLeadImportLimit || !isOwner}
                        >
                          {submitting
                            ? <><Spinner /> Adding…</>
                            : `+ Add to campaign${selectedProspectIds.size ? ` (${selectedProspectIds.size})` : ''}`}
                        </AddToCampaignBtn>
                      </ProspectsHeaderRight>
                    </ProspectsHeader>

                    {searchError ? (
                      <ProspectsEmptyBox>
                        <LeadSearchEmptyTitle>{searchError}</LeadSearchEmptyTitle>
                        <LeadSearchEmptyText>
                          The search did not come back. Check your connection and try again.
                        </LeadSearchEmptyText>
                        <ProspectsRetryBtn
                          type="button"
                          onClick={() => void runProspectSearch(effectiveLeadPrefs)}
                        >
                          Try again
                        </ProspectsRetryBtn>
                      </ProspectsEmptyBox>
                    /* The debounce window counts as searching: filters are set, the
                       request has just not left yet, and an empty panel in between
                       reads as "no results". */
                    ) : (searchingProspects || (hasPreferences && !prospectsSearched)) && prospectResults.length === 0 ? (
                      <EmptyBox><HintText><InlineSpinner /> Searching for leads…</HintText></EmptyBox>
                    ) : !hasPreferences ? (
                      <ProspectsEmptyBox>
                        <LeadSearchEmptyTitle>No targeting filters set</LeadSearchEmptyTitle>
                        <LeadSearchEmptyText>
                          Pick a role, location, industry or company above and matching people show up here.
                        </LeadSearchEmptyText>
                      </ProspectsEmptyBox>
                    ) : prospectsSearched && filteredProspects.length === 0 ? (
                      <ProspectsEmptyBox>
                        <LeadSearchEmptyTitle>
                          {prospectNameFilter ? 'No results match that name' : 'No prospects matched these filters'}
                        </LeadSearchEmptyTitle>
                        <LeadSearchEmptyText>
                          {prospectNameFilter
                            ? 'Clear the name filter to see the full result set.'
                            : <>
                                {lastSearchStats && lastSearchStats.skippedConnections > 0 && (
                                  <>· {lastSearchStats.skippedConnections} matches are already in your LD Contacts (skipped for outreach).<br /></>
                                )}
                                {lastSearchStats && lastSearchStats.skippedSuppressed > 0 && (
                                  <>· {lastSearchStats.skippedSuppressed} matches are on the workspace suppression list (skipped).<br /></>
                                )}
                                Try broadening the filters, or add leads from <strong>LD Contacts</strong> or a <strong>CSV</strong> instead.
                              </>}
                        </LeadSearchEmptyText>
                      </ProspectsEmptyBox>
                    ) : prospectsSearched ? (
                      <ProspectsResults>
                        <ProspectHeaderRow>
                          {/* Same control and same trick as the lead table: the menu is
                              positioned over the row, so its counter can be wider than the
                              checkbox column without pushing the columns around. */}
                          <ProspectSelectAllAnchor onClick={e => e.stopPropagation()}>
                            <SelectAllCheckbox
                              isCheckboxChecked={prospectsOnPageSelected}
                              pageSize={prospectPageSize}
                              total={selectableProspects.length}
                              paginated={selectableOnPageCount}
                              isAllSelected={allProspectsSelected}
                              selected={selectedProspectIds.size}
                              label="leads"
                              selectAllHandler={selectAllProspects}
                              selectOnThisPageHadler={selectProspectsOnPage}
                              clearHandler={clearProspectSelection}
                              backgroundColor="#fafbff"
                            />
                          </ProspectSelectAllAnchor>
                          <span />
                          <ProspectHeaderLabel style={{ gridColumn: 'span 2' }}>
                            {selectedProspectIds.size > 0 ? '' : 'Lead'}
                          </ProspectHeaderLabel>
                          <ProspectHeaderMeta>Company</ProspectHeaderMeta>
                          <ProspectHeaderMeta>Location</ProspectHeaderMeta>
                        </ProspectHeaderRow>

                        {prospectPageRows.map(p => {
                          const key = prospectKey(p);
                          const profileUrl = classicLinkedInProfileUrl(p);
                          return (
                            <ProspectRow key={key} $disabled={p.alreadyEnrolled}>
                              <ProspectCheckboxCell>
                                <StyledCheckbox
                                  checked={selectedProspectIds.has(key)}
                                  disabled={p.alreadyEnrolled}
                                  onClickHandler={() => !p.alreadyEnrolled && toggleProspect(key)}
                                  checkboxId={`prospect-${key}`}
                                />
                              </ProspectCheckboxCell>
                              <CampaignAvatar imageUrl={p.imageUrl} name={p.name} fallback={p.publicIdentifier} size={36} />
                              {/* The "In campaign" tag sits with the name: as a sixth grid
                                  child it spilled past the last column. */}
                              <Column gap="1px" style={{ minWidth: 0 }}>
                                <Row alignItems gap="6px" style={{ minWidth: 0 }}>
                                  <LeadName>
                                    {profileUrl
                                      ? <a href={profileUrl} target="_blank" rel="noopener noreferrer">{fieldText(p.name) || fieldText(p.publicIdentifier) || '—'}</a>
                                      : (fieldText(p.name) || fieldText(p.publicIdentifier) || '—')}
                                  </LeadName>
                                  {p.alreadyEnrolled && (
                                    <MiniTag $color={styles.colors.black300} $bg="#f1f3f8">In campaign</MiniTag>
                                  )}
                                </Row>
                                <LeadSub>{fieldText(p.title) || '—'}</LeadSub>
                              </Column>
                              <ProspectMeta>{fieldText(p.company) || '—'}</ProspectMeta>
                              <ProspectMeta $muted>{fieldText(p.location) || '—'}</ProspectMeta>
                              <TooltipWrapper tooltipText="Remove from search results" placement="top">
                                <ProspectDismissBtn
                                  type="button"
                                  aria-label={`Dismiss ${fieldText(p.name) || 'lead'}`}
                                  onClick={() => dismissProspect(key)}
                                >
                                  ✕
                                </ProspectDismissBtn>
                              </TooltipWrapper>
                            </ProspectRow>
                          );
                        })}

                        <LeadsPagination
                          page={prospectPage}
                          pageSize={prospectPageSize}
                          total={filteredProspects.length}
                          loading={searchingProspects}
                          onPageChange={setProspectPage}
                          onPageSizeChange={size => { setProspectPageSize(size); setProspectPage(1); }}
                        />
                      </ProspectsResults>
                    ) : null}
                  </ProspectsPanel>
                )}

                {/* "Added to campaign" is rendered by the main lead table below, which
                    already carries the columns, bulk removal and paging the design asks
                    for — see the TableAndSidebar block. */}
              </>
            )}

            {/* LD Contacts */}
            {sourceTab === 'contacts' && (
              <>
                {embedded && (
                <SubTabBar>
                  <SubTab $active={contactsSubTab === 'search'} onClick={() => setContactsSubTab('search')}>
                    Search & Add
                  </SubTab>
                  <SubTab $active={contactsSubTab === 'enrolled'} onClick={() => setContactsSubTab('enrolled')}>
                    In Campaign{total > 0 ? <SubTabCount>{total}</SubTabCount> : null}
                  </SubTab>
                </SubTabBar>
                )}

                {(embedded ? contactsSubTab === 'search' : true) && (
                  <ContactsPanel>
                    <SearchToolbar>
                      {campaignFilterActive && (
                        <CampaignContactsFilterBar
                          onFilterChange={() => void handleSearchContacts(1)}
                          onClearContactResults={clearContactResults}
                        />
                      )}
                    </SearchToolbar>

                    <Row alignItems gap="8px" style={{ marginTop: '10px' }}>
                      {searchingContacts && (
                        <HintText><Spinner /> Searching contacts…</HintText>
                      )}
                      {contactsSearched && selectedContactIds.size > 0 && (
                        <ActionBtn onClick={handleEnrollContacts} disabled={submitting} style={{ background: styles.colors.green600 }}>
                          {submitting ? <><Spinner /> Adding…</> : `+ Add ${selectedContactIds.size} to campaign`}
                        </ActionBtn>
                      )}
                    </Row>

                    {!contactsSearched && !searchingContacts && (
                      <ContactsEmptyState>
                        <ContactsEmptyIcon><SvgFragment type="sideContactsLucide" /></ContactsEmptyIcon>
                        <ContactsEmptyTitle>Find people already in your network</ContactsEmptyTitle>
                        <ContactsEmptyText>
                          Search by location or job title, apply filters, or tap a quick filter above — then select contacts to add them to this campaign.
                        </ContactsEmptyText>
                      </ContactsEmptyState>
                    )}

                    {contactsSearched && (
                      <ContactResults>
                        <ContactResultHeader alignItems>
                          <Row alignItems gap="10px">
                            <ContactCheckbox type="checkbox" checked={allSelected} onChange={toggleAllContacts} title="Select all" />
                            <ResultCount>
                              {contactTotal.toLocaleString()} contact{contactTotal !== 1 ? 's' : ''}
                              {contactTotal > contactResults.length && <ResultDim> · showing {contactResults.length}</ResultDim>}
                            </ResultCount>
                          </Row>
                          {selectedContactIds.size > 0 && <SelectedPill>{selectedContactIds.size} selected</SelectedPill>}
                        </ContactResultHeader>
                        {contactResults.map(c => (
                          <ContactRow key={c.connectionId} $selected={selectedContactIds.has(c.connectionId)} $disabled={c.alreadyEnrolled || c.suppressed}>
                            <ContactCheckbox type="checkbox" checked={selectedContactIds.has(c.connectionId)} disabled={c.alreadyEnrolled || c.suppressed} onChange={() => !c.alreadyEnrolled && !c.suppressed && toggleContact(c.connectionId)} />
                            <CampaignAvatar imageUrl={c.imageUrl} name={c.name} fallback={c.publicIdentifier} size={36} />
                            <Column gap="2px" style={{ flex: 1, minWidth: 0 }}>
                              <ContactName href={classicLinkedInProfileUrl(c) || undefined} target="_blank" rel="noopener noreferrer">{c.name || c.publicIdentifier || '—'}</ContactName>
                              <ContactSub>{[c.title, c.company].filter(Boolean).join(' · ') || '—'}</ContactSub>
                            </Column>
                            <Row alignItems gap="6px">
                              {c.isProspect && <MiniTag $color={styles.colors.primary600} $bg={styles.colors.primary100}>Prospect</MiniTag>}
                              {c.isImported && <MiniTag $color={styles.colors.orange600} $bg={styles.colors.yellow200}>Imported</MiniTag>}
                              {c.alreadyEnrolled && <MiniTag $color={styles.colors.black300} $bg="#f1f3f8">In campaign</MiniTag>}
                              {c.suppressed && !c.alreadyEnrolled && (
                                <MiniTag $color={STATUS_COLORS.suppressed.color} $bg={STATUS_COLORS.suppressed.bg} title="On the workspace suppression list">
                                  Suppressed
                                </MiniTag>
                              )}
                            </Row>
                          </ContactRow>
                        ))}
                        {contactTotal > contactResults.length && (
                          <LoadMoreRow>
                            <GhostBtn onClick={() => handleSearchContacts(contactPage + 1)} disabled={searchingContacts}>
                              {searchingContacts ? <><Spinner /> Loading…</> : 'Load more'}
                            </GhostBtn>
                          </LoadMoreRow>
                        )}
                      </ContactResults>
                    )}
                  </ContactsPanel>
                )}

                {embedded && contactsSubTab === 'enrolled' && (
                  <EnrolledPanel
                    leads={leads}
                    loading={loading}
                    sequence={campaignSequence}
                    onEditMessage={isOwner ? handleEditLeadMessage : undefined}
                    onStatusChange={handleStatusChange}
                    onMove={isOwner ? openMoveLeads : undefined}
                    onRemove={handleRemove}
                    onRetry={handleRetry}
                    removingIds={removingIds}
                    retryingIds={retryingIds}
                    emptyMessage={leadSearch ? `No leads match "${leadSearch}".` : 'No LD Contacts enrolled yet.'}
                    selection={selectionProps}
                    selectedLeadIds={selectedLeadIds}
                    isAllSelected={isAllLeadsSelected}
                    onSelectLead={selectLeadCheckboxHandler}
                    footer={leadsPaginationBar}
                    showStatus={showLeadStatus}
                  />
                )}
              </>
            )}

            {/* CSV Import */}
            {sourceTab === 'csv' && (
              <>
                {embedded && (
                <SubTabBar>
                  <SubTab $active={csvSubTab === 'import'} onClick={() => setCsvSubTab('import')}>
                    Import
                  </SubTab>
                  <SubTab $active={csvSubTab === 'enrolled'} onClick={() => setCsvSubTab('enrolled')}>
                    In Campaign{total > 0 ? <SubTabCount>{total}</SubTabCount> : null}
                  </SubTab>
                </SubTabBar>
                )}

                {(embedded ? csvSubTab === 'import' : true) && (
                  <CsvPanel>
                    <CsvHeader>Paste LinkedIn profile URLs or public identifiers — one per line — or upload a file. Up to {CSV_MAX_ROWS.toLocaleString()} rows per import.</CsvHeader>
                    <CsvLayout>
                      <CsvTextarea
                        value={csvText}
                        onChange={e => setCsvText(e.target.value)}
                        placeholder={'john-doe-abc123\nhttps://linkedin.com/in/jane-smith\n\nOr a CSV with columns:\npublicIdentifier, name, title, company, location\n\nOr a single LinkedIn URL column:\nLinkedIn\nhttps://www.linkedin.com/in/jane-smith'}
                        spellCheck={false}
                      />
                      <CsvDropZone onClick={() => fileInputRef.current?.click()} title="Upload CSV">
                        <CsvDropIcon><SvgFragment type="sideCsvEnrichLucide" /></CsvDropIcon>
                        <CsvDropLabel>Upload a file</CsvDropLabel>
                        <CsvDropSub>Click to browse<br />.csv or .txt</CsvDropSub>
                        <input ref={fileInputRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={handleFileUpload} />
                      </CsvDropZone>
                    </CsvLayout>
                    <CsvActions>
                      <ActionBtn onClick={handleEnrollCsv} disabled={submitting || csvRows.length === 0 || csvRows.length > CSV_MAX_ROWS}>
                        {submitting ? <><Spinner /> Importing…</> : csvRows.length > 0 ? `Import ${csvRows.length.toLocaleString()} lead${csvRows.length !== 1 ? 's' : ''}` : 'Import leads'}
                      </ActionBtn>
                      {csvText && <GhostBtn onClick={() => setCsvText('')}>Clear</GhostBtn>}
                      {(csvRows.length > 0 || csvSkipped > 0) && (
                        <CsvDetectedBadge>
                          {csvRows.length > CSV_MAX_ROWS
                            ? `${csvRows.length.toLocaleString()} rows — over the ${CSV_MAX_ROWS.toLocaleString()} row limit`
                            : `${csvRows.length.toLocaleString()} row${csvRows.length !== 1 ? 's' : ''} detected`}
                          {/* Rows with no readable identifier used to vanish without a trace. */}
                          {csvSkipped > 0 && ` · ${csvSkipped.toLocaleString()} unreadable`}
                        </CsvDetectedBadge>
                      )}
                    </CsvActions>
                  </CsvPanel>
                )}

                {embedded && csvSubTab === 'enrolled' && (
                  <EnrolledPanel
                    leads={leads}
                    loading={loading}
                    sequence={campaignSequence}
                    onEditMessage={isOwner ? handleEditLeadMessage : undefined}
                    onStatusChange={handleStatusChange}
                    onMove={isOwner ? openMoveLeads : undefined}
                    onRemove={handleRemove}
                    onRetry={handleRetry}
                    removingIds={removingIds}
                    retryingIds={retryingIds}
                    emptyMessage={leadSearch ? `No leads match "${leadSearch}".` : 'No CSV leads enrolled yet.'}
                    selection={selectionProps}
                    selectedLeadIds={selectedLeadIds}
                    isAllSelected={isAllLeadsSelected}
                    onSelectLead={selectLeadCheckboxHandler}
                    footer={leadsPaginationBar}
                    showStatus={showLeadStatus}
                  />
                )}
              </>
            )}

            {/* Post URL — LinkedIn post commenters */}
            {sourceTab === 'posts' && (
              <>
                {embedded && (
                <SubTabBar>
                  <SubTab $active={postsSubTab === 'import'} onClick={() => setPostsSubTab('import')}>
                    Import
                  </SubTab>
                  <SubTab $active={postsSubTab === 'enrolled'} onClick={() => setPostsSubTab('enrolled')}>
                    In Campaign{total > 0 ? <SubTabCount>{total}</SubTabCount> : null}
                  </SubTab>
                </SubTabBar>
                )}

                {(embedded ? postsSubTab === 'import' : true) && (
                  <CsvPanel>
                    <CsvHeader>
                      {isProxyCampaign
                        ? 'Paste a LinkedIn post URL — we’ll import commenters via the connected LinkedIn account (no extension).'
                        : 'Paste a LinkedIn post URL — we’ll pull in everyone who commented.'}
                    </CsvHeader>
                    {isProxyCampaign && !hasUnipileAccount && !postsTrialLimitReached && (
                      <PostsTrialLimitText style={{ marginBottom: 12 }}>
                        Connect a LinkedIn account to this campaign before importing post commenters.
                      </PostsTrialLimitText>
                    )}
                    {postsTrialLimitReached ? (
                      <PostsTrialLimitCard>
                        <PostsTrialLimitTitle>Trial import limit reached</PostsTrialLimitTitle>
                        <PostsTrialLimitText>
                          {postCommentersAccess?.message
                            || 'Your trial post import limit has been reached. Upgrade to Growth for unlimited imports.'}
                        </PostsTrialLimitText>
                        <PostsTrialLimitLink
                          type="button"
                          onClick={() => navigate(AppRoutes.BILLING)}
                        >
                          Upgrade to Growth
                        </PostsTrialLimitLink>
                      </PostsTrialLimitCard>
                    ) : (
                      <>
                        <PostUrlLayout>
                          <PostUrlInput
                            type="url"
                            value={postUrl}
                            onChange={e => setPostUrl(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                void handleImportPostCommentators();
                              }
                            }}
                            placeholder="https://www.linkedin.com/posts/…"
                            spellCheck={false}
                            $valid={postUrlValid}
                            $dirty={!!postUrl.trim()}
                          />
                          <ActionBtn
                            onClick={handleImportPostCommentators}
                            disabled={submitting || !postUrlValid || (isProxyCampaign && !hasUnipileAccount)}
                          >
                            {submitting ? <><Spinner /> {postsImportStatus || 'Importing…'}</> : 'Import commenters'}
                          </ActionBtn>
                        </PostUrlLayout>
                        <CsvActions>
                          {postUrlValid && !submitting && (
                            <CsvDetectedBadge>Valid post URL</CsvDetectedBadge>
                          )}
                          {!!postUrl.trim() && !postUrlValid && !submitting && (
                            <PostUrlInvalidBadge>Not a LinkedIn post URL</PostUrlInvalidBadge>
                          )}
                          {submitting && postsImportStatus && (
                            <CsvDetectedBadge>{postsImportStatus}</CsvDetectedBadge>
                          )}
                          {postsTrialHint && !submitting && (
                            <PostsTrialHint>{postsTrialHint}</PostsTrialHint>
                          )}
                        </CsvActions>
                      </>
                    )}
                  </CsvPanel>
                )}

                {embedded && postsSubTab === 'enrolled' && (
                  <EnrolledPanel
                    leads={leads}
                    loading={loading}
                    sequence={campaignSequence}
                    onEditMessage={isOwner ? handleEditLeadMessage : undefined}
                    onStatusChange={handleStatusChange}
                    onMove={isOwner ? openMoveLeads : undefined}
                    onRemove={handleRemove}
                    onRetry={handleRetry}
                    removingIds={removingIds}
                    retryingIds={retryingIds}
                    emptyMessage={leadSearch ? `No leads match "${leadSearch}".` : 'No post commenters enrolled yet.'}
                    selection={selectionProps}
                    selectedLeadIds={selectedLeadIds}
                    isAllSelected={isAllLeadsSelected}
                    onSelectLead={selectLeadCheckboxHandler}
                    footer={leadsPaginationBar}
                    showStatus={showLeadStatus}
                  />
                )}
              </>
            )}

            {/* Sales Navigator — URL via Unipile and/or extension; saved lists via extension. */}
            {sourceTab === 'sn_lists' && (
              <>
                {embedded && (
                <SubTabBar>
                  <SubTab $active={snListsSubTab === 'import'} onClick={() => setSnListsSubTab('import')}>
                    Import
                  </SubTab>
                  <SubTab $active={snListsSubTab === 'enrolled'} onClick={() => setSnListsSubTab('enrolled')}>
                    In Campaign{total > 0 ? <SubTabCount>{total}</SubTabCount> : null}
                  </SubTab>
                </SubTabBar>
                )}

                {(embedded ? snListsSubTab === 'import' : true) && (
                  <CsvPanel>
                    <CsvHeader>
                      {isProxyCampaign && !snExtensionReady
                        ? 'Paste a Sales Navigator people search URL. We’ll import via your connected LinkedIn account. Install the LeadDelta extension to also import saved lists (or run the search through the extension).'
                        : 'Run your search in Sales Navigator, then paste the URL here — or pick a saved lead list. We’ll skip anyone already in this campaign.'}
                    </CsvHeader>
                    {isProxyCampaign && !canSnImportUrl && (
                      <HintText style={{ marginBottom: 12 }}>
                        Connect a LinkedIn account to this campaign, or install the LeadDelta extension and log in to LinkedIn.
                      </HintText>
                    )}
                    <SnSearchUrlSection>
                      <PostUrlLayout>
                        <PostUrlInput
                          type="url"
                          value={snSearchUrl}
                          onChange={e => setSnSearchUrl(e.target.value)}
                          placeholder="https://www.linkedin.com/sales/search/people?query=..."
                          $valid={snSearchUrlValid}
                          $dirty={!!snSearchUrl.trim()}
                          disabled={snPreviewLoading || submitting}
                        />
                        <ActionBtn
                          type="button"
                          onClick={() => void handleImportSnSearchUrl()}
                          disabled={
                            !snSearchUrlValid
                            || snListsLoading
                            || snPreviewLoading
                            || submitting
                            || !canSnImportUrl
                          }
                        >
                          {snImportSource === 'url' && (snPreviewLoading || submitting)
                            ? <><Spinner /> Importing search…</>
                            : 'Import search'}
                        </ActionBtn>
                      </PostUrlLayout>
                      <CsvActions>
                        {snImportSource === 'url' && (snPreviewLoading || submitting) && (
                          <GhostBtn type="button" onClick={handleCancelSnListFetch}>Stop</GhostBtn>
                        )}
                        {!!snSearchUrl.trim() && !snSearchUrlValid && !snPreviewLoading && (
                          <PostUrlInvalidBadge>Not a Sales Navigator people search URL</PostUrlInvalidBadge>
                        )}
                        {snSearchUrlValid && !snPreviewLoading && (
                          <CsvDetectedBadge>Valid search URL</CsvDetectedBadge>
                        )}
                        {snImportSource === 'url' && snPreviewStatus && (
                          <CsvDetectedBadge>{snPreviewStatus}</CsvDetectedBadge>
                        )}
                      </CsvActions>
                    </SnSearchUrlSection>
                    {showSnListsUi && (
                      <>
                    <SnSourceDivider><span>or choose a saved lead list</span></SnSourceDivider>
                    <CsvActions>
                      <ActionBtn
                        onClick={() => void handleLoadSnLists()}
                        disabled={snListsLoading || snPreviewLoading || submitting}
                      >
                        {snListsLoading ? <><Spinner /> Loading lists…</> : snListsLoaded ? 'Refresh lists' : 'Load lists'}
                      </ActionBtn>
                      <ActionBtn
                        onClick={() => void handleImportSnList()}
                        disabled={
                          !selectedSnListId
                          || snListsLoading
                          || snPreviewLoading
                          || submitting
                        }
                      >
                        {snImportSource === 'list' && (snPreviewLoading || submitting)
                          ? <><Spinner /> Importing list…</>
                          : 'Import list'}
                      </ActionBtn>
                      {snImportSource === 'list' && (snPreviewLoading || submitting) && (
                        <GhostBtn type="button" onClick={handleCancelSnListFetch}>Stop</GhostBtn>
                      )}
                      {snListsLoaded && !snListsLoading && !(snPreviewLoading || submitting) && (
                        <CsvDetectedBadge>
                          {snLists.length.toLocaleString()} list{snLists.length !== 1 ? 's' : ''} found
                        </CsvDetectedBadge>
                      )}
                      {snImportSource === 'list' && (snPreviewLoading || submitting) && snPreviewStatus && (
                        <CsvDetectedBadge>{snPreviewStatus}</CsvDetectedBadge>
                      )}
                    </CsvActions>
                    {snListsLoading && !snLists.length && (
                      <HintText style={{ marginTop: 12 }}><Spinner /> Fetching Sales Navigator lists…</HintText>
                    )}
                    {snListsLoaded && !snListsLoading && snLists.length === 0 && (
                      <HintText style={{ marginTop: 12 }}>No lead lists found. Create one in Sales Navigator, then refresh.</HintText>
                    )}
                    {snLists.length > 0 && (
                      <SnListGrid>
                        {snLists.map(list => (
                          <SnListCard
                            key={list.id}
                            type="button"
                            $active={selectedSnListId === list.id}
                            disabled={snPreviewLoading || submitting}
                            onClick={() => {
                              if (snPreviewLoading || submitting) return;
                              setSelectedSnListId(list.id);
                              if (snPreviewListId && snPreviewListId !== list.id) {
                                setSnPreviewProfiles([]);
                                setSnPreviewTotal(null);
                                setSnPreviewListId(null);
                                setSnPreviewStatus('');
                                setSnPreviewPage(0);
                              }
                            }}
                          >
                            <SnListName>{list.name}</SnListName>
                            <SnListMeta>
                              {typeof list.entityCount === 'number'
                                ? `${list.entityCount.toLocaleString()} lead${list.entityCount !== 1 ? 's' : ''}`
                                : '—'}
                              {list.listSource ? ` · ${list.listSource}` : ''}
                            </SnListMeta>
                          </SnListCard>
                        ))}
                      </SnListGrid>
                    )}
                      </>
                    )}
                  </CsvPanel>
                )}

                {embedded && snListsSubTab === 'enrolled' && (
                  <EnrolledPanel
                    leads={leads}
                    loading={loading}
                    sequence={campaignSequence}
                    onEditMessage={isOwner ? handleEditLeadMessage : undefined}
                    onStatusChange={handleStatusChange}
                    onMove={isOwner ? openMoveLeads : undefined}
                    onRemove={handleRemove}
                    onRetry={handleRetry}
                    removingIds={removingIds}
                    retryingIds={retryingIds}
                    emptyMessage={leadSearch ? `No leads match "${leadSearch}".` : 'No Sales Navigator leads enrolled yet.'}
                    selection={selectionProps}
                    selectedLeadIds={selectedLeadIds}
                    isAllSelected={isAllLeadsSelected}
                    onSelectLead={selectLeadCheckboxHandler}
                    footer={leadsPaginationBar}
                    showStatus={showLeadStatus}
                  />
                )}
              </>
            )}
          </SrcPanel>
          )}
        </SourceArea>

        {/* Lead table — the campaign's enrolled leads. For LeadSearch it backs the
            "Added to campaign" tab; the "Search results" tab has its own picker above. */}
        {/* Header above the table, never inside TableAndSidebar — that is a flex *row*
            (table + lead sidebar), so a child there lands beside the table, not over it. */}
        {(sourceTab === 'all' || (sourceTab === 'leadsearch' && leadSearchSubTab === 'enrolled')) && (
          <ProspectsHeader $inPanelBody>
            <Column gap="2px">
              <ProspectsTitle>
                {sourceTab === 'all' ? 'Leads in this campaign' : 'Leads added to campaign'}
              </ProspectsTitle>
              <ProspectsSub>
                {sourceTab === 'all'
                  ? (activeActivityLabel
                    ? `${total} lead${total === 1 ? '' : 's'} · ${activeActivityLabel}`
                    : `${total} lead${total === 1 ? '' : 's'} enrolled`)
                  : `${total} / ${leadImportLimit} leads added to the campaign`}
                {leadSearch && !loading ? ` · ${total} matching "${leadSearch}"` : ''}
              </ProspectsSub>
            </Column>
            <ProspectsHeaderRight>
              {leadsSearchBox}
              {isOwner && (
                <>
                  {showResendInMail && (
                    <QueueInMailBtn
                      type="button"
                      onClick={handleBulkResendInMail}
                      disabled={resendingInMail || bulkResendInMailCount === 0}
                    >
                      {resendingInMail
                        ? 'Queueing…'
                        : `Queue InMail${bulkResendInMailCount ? ` (${bulkResendInMailCount})` : ''}`}
                    </QueueInMailBtn>
                  )}
                  <MoveLeadsBtn
                    type="button"
                    onClick={() => openMoveLeads()}
                    disabled={selectionProps.bulkDeleteCount === 0}
                  >
                    <SvgFragment type="moveLead" />
                    {`Move${selectionProps.bulkDeleteCount ? ` (${selectionProps.bulkDeleteCount})` : ''}`}
                  </MoveLeadsBtn>
                  <RemoveLeadsBtn
                    type="button"
                    onClick={handleBulkRemove}
                    disabled={bulkRemoving || selectionProps.bulkDeleteCount === 0}
                  >
                    <SvgFragment type="trashCan" />
                    {bulkRemoving
                      ? 'Removing…'
                      : `Remove leads${selectionProps.bulkDeleteCount ? ` (${selectionProps.bulkDeleteCount})` : ''}`}
                  </RemoveLeadsBtn>
                </>
              )}
              <ExportLeadsBtn
                type="button"
                onClick={handleExportLeads}
                disabled={exportingLeads || exportCount === 0}
                title={selectedLeadIds.length || isAllLeadsSelected ? 'Export the selected leads' : 'Export all leads shown'}
              >
                <Download size={15} strokeWidth={2} />
                {exportingLeads ? 'Exporting…' : `Export${exportCount ? ` (${exportCount})` : ''}`}
              </ExportLeadsBtn>
            </ProspectsHeaderRight>
          </ProspectsHeader>
        )}

        {(sourceTab === 'all' || (sourceTab === 'leadsearch' && leadSearchSubTab === 'enrolled')) && <TableAndSidebar $inScrollBody={panelBodyScrollable}>
          <TableArea $inScrollBody={panelBodyScrollable}>
            {loading && leads.length === 0 ? (
              <EmptyBox aria-busy="true" aria-label="Loading leads">
                <LoadingSpinner height="28px" width="28px" />
              </EmptyBox>
            ) : leads.length === 0 ? (
              <EmptyBox>
                <EmptyIllustration><SvgFragment type={leadSearch ? 'searchEmpty' : 'sideCampaignsLucide'} /></EmptyIllustration>
                <EmptyTitle>
                  {leadSearch
                    ? 'No matching leads'
                    : filteredEmpty
                      ? 'No leads match these filters'
                      : sourceTab === 'leadsearch'
                      ? 'No leads added yet'
                      : 'No leads yet'}
                </EmptyTitle>
                <HintText>
                  {leadSearch
                    ? `No leads match "${leadSearch}". Try a different name.`
                    : filteredEmpty
                      ? 'Try a different period, or click the selected filter again to show all leads.'
                    : !isOwner
                      ? 'No leads have been enrolled in this campaign yet.'
                      : sourceTab === 'leadsearch'
                        ? (hasPreferences
                            ? 'Pick people on the Search results tab and add them to this campaign.'
                            : 'Set targeting filters to search for leads.')
                        : !hasPreferences
                          ? 'Add leads via LD Contacts or CSV, or set targeting preferences to use LeadSearch.'
                          : 'Use the tabs above to add leads via LeadSearch, LD Contacts, or CSV.'}
                </HintText>
                {isOwner && !embedded && !leadSearch && !filteredEmpty && !hasPreferences && (
                  <ActionBtn type="button" onClick={goToTargetingStep} style={{ alignSelf: 'center', marginTop: 4 }}>
                    Set targeting preferences
                  </ActionBtn>
                )}
              </EmptyBox>
            ) : (
              <>
                <DesktopTableWrap>
                  <Table>
                    <colgroup>
                      <col style={{ width: 36 }} />
                      <col />
                      <col style={{ width: '15%' }} />
                      <col style={{ width: '14%' }} />
                      {showLeadStatus && <col style={{ width: '12%' }} />}
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: 148 }} />
                    </colgroup>
                    <THead>
                      <tr>
                        <ThCheckbox>
                          <LeadsSelectAllHeader {...selectionProps} />
                        </ThCheckbox>
                        <Th>Lead</Th>
                        <Th style={{ width: '14%' }}>Company</Th>
                        {/* Only worth a column when the sequence actually emails people —
                            a LinkedIn-only campaign shouldn't pay for the width. */}
                        {sequenceHasEmailStep && <Th style={{ width: '16%' }}>Email</Th>}
                        <Th style={{ width: '14%' }}>Location</Th>
                        {showLeadStatus && (
                          <Th style={{ width: '12%' }} onClick={e => e.stopPropagation()}>
                            {(selectedLeadIds.length > 0 || isAllLeadsSelected) ? (
                              <BulkStatusHeaderPicker
                                disabled={!isOwner || bulkStatusChanging}
                                onSelect={handleBulkStatusPick}
                              />
                            ) : 'Status'}
                          </Th>
                        )}
                        <Th style={{ width: '10%' }}>Source</Th>
                        <Th style={{ width: '10%' }}>Enrolled</Th>
                        <ThActions onClick={e => e.stopPropagation()}>
                          <BulkLeadActionCell {...selectionProps} />
                        </ThActions>
                      </tr>
                    </THead>
                    <tbody>
                      {leads.map(lead => {
                        const profileUrl = classicLinkedInProfileUrl(lead);
                        return (
                        <TRow key={lead._id} $selected={selectedLead?._id === lead._id} onClick={() => setSelectedLead(prev => prev?._id === lead._id ? null : lead)}>
                          <TdCheckbox onClick={e => e.stopPropagation()}>
                            <StyledCheckbox
                              checked={isAllLeadsSelected || selectedLeadIds.includes(lead._id)}
                              onClickHandler={(e: React.MouseEvent) => { e.stopPropagation(); selectLeadCheckboxHandler(lead._id); }}
                              checkboxId={`campaign-lead-${lead._id}`}
                            />
                          </TdCheckbox>
                          <Td>
                            <Row alignItems gap="10px">
                              <CampaignAvatar imageUrl={lead.imageUrl} name={lead.name} fallback={lead.publicIdentifier} size={36} />
                              <Column gap="1px" style={{ minWidth: 0 }}>
                                <LeadName>
                                  {profileUrl
                                    ? <a href={profileUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>{lead.name || lead.publicIdentifier || '—'}</a>
                                    : (lead.name || lead.publicIdentifier || '—')}
                                </LeadName>
                                <LeadSub>{lead.title || '—'}</LeadSub>
                              </Column>
                            </Row>
                          </Td>
                          <Td><CellText>{lead.company || '—'}</CellText></Td>
                          {sequenceHasEmailStep && (
                            <Td onClick={e => e.stopPropagation()}>
                              {editingEmailFor === lead._id ? (
                                <EmailEditRow>
                                  <EmailInput
                                    autoFocus
                                    type="email"
                                    placeholder="name@company.com"
                                    value={emailDraft}
                                    onChange={e => setEmailDraft(e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') saveLeadEmail(lead);
                                      if (e.key === 'Escape') setEditingEmailFor(null);
                                    }}
                                  />
                                  {/* 'checkmark' is the bare tick. 'check' is a filled
                                      purple disc with the tick baked in, which sits
                                      inside a button as a second circle. */}
                                  <EmailIconBtn type="button" title="Save" onClick={() => saveLeadEmail(lead)}>
                                    <SvgFragment type="checkmark" />
                                  </EmailIconBtn>
                                </EmailEditRow>
                              ) : lead.email ? (
                                <EmailCell onClick={() => startEditEmail(lead)} title="Click to edit">
                                  <CellText>{lead.email}</CellText>
                                  {/* A bare address would be misleading once it's dead or
                                      opted out — say so instead of implying it's sendable. */}
                                  {lead.emailStatus === 'bounced' && <EmailFlag $tone="bad">Bounced</EmailFlag>}
                                  {lead.emailStatus === 'suppressed' && <EmailFlag $tone="bad">Unsubscribed</EmailFlag>}
                                  {lead.emailStatus !== 'bounced' && lead.emailStatus !== 'suppressed' && lead.emailType && (
                                    <EmailFlag $tone="muted">{lead.emailType === 'private' ? 'Personal' : 'Work'}</EmailFlag>
                                  )}
                                </EmailCell>
                              ) : (
                                <AddEmailBtn type="button" onClick={() => startEditEmail(lead)}>
                                  + Add email
                                </AddEmailBtn>
                              )}
                            </Td>
                          )}
                          <Td><CellText $muted>{lead.location || '—'}</CellText></Td>
                          {showLeadStatus && (
                            <Td onClick={e => e.stopPropagation()}>
                              <StatusPicker value={lead.status} onChange={s => handleStatusChange(lead, s)} suppressedTitle={leadSuppressionTitle(lead)} />
                            </Td>
                          )}
                          <Td>
                            {lead.source && SOURCE_CFG[lead.source] && (
                              <SourceBadge $color={SOURCE_CFG[lead.source].color} $bg={SOURCE_CFG[lead.source].bg}>
                                {SOURCE_CFG[lead.source].label}
                              </SourceBadge>
                            )}
                          </Td>
                          <Td><DateCell>{lead.enrolledAt ? new Date(lead.enrolledAt).toLocaleDateString() : '—'}</DateCell></Td>
                          <TdActions onClick={e => e.stopPropagation()}>
                            <LeadRowActionCell
                              lead={lead}
                              sequence={campaignSequence}
                              onEditMessage={isOwner ? handleEditLeadMessage : undefined}
                              onRetry={handleRetry}
                              onMove={isOwner ? openMoveLeads : undefined}
                              onRemove={handleRemove}
                              retrying={retryingIds.has(lead._id)}
                              removing={removingIds.has(lead._id)}
                            />
                          </TdActions>
                        </TRow>
                        );
                      })}
                    </tbody>
                  </Table>
                </DesktopTableWrap>
                <MobileLeadList>
                  <MobileLeadListHeader>
                    <LeadsSelectAllHeader {...selectionProps} />
                    <LeadRowActions>
                      {selectionProps.bulkDeleteCount > 0 && (
                        <LeadMoveIconBtn
                          onClick={() => openMoveLeads()}
                          tooltip={`Move ${selectionProps.bulkDeleteCount} lead${selectionProps.bulkDeleteCount === 1 ? '' : 's'}`}
                        />
                      )}
                      {selectionProps.bulkDeleteCount > 0 && (
                        <LeadDeleteIconBtn
                          onClick={handleBulkRemove}
                          disabled={bulkRemoving}
                          danger
                          tooltip={`Remove ${selectionProps.bulkDeleteCount} lead${selectionProps.bulkDeleteCount === 1 ? '' : 's'}`}
                        />
                      )}
                      <ActionSlot>
                        {selectionProps.bulkRetryCount > 0 && (
                          <LeadRetryIconBtn
                            onClick={handleBulkRetry}
                            disabled={bulkRetrying}
                            tooltip={`Retry ${selectionProps.bulkRetryCount} failed lead${selectionProps.bulkRetryCount === 1 ? '' : 's'}`}
                          />
                        )}
                      </ActionSlot>
                    </LeadRowActions>
                  </MobileLeadListHeader>
                  {leads.map(lead => (
                    <MobileLeadCard
                      key={lead._id}
                      $selected={selectedLead?._id === lead._id}
                      onClick={() => setSelectedLead(prev => prev?._id === lead._id ? null : lead)}
                    >
                      <MobileLeadCheckbox onClick={e => e.stopPropagation()}>
                        <StyledCheckbox
                          checked={isAllLeadsSelected || selectedLeadIds.includes(lead._id)}
                          onClickHandler={(e: React.MouseEvent) => { e.stopPropagation(); selectLeadCheckboxHandler(lead._id); }}
                          checkboxId={`mobile-campaign-lead-${lead._id}`}
                        />
                      </MobileLeadCheckbox>
                      <CampaignAvatar imageUrl={lead.imageUrl} name={lead.name} fallback={lead.publicIdentifier} size={40} />
                      <MobileLeadBody>
                        <LeadName>
                          {lead.name || lead.publicIdentifier || '—'}
                        </LeadName>
                        <LeadSub>{[lead.title, lead.company].filter(Boolean).join(' · ') || '—'}</LeadSub>
                        <MobileLeadMeta onClick={e => e.stopPropagation()}>
                          {showLeadStatus && (
                            <StatusPicker value={lead.status} onChange={s => handleStatusChange(lead, s)} suppressedTitle={leadSuppressionTitle(lead)} />
                          )}
                          {lead.source && SOURCE_CFG[lead.source] && (
                            <SourceBadge $color={SOURCE_CFG[lead.source].color} $bg={SOURCE_CFG[lead.source].bg}>
                              {SOURCE_CFG[lead.source].label}
                            </SourceBadge>
                          )}
                        </MobileLeadMeta>
                      </MobileLeadBody>
                      <MobileLeadActions onClick={e => e.stopPropagation()}>
                        <LeadRowActionCell
                          lead={lead}
                          sequence={campaignSequence}
                          onEditMessage={isOwner ? handleEditLeadMessage : undefined}
                          onRetry={handleRetry}
                          onMove={isOwner ? openMoveLeads : undefined}
                          onRemove={handleRemove}
                          retrying={retryingIds.has(lead._id)}
                          removing={removingIds.has(lead._id)}
                        />
                      </MobileLeadActions>
                    </MobileLeadCard>
                  ))}
                </MobileLeadList>
                {leadsPaginationBar}
              </>
            )}
          </TableArea>
          {messageEdit && (
            <LeadMessageEditModal
              show
              onHide={() => setMessageEdit(null)}
              campaign={campaign}
              lead={messageEdit.lead}
              stepIndex={messageEdit.stepIndex}
              step={messageEdit.step}
              onSaved={handleLeadMessageSaved}
            />
          )}
          {selectedLead && (
            <SidebarSlot onClick={() => setSelectedLead(null)}>
              <SidebarInner onClick={e => e.stopPropagation()}>
                <CampaignLeadSidebar
                  lead={selectedLead}
                  campaign={campaign}
                  proxyAccounts={proxyAccounts}
                  onClose={() => setSelectedLead(null)}
                  onSuppress={handleSuppressFromLead}
                />
              </SidebarInner>
            </SidebarSlot>
          )}
        </TableAndSidebar>}

        {!embedded && (sourceTab === 'all' || sourceTab === 'leadsearch') && total > 0 && (
          <PanelFooter>
            <HintText>
              {leads.length} of {total} lead{total === 1 ? '' : 's'}
              {leadSearch ? ' matching search' : ''}
            </HintText>
          </PanelFooter>
        )}

        </PanelBody>
    </Panel>
  );

  return embedded ? inner : (fullPage ? inner : <Overlay onClick={onClose}>{inner}</Overlay>);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Never hand React a non-string. The provider returns `company` as `{ name }` — and as a
 * bare `{}` when it cannot resolve an employer — which React refuses to render, taking the
 * whole panel down with it. Module scope so the row filter can use it too.
 */
function fieldText(value: any): string {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  if (typeof value === 'object') return typeof value.name === 'string' ? value.name : '';
  return String(value);
}

function getInitials(name: string, fallback?: string): string {
  const src = name || fallback || '';
  return src ? src.split(/[\s\-_]+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() : '?';
}

const CampaignAvatar: React.FC<{ imageUrl?: string; name: string; fallback?: string; size?: number }> = ({
  imageUrl, name, fallback, size = 30,
}) => {
  const [imgFailed, setImgFailed] = React.useState(false);
  const showImg = !!imageUrl && !imgFailed;
  return (
    <AvatarCircle $size={size} $hasImage={showImg}>
      {showImg
        ? <img src={imageUrl} alt="" onError={() => setImgFailed(true)} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
        : getInitials(name, fallback)
      }
    </AvatarCircle>
  );
};

const AvatarCircle = styled.div<{ $size: number; $hasImage: boolean }>`
  width: ${({ $size }) => $size}px; height: ${({ $size }) => $size}px;
  border-radius: 50%; flex-shrink: 0; overflow: hidden;
  background: ${({ $hasImage }) => $hasImage ? 'transparent' : styles.colors.primary100};
  color: ${styles.colors.primary600}; font-family: NunitoSans; font-weight: 800;
  font-size: ${({ $size }) => Math.round($size * 0.36)}px;
  display: flex; align-items: center; justify-content: center;
  border: 1.5px solid ${({ $hasImage }) => $hasImage ? 'transparent' : styles.colors.primary200};
`;

// ─── EnrolledPanel sub-component ─────────────────────────────────────────────

// ─── Selection toolbar + enrolled list ───────────────────────────────────────

const LeadMoveIconBtn: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  tooltip: string;
}> = ({ onClick, disabled, tooltip }) => (
  <TooltipWrapper tooltipText={tooltip} placement="top">
    <IconActionBtn type="button" onClick={onClick} disabled={disabled} $stroke>
      <SvgFragment type="moveLead" />
    </IconActionBtn>
  </TooltipWrapper>
);

const LeadDeleteIconBtn: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  tooltip: string;
  danger?: boolean;
}> = ({ onClick, disabled, tooltip, danger }) => (
  <TooltipWrapper tooltipText={tooltip} placement="top">
    <IconActionBtn type="button" onClick={onClick} disabled={disabled} $danger={danger}>
      <SvgFragment type="trashCan" />
    </IconActionBtn>
  </TooltipWrapper>
);

const LeadRetryIconBtn: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  tooltip: string;
}> = ({ onClick, disabled, tooltip }) => (
  <TooltipWrapper tooltipText={tooltip} placement="top">
    <IconActionBtn type="button" onClick={onClick} disabled={disabled} $accent>
      <SvgFragment type="refresh" />
    </IconActionBtn>
  </TooltipWrapper>
);

const LeadMessageIconBtn: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  tooltip: string;
  customized?: boolean;
}> = ({ onClick, disabled, tooltip, customized }) => (
  <TooltipWrapper tooltipText={tooltip} placement="top">
    <IconActionBtn type="button" onClick={onClick} disabled={disabled} $accent={customized}>
      <SvgFragment type="message" />
    </IconActionBtn>
  </TooltipWrapper>
);

// Per-row actions: message edit, move, delete, retry (retry only for failed leads).
const LeadRowActionCell: React.FC<{
  lead: ICampaignLead;
  sequence: ISequenceStep[];
  onEditMessage?: (lead: ICampaignLead) => void;
  onMove?: (lead: ICampaignLead) => void;
  onRetry: (lead: ICampaignLead) => void;
  onRemove: (lead: ICampaignLead) => void;
  retrying: boolean;
  removing: boolean;
}> = ({ lead, sequence, onEditMessage, onMove, onRetry, onRemove, retrying, removing }) => {
  const upcoming = getUpcomingTextStep(lead, sequence);
  const hasCustom = upcoming ? leadHasStepContentOverride(lead, upcoming.stepIndex) : false;
  return (
    <LeadRowActions>
      <ActionSlot>
        {upcoming && onEditMessage && (
          <LeadMessageIconBtn
            onClick={() => onEditMessage(lead)}
            tooltip={hasCustom ? 'Custom message — click to edit' : 'Preview / edit message for this lead'}
            customized={hasCustom}
          />
        )}
      </ActionSlot>
      <ActionSlot>
        {onMove && (
          <LeadMoveIconBtn
            onClick={() => onMove(lead)}
            tooltip="Move to another campaign"
          />
        )}
      </ActionSlot>
      <LeadDeleteIconBtn
        onClick={() => onRemove(lead)}
        disabled={removing}
        tooltip="Remove lead"
      />
      <ActionSlot>
        {isRetryable(lead) && (
          <LeadRetryIconBtn
            onClick={() => onRetry(lead)}
            disabled={retrying}
            tooltip={lead.status === 'suppressed'
              ? 'Put back into the campaign (remove its suppression-list entry first)'
              : 'Retry failed lead'}
          />
        )}
      </ActionSlot>
    </LeadRowActions>
  );
};

/**
 * Header counterpart of LeadRowActionCell. It must render the *same four slots* in the
 * same order — message, move, delete, retry — or the bulk retry sits one slot left of
 * the retry icons in the rows below it.
 */
// Only the bulk retry lives here, so with nothing to retry the cell stays empty — its
// 32px slots would otherwise make the header row as tall as a lead row.
const BulkLeadActionCell: React.FC<LeadsSelectionProps & { deleteDisabled?: boolean }> = ({
  bulkRetryCount, bulkRetrying, onBulkRetry,
}) => (bulkRetryCount === 0 ? null : (
  <LeadRowActions>
    <ActionSlot />
    {/* Bulk move and removal are labelled buttons in the panel header. Empty slots keep
        this column aligned with the row icons. */}
    <ActionSlot />
    <ActionSlot />
    <ActionSlot>
      <LeadRetryIconBtn
        onClick={onBulkRetry}
        disabled={bulkRetrying}
        tooltip={`Retry ${bulkRetryCount} failed lead${bulkRetryCount === 1 ? '' : 's'}`}
      />
    </ActionSlot>
  </LeadRowActions>
));

interface LeadsSelectionProps {
  allOnPageSelected: boolean;
  pageSize: number;
  total: number;
  paginated: number;
  isAllSelected: boolean;
  selectedCount: number;
  bulkDeleteCount: number;
  bulkRemoving: boolean;
  bulkRetryCount: number;
  bulkRetrying: boolean;
  onSelectAll: () => void;
  onSelectPage: () => void;
  onClearSelection: () => void;
  onBulkRemove: () => void;
  onBulkMove: () => void;
  onBulkRetry: () => void;
}

const LeadsSelectAllHeader: React.FC<LeadsSelectionProps> = ({
  allOnPageSelected, pageSize, total, paginated, isAllSelected, selectedCount,
  onSelectAll, onSelectPage, onClearSelection,
}) => (
  <CheckboxHeaderWrap onClick={e => e.stopPropagation()}>
    <SelectAllCheckbox
      isCheckboxChecked={allOnPageSelected}
      pageSize={pageSize}
      total={total}
      paginated={paginated}
      isAllSelected={isAllSelected}
      selected={selectedCount}
      label="leads"
      selectAllHandler={onSelectAll}
      selectOnThisPageHadler={onSelectPage}
      clearHandler={onClearSelection}
      backgroundColor={styles.colors.white}
    />
  </CheckboxHeaderWrap>
);

interface EnrolledPanelProps {
  leads: ICampaignLead[];
  loading: boolean;
  emptyMessage: string;
  sequence: ISequenceStep[];
  onEditMessage?: (lead: ICampaignLead) => void;
  onStatusChange: (lead: ICampaignLead, status: CampaignLeadStatus) => void;
  onRemove: (lead: ICampaignLead) => void;
  onMove?: (lead: ICampaignLead) => void;
  onRetry: (lead: ICampaignLead) => void;
  removingIds: Set<string>;
  retryingIds: Set<string>;
  selection: LeadsSelectionProps;
  selectedLeadIds: string[];
  isAllSelected: boolean;
  onSelectLead: (id: string) => void;
  /** Page bar for the shared leads list — same one the main table renders. */
  footer?: React.ReactNode;
  /** Off while recipients are being assembled: every lead is still "Pending". */
  showStatus?: boolean;
}

const EnrolledPanel: React.FC<EnrolledPanelProps> = ({
  leads, loading, emptyMessage, sequence, onEditMessage, onStatusChange, onMove, onRemove, onRetry, removingIds, retryingIds,
  selection, selectedLeadIds, isAllSelected, onSelectLead, footer, showStatus = true,
}) => {
  // Avoid disruptive spinner flashes on background refresh — only show loading
  // when there's no data to render yet.
  if (loading && !leads.length) return <EnrolledWrap><HintText>Loading…</HintText></EnrolledWrap>;
  if (!leads.length) return (
    <EnrolledWrap style={{ flexDirection: 'column', alignItems: 'center', gap: 6, padding: '24px 0' }}>
      <HintText>{emptyMessage}</HintText>
    </EnrolledWrap>
  );
  return (
    <EnrolledWrap>
      <EnrolledHeaderRow>
        <EnrolledCheckboxCell>
          <LeadsSelectAllHeader {...selection} />
        </EnrolledCheckboxCell>
        <EnrolledHeaderLabel>Lead</EnrolledHeaderLabel>
        <BulkActionsSlot>
          <BulkLeadActionCell {...selection} />
          <MoveLeadsBtn
            type="button"
            onClick={selection.onBulkMove}
            disabled={selection.bulkDeleteCount === 0}
          >
            <SvgFragment type="moveLead" />
            {`Move${selection.bulkDeleteCount ? ` (${selection.bulkDeleteCount})` : ''}`}
          </MoveLeadsBtn>
          <RemoveLeadsBtn
            type="button"
            onClick={selection.onBulkRemove}
            disabled={selection.bulkRemoving || selection.bulkDeleteCount === 0}
          >
            <SvgFragment type="trashCan" />
            {selection.bulkRemoving
              ? 'Removing…'
              : `Remove leads${selection.bulkDeleteCount ? ` (${selection.bulkDeleteCount})` : ''}`}
          </RemoveLeadsBtn>
        </BulkActionsSlot>
      </EnrolledHeaderRow>
      {leads.map(lead => {
        const profileUrl = classicLinkedInProfileUrl(lead);
        return (
        <EnrolledItem key={lead._id}>
          <EnrolledCheckboxCell>
            <StyledCheckbox
              checked={isAllSelected || selectedLeadIds.includes(lead._id)}
              onClickHandler={(e: React.MouseEvent) => { e.stopPropagation(); onSelectLead(lead._id); }}
              checkboxId={`enrolled-lead-${lead._id}`}
            />
          </EnrolledCheckboxCell>
          <CampaignAvatar imageUrl={lead.imageUrl} name={lead.name} fallback={lead.publicIdentifier} size={36} />
          <Column gap="1px" style={{ flex: 1, minWidth: 0 }}>
            <LeadName>
              {profileUrl
                ? <a href={profileUrl} target="_blank" rel="noopener noreferrer">{lead.name || lead.publicIdentifier || '—'}</a>
                : (lead.name || lead.publicIdentifier || '—')}
            </LeadName>
            <LeadSub>{[lead.title, lead.company].filter(Boolean).join(' · ') || '—'}</LeadSub>
          </Column>
          {showStatus && <StatusPicker value={lead.status} onChange={s => onStatusChange(lead, s)} suppressedTitle={leadSuppressionTitle(lead)} />}
          <LeadRowActionCell
            lead={lead}
            sequence={sequence}
            onEditMessage={onEditMessage}
            onMove={onMove}
            onRetry={onRetry}
            onRemove={onRemove}
            retrying={retryingIds.has(lead._id)}
            removing={removingIds.has(lead._id)}
          />
        </EnrolledItem>
        );
      })}
      {footer}
    </EnrolledWrap>
  );
};

// ═══════════════════════════════════════════════════════════
// Styled Components
// ═══════════════════════════════════════════════════════════

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(10,15,30,0.45); backdrop-filter: blur(2px);
  z-index: 210; display: flex; align-items: flex-start; justify-content: center;
  padding: 28px 16px; overflow-y: auto;
`;

const Panel = styled.div<{ $fullPage?: boolean; $embedded?: boolean }>`
  background: ${styles.colors.white};
  ${({ $embedded }) => $embedded && `
    width: 100%; border: none; box-shadow: none; max-height: none; border-radius: 0;
  `}
  ${({ $fullPage, $embedded }) => !$embedded && ($fullPage ? `
    width: 100%; height: 100%; border-radius: 0; box-shadow: none; max-height: none;
  ` : `
    border-radius: 20px; width: 100%; max-width: 960px; max-height: calc(100vh - 56px);
    box-shadow: 0 32px 80px rgba(0,0,0,0.22);
  `)}
  display: flex; flex-direction: column; overflow: hidden;
`;

const PanelBody = styled.div<{ $scrollable?: boolean }>`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow-x: hidden;
  ${({ $scrollable }) => $scrollable ? `
    overflow-y: auto;
    padding-bottom: 20px;
    scrollbar-width: thin;
    scrollbar-color: #dde3ee transparent;
    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-thumb { background: #dde3ee; border-radius: 3px; }
  ` : `
    overflow: hidden;
  `}
`;

const EmailCoverageWrap = styled.div`
  margin: 16px 24px 3px;
  ${campaignMobile`margin: 12px 12px;`}
`;

const LeadsSourceHelper = styled.div`
  margin: 0 24px 16px;
  padding: 16px 18px;
  border-radius: 10px;
  border: 1.5px solid ${styles.colors.primary300};
  background: ${styles.colors.primary100};
  ${campaignMobile`margin: 0 12px 12px; padding: 14px;`}
`;

const LeadsSourceHelperHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

const LeadsSourceHelperTitle = styled.div`
  font-family: NunitoSans;
  font-size: 14px;
  font-weight: 800;
  color: ${styles.colors.black600};
  flex: 1;
  min-width: 0;
`;

const LeadsSourceHelperClose = styled.button`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  margin: -4px -4px 0 0;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${styles.colors.black300};
  .svg { width: 16px; height: 16px; }
  .path { fill: currentColor !important; }
  &:hover {
    background: rgba(255, 255, 255, 0.7);
    color: ${styles.colors.black600};
  }
`;

const LeadsSourceHelperList = styled.ul`
  margin: 0 0 10px;
  padding-left: 18px;
  font-family: NunitoSans;
  font-size: 13px;
  line-height: 1.55;
  color: ${styles.colors.black400};

  li + li { margin-top: 4px; }
`;

const LeadsSourceHelperNote = styled.div`
  margin-bottom: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.65);
  border: 1px solid ${styles.colors.primary200};
  font-family: NunitoSans;
  font-size: 12px;
  line-height: 1.5;
  color: ${styles.colors.black500};
`;

const LeadsSourceHelperActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const LeadsSourceHelperLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  color: ${styles.colors.primary600};
  text-decoration: underline;
  &:hover { color: ${styles.colors.primary400}; }
`;

const LeadsSourceHelperSep = styled.span`
  color: ${styles.colors.black300};
  font-size: 13px;
  user-select: none;
`;

const SimBadge = styled.span`
  padding: 3px 10px; border-radius: 20px;
  background: ${styles.colors.yellow200}; color: ${styles.colors.orange600};
  font-family: NunitoSans; font-size: 11px; font-weight: 700;
  cursor: default;
`;

const BackBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  background: none; border: none; cursor: pointer; padding: 0;
  font-family: NunitoSans; font-size: 13px; font-weight: 600;
  color: ${styles.colors.black400};
  .svg { width: 16px; height: 16px; }
  .path { fill: ${styles.colors.black400} !important; }
  &:hover { color: ${styles.colors.primary600}; .path { fill: ${styles.colors.primary600} !important; } }
`;

const TooltipBtnWrap = styled.span`display: inline-flex;`;

const HeaderActionBtn = styled.button<{ $variant: 'success' | 'warning' | 'ghost' | 'danger' | 'edit' }>`
  height: 28px; padding: 0 10px; border-radius: 6px;
  font-size: 12px; font-weight: 700; font-family: NunitoSans;
  cursor: pointer; border: none; display: inline-flex; align-items: center;
  justify-content: center; gap: 5px; line-height: 1;
  svg { display: block; flex-shrink: 0; }
  ${({ $variant }) => $variant === 'success' && `background: ${styles.colors.green200}; color: ${styles.colors.green600}; .path { stroke: ${styles.colors.green600} !important; fill: none !important; } &:hover:not(:disabled) { filter: brightness(0.95); }`}
  ${({ $variant }) => $variant === 'warning' && `background: ${styles.colors.yellow200}; color: ${styles.colors.orange600}; .path { stroke: ${styles.colors.orange600} !important; fill: none !important; } &:hover:not(:disabled) { filter: brightness(0.95); }`}
  ${({ $variant }) => $variant === 'ghost'   && `background: ${styles.colors.black100}; color: ${styles.colors.black400}; .path { stroke: ${styles.colors.black400} !important; fill: none !important; } &:hover:not(:disabled) { background: ${styles.colors.black200}; }`}
  ${({ $variant }) => $variant === 'danger'  && `background: ${styles.colors.red600}; color: white; &:hover:not(:disabled) { background: ${styles.colors.red500}; }`}
  ${({ $variant }) => $variant === 'edit'    && `background: transparent; color: ${styles.colors.primary600}; border: 1px solid ${styles.colors.primary200}; &:hover:not(:disabled) { background: ${styles.colors.primary100}; }`}
  &:disabled { opacity: 0.38; cursor: default; filter: none; }
  ${campaignMobile`height: 32px; padding: 0 8px; font-size: 11px;`}
`;

const PanelHeader = styled(Row)`
  padding: 18px 24px; border-bottom: 1px solid #eef2f7; flex-shrink: 0;
  ${campaignMobile`
    padding: 14px 12px;
    flex-direction: column;
    align-items: flex-start !important;
    gap: 10px;
  `}
`;

const RunTimerPill = styled.div<{ $running: boolean }>`
  height: 28px;
  padding: 0 10px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid ${({ $running }) => ($running ? styles.colors.primary200 : '#e2e8f0')};
  background: ${({ $running }) => ($running ? styles.colors.primary100 : '#f8fafc')};
  color: ${({ $running }) => ($running ? styles.colors.primary600 : styles.colors.black400)};
  font-size: 12px;
  font-weight: 700;
  font-family: NunitoSans;
  user-select: none;
`;

const RunTimerDot = styled.span<{ $running: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $running }) => ($running ? styles.colors.primary600 : '#94a3b8')};
  box-shadow: ${({ $running }) => ($running ? '0 0 0 3px rgba(89,100,255,0.12)' : 'none')};
`;

const HeaderIcon = styled.div`
  width: 40px; height: 40px; border-radius: 12px; background: ${styles.colors.primary600};
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(89,100,255,0.3);
  .svg { width: 18px; height: 18px; } .path { stroke: white !important; fill: none !important; }
`;

const CloseBtn = styled.button`
  width: 32px; height: 32px; border: none; background: transparent; border-radius: 8px; cursor: pointer;
  display: flex; align-items: center; justify-content: center; color: ${styles.colors.black300};
  .svg { width: 18px; height: 18px; } .path { fill: currentColor !important; }
  &:hover { background: ${styles.colors.black100}; color: ${styles.colors.black600}; }
`;

const FunnelRow = styled.div`
  display: flex; align-items: center; padding: 12px 24px;
  background: #fafbff; border-bottom: 1px solid #eef2f7; gap: 2px; flex-shrink: 0;
  ${campaignMobile`
    padding: 10px 12px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  `}
`;

const MessagingStatsRow = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 12px 18px;
  padding: 8px 24px; background: ${styles.colors.white};
  border-bottom: 1px solid #eef2f7; flex-shrink: 0;
  ${campaignMobile`padding: 8px 12px;`}
`;

const MessagingStat = styled.div<{ $clickable?: boolean; $accent?: boolean }>`
  display: flex; align-items: baseline; gap: 6px;
  ${({ $clickable }) => $clickable ? 'cursor: pointer;' : ''}
  ${({ $accent }) => $accent ? `padding: 2px 8px; border-radius: 8px; background: ${styles.colors.primary100};` : ''}
`;

const MessagingStatVal = styled.span<{ $color?: string }>`
  font-size: 14px; font-weight: 700;
  color: ${({ $color }) => $color || styles.colors.black600};
`;

const MessagingStatLabel = styled.span`
  font-size: 12px; color: ${styles.colors.black400};
`;

const LeadsSearchRow = styled(Row)`
  padding: 8px 20px;
  background: ${styles.colors.white};
  flex-shrink: 0;
  ${campaignMobile`padding: 8px 12px;`}
`;

const LeadsSearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  max-width: 320px;
  height: 36px;
  padding: 0 8px 0 10px;
  border: 1.5px solid #dde3ee;
  border-radius: 10px;
  background: ${styles.colors.white};
  transition: border-color 0.15s, box-shadow 0.15s;
  ${campaignMobile`max-width: none;`}

  &:focus-within {
    border-color: ${styles.colors.primary600};
    box-shadow: 0 0 0 3px rgba(89, 100, 255, 0.08);
  }
`;

const SearchIconBox = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  line-height: 0;

  .svg { width: 16px; height: 16px; }
  .path { stroke: ${styles.colors.black300} !important; fill: none !important; }
`;

const LeadsSearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  font-family: NunitoSans;
  font-size: 13px;
  color: ${styles.colors.black600};

  &::placeholder { color: ${styles.colors.black300}; }
  &::-webkit-search-cancel-button { display: none; }
`;

const LeadsSearchClear = styled.button`
  border: none;
  background: transparent;
  color: ${styles.colors.black300};
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;

  &:hover { color: ${styles.colors.black600}; }
`;

const FunnelItem = styled.div<{ $active: boolean }>`
  flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px;
  padding: 8px 4px 7px; border-radius: 11px; cursor: pointer; min-width: 0;
  background: ${({ $active }) => $active ? styles.colors.primary100 : 'transparent'};
  border: 1.5px solid ${({ $active }) => $active ? styles.colors.primary200 : 'transparent'};
  transition: all 0.14s ease;
  &:hover { background: ${styles.colors.primary100}; border-color: ${styles.colors.primary200}; transform: translateY(-1px); }
  ${campaignMobile`flex: 0 0 auto; min-width: 56px; &:hover { transform: none; }`}
`;

const FunnelCount = styled.span<{ $color: string }>`
  font-family: NunitoSans; font-size: 20px; font-weight: 800; line-height: 1;
  color: ${({ $color }) => $color};
`;

const FunnelLabel = styled.span`
  font-family: NunitoSans; font-size: 9px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.09em; color: ${styles.colors.black300};
  text-align: center; overflow-wrap: anywhere;
`;

const ActivityBar = styled.div`
  display: flex; flex-direction: column; gap: 10px; flex-shrink: 0;
  padding: 12px 0; border-bottom: 1px solid #eef2f7;
  ${campaignMobile`padding: 10px 0;`}
`;

const ActivityChips = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px;
  padding: 0 24px;
  ${campaignMobile`padding: 0 16px;`}
`;

// The analytics cards, shrunk to chips: same icon tile and colour, the count of leads.
const ActivityChip = styled.button<{ $active: boolean; $color: string }>`
  display: inline-flex; align-items: center; gap: 6px;
  height: 30px; padding: 0 10px 0 4px; border-radius: 100px; cursor: pointer;
  border: 1px solid ${({ $active, $color }) => ($active ? $color : '#E5E7EB')};
  background: ${({ $active }) => ($active ? '#F9FAFF' : styles.colors.white)};
  box-shadow: ${({ $active, $color }) => ($active ? `0 0 0 1px ${$color}` : 'none')};
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 13px; line-height: 18px;
  color: #414651; white-space: nowrap;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:hover { border-color: ${({ $color }) => $color}; }
`;

const ChipDivider = styled.span`
  width: 1px; height: 20px; margin: 0 4px; align-self: center; background: #E5E7EB;
`;

const ActivityChipIcon = styled.span`
  width: 22px; height: 22px; flex-shrink: 0; border: 1px solid; border-radius: 100px;
  display: inline-flex; align-items: center; justify-content: center;
`;

const ActivityChipCount = styled.span`
  font-family: 'InterVariable', 'Inter', NunitoSans, sans-serif; font-weight: 600;
  font-variant-numeric: tabular-nums; color: #252B37;
`;

const FunnelHeading = styled.div`
  display: flex; align-items: center; gap: 6px;
  padding: 10px 24px 0; background: #fafbff;
  font-family: NunitoSans; font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em; color: ${styles.colors.black300};
  ${campaignMobile`padding: 8px 12px 0;`}
`;

const FunnelHeadingInfo = styled.span`
  width: 14px; height: 14px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid ${styles.colors.black300}; cursor: help;
  font-size: 9px; line-height: 1; letter-spacing: 0;
`;

const FunnelArrow = styled.span<{ $subtle?: boolean }>`
  color: ${({ $subtle }) => $subtle ? '#cdd5e6' : '#dde3ee'};
  font-size: ${({ $subtle }) => $subtle ? '12px' : '16px'}; margin: 0 2px; user-select: none;
`;

// Final-outcome cluster — visually grouped apart from the engagement funnel
const OutcomeGroup = styled.div`
  flex: 3; display: flex; align-items: center; justify-content: space-around; gap: 2px;
  margin-left: 8px; padding: 3px 6px; border-radius: 13px;
  background: #f6f7fb; border: 1px solid #eceff6;
  ${campaignMobile`flex: 0 0 auto;`}
`;

// Total — a distinct pill set apart from the status items
const TotalItem = styled.div<{ $active: boolean }>`
  flex: 0 0 auto; display: flex; flex-direction: column; align-items: center; gap: 4px;
  padding: 8px 18px; margin-left: 10px; border-radius: 12px; cursor: pointer;
  background: ${({ $active }) => $active ? styles.colors.primary100 : '#f4f6ff'};
  border: 1.5px solid ${({ $active }) => $active ? styles.colors.primary600 : styles.colors.primary200};
  transition: all 0.14s ease;
  &:hover { border-color: ${styles.colors.primary600}; transform: translateY(-1px); }
  ${campaignMobile`
    margin-left: 4px;
    padding: 8px 14px;
    &:hover { transform: none; }
  `}
`;

// Source tabs
const SourceArea = styled.div<{ $flush?: boolean }>`flex-shrink: 0`;

const SourceTabBar = styled.div`
  display: flex; gap: 0; padding: 0 16px; border-bottom: 1px solid #eef2f7; overflow-x: auto;
  scrollbar-width: none; &::-webkit-scrollbar { display: none; }
`;

const SrcTab = styled.button.attrs({ type: 'button' as const })<{ $active: boolean }>`
  display: inline-flex; align-items: center; gap: 6px;
  height: 38px; padding: 0 14px; border: none; background: transparent; cursor: pointer;
  font-family: NunitoSans; font-size: 13px; font-weight: 600; white-space: nowrap;
  color: ${({ $active }) => $active ? styles.colors.primary600 : styles.colors.black400};
  border-bottom: 2px solid ${({ $active }) => $active ? styles.colors.primary600 : 'transparent'};
  margin-bottom: -1px; transition: all 0.14s;
  .svg { width: 13px; height: 13px; } .path { stroke: currentColor !important; fill: none !important; }
  &:hover { color: ${styles.colors.primary600}; }
`;

const SrcPanel = styled.div`
  padding: 12px 20px;
  ${campaignMobile`padding: 10px 12px;`}
`;

// Shared buttons
const ActionBtn = styled.button.attrs({ type: 'button' as const })`
  height: 34px; padding: 0 16px;
  background: ${styles.colors.primary600}; color: ${styles.colors.white};
  border: none; border-radius: 8px; font-size: 13px; font-weight: 700; font-family: NunitoSans;
  cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  align-self: flex-start; width: fit-content; max-width: 100%;
  box-shadow: 0 2px 8px rgba(89,100,255,0.2); transition: all 0.15s;
  &:hover:not(:disabled) { background: ${styles.colors.primary400}; transform: translateY(-1px); }
  &:disabled { background: ${styles.colors.black200}; cursor: default; box-shadow: none; transform: none; }
`;

const GhostBtn = styled.button.attrs({ type: 'button' as const })`
  height: 34px; padding: 0 14px; background: transparent; color: ${styles.colors.black400};
  border: 1.5px solid #dde3ee; border-radius: 8px;
  font-size: 12px; font-weight: 600; font-family: NunitoSans; cursor: pointer;
  align-self: flex-start; width: fit-content; max-width: 100%;
  &:hover:not(:disabled) { background: ${styles.colors.black100}; border-color: ${styles.colors.black300}; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

// ── LeadSearch picker (Search results tab)
const ProspectsPanel = styled.div`min-height: 300px;`;

const ProspectsHeader = styled.div<{ $inPanelBody?: boolean }>`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
  flex-wrap: wrap; padding: 4px 2px 12px;
  /* Above the lead table it is a direct child of the panel body, which has no padding of
     its own — match the source panel's gutters so it lines up with the table below. */
  ${({ $inPanelBody }) => $inPanelBody && `
    padding: 12px 20px 4px;
    flex-shrink: 0;
  `}
  ${({ $inPanelBody }) => $inPanelBody && campaignMobile`padding: 10px 12px 4px;`}
`;

const ProspectsTitle = styled.span`
  font-family: NunitoSans; font-size: 15px; font-weight: 700; color: ${styles.colors.black600};
`;

const ProspectsSub = styled.span`
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black300};
`;

// One row: the box and the button wrapped onto separate lines in a narrow panel, which
// read as two unrelated controls. They only stack on real mobile widths.
const ProspectsHeaderRight = styled.div`
  display: flex; align-items: center; justify-content: flex-end; gap: 8px;
  flex-wrap: nowrap; margin-left: auto; min-width: 0; flex: 1 1 auto;
  /* LeadsSearchWrap is width:100% for the standalone search row. In this cluster
     that width pushes Move and Remove past the panel edge, where overflow clips them. */
  ${LeadsSearchWrap} {
    width: auto;
    flex: 1 1 180px;
    min-width: 140px;
    max-width: 280px;
  }
  ${campaignMobile`width: 100%; flex: 1 1 100%;`}
`;

// Same shape as the enrolled-list search box, so the two tabs read as one control set.
// It shrinks before the button does, rather than pushing it onto its own line.
const ProspectsSearchWrap = styled.div`
  display: flex; align-items: center; gap: 6px;
  flex: 1 1 200px; min-width: 120px; max-width: 260px; height: 36px; padding: 0 8px 0 10px;
  border: 1.5px solid #dde3ee; border-radius: 10px; background: ${styles.colors.white};
  transition: border-color 0.15s;
  &:focus-within { border-color: ${styles.colors.primary600}; }
  ${campaignMobile`max-width: none;`}
`;

// Counterpart of AddToCampaignBtn on the enrolled tab: same size and position, red outline
// because it destroys something. Labelled rather than a bare bin — bulk removal is worth
// naming, and this is the one place it is offered.
const MoveLeadsBtn = styled.button`
  display: inline-flex; align-items: center; gap: 7px; flex-shrink: 0;
  height: 36px; padding: 0 14px; border-radius: 8px; cursor: pointer;
  border: 1px solid ${styles.colors.primary600}; background: white;
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: ${styles.colors.primary600};
  white-space: nowrap;
  .svg  { width: 15px; height: 15px; }
  .path { fill: none; stroke: ${styles.colors.primary600}; }
  &:hover:not(:disabled) { background: ${styles.colors.primary100}; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const ExportLeadsBtn = styled.button`
  display: inline-flex; align-items: center; gap: 7px; flex-shrink: 0;
  height: 36px; padding: 0 14px; border-radius: 8px; cursor: pointer;
  border: 1px solid ${styles.colors.primary600}; background: white;
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: ${styles.colors.primary600};
  white-space: nowrap;
  &:hover:not(:disabled) { background: ${styles.colors.primary100}; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const QueueInMailBtn = styled.button`
  display: inline-flex; align-items: center; flex-shrink: 0;
  height: 36px; padding: 0 14px; border-radius: 8px; cursor: pointer;
  border: 1px solid ${styles.colors.primary600}; background: ${styles.colors.primary600};
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: white;
  white-space: nowrap;
  &:hover:not(:disabled) { filter: brightness(0.96); }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const RemoveLeadsBtn = styled.button`
  display: inline-flex; align-items: center; gap: 7px; flex-shrink: 0;
  height: 36px; padding: 0 14px; border-radius: 8px; cursor: pointer;
  border: 1px solid ${styles.colors.red600}; background: white;
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: ${styles.colors.red600};
  white-space: nowrap;
  .svg  { width: 15px; height: 15px; }
  .path { fill: ${styles.colors.red600}; }
  &:hover:not(:disabled) { background: ${styles.colors.red200}; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const AddToCampaignBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  height: 36px; padding: 0 14px; border: none; border-radius: 8px; cursor: pointer;
  background: ${styles.colors.primary600}; color: white;
  font-family: NunitoSans; font-size: 13px; font-weight: 700; white-space: nowrap;
  &:hover:not(:disabled) { filter: brightness(1.05); }
  &:disabled { opacity: 0.45; cursor: default; }
`;

// No card around the list: the rows and the page bar carry their own separators, and the
// extra frame boxed the whole strip in.
const ProspectsResults = styled.div`
  border-top: 1px solid #eef2f7;
`;

// Header and rows share one column template, so every checkbox, name and column lines up
// and the row ends where the header does instead of leaving dead space on the right.
const prospectGrid = `
  display: grid;
  grid-template-columns: 26px 36px minmax(0, 1fr) minmax(0, 22%) minmax(0, 22%) 32px;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
`;

const ProspectHeaderRow = styled.div`
  ${prospectGrid}
  height: 40px;
  background: #fafbff; border-bottom: 1px solid #eef2f7;
  position: relative;
  overflow: visible;
`;

// Absolute, so the select-all menu's counter ("20 SELECTED (CLEAR)") can be wider than
// the 26px checkbox column without taking part in the grid — mirrors CheckboxHeaderWrap
// in the lead table.
const ProspectSelectAllAnchor = styled.div`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  white-space: nowrap;
`;

const ProspectHeaderLabel = styled.span`
  font-family: NunitoSans; font-size: 10px; font-weight: 800; letter-spacing: 0.06em;
  text-transform: uppercase; color: ${styles.colors.black300};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;

const ProspectHeaderMeta = styled(ProspectHeaderLabel)`
  ${campaignMobile`display: none;`}
`;

const ProspectRow = styled.div<{ $disabled?: boolean }>`
  ${prospectGrid}
  min-height: 56px;
  border-bottom: 1px solid #f4f6fb;
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
  &:last-child { border-bottom: none; }
  &:hover { background: #fafbff; }
  &:hover ${() => ProspectDismissBtn} { opacity: 1; }
`;

const ProspectMeta = styled.span<{ $muted?: boolean }>`
  min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-family: NunitoSans; font-size: 12px;
  color: ${({ $muted }) => ($muted ? styles.colors.black300 : styles.colors.black500)};
  ${campaignMobile`display: none;`}
`;

const ProspectCheckboxCell = styled.div`
  display: flex; align-items: center; justify-content: flex-start;
`;

// Rejecting a candidate, not deleting a lead — nothing is stored, so it is a quiet ✕
// rather than a red bin, and it only shows on hover.
const ProspectDismissBtn = styled.button`
  width: 28px; height: 28px; border-radius: 7px; border: none; background: transparent;
  cursor: pointer; color: ${styles.colors.black300};
  font-family: NunitoSans; font-size: 13px; line-height: 1;
  opacity: 0;
  transition: opacity 0.12s, background 0.12s, color 0.12s;
  &:hover { background: ${styles.colors.black100}; color: ${styles.colors.black500}; }
  &:focus-visible { opacity: 1; }
`;


const ProspectsEmptyBox = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 40px 24px; text-align: center;
`;

// GhostBtn pins itself with `align-self: flex-start`, which beat the box's centring and
// left the button hanging on the far left of the message.
const ProspectsRetryBtn = styled(GhostBtn)`
  align-self: center;
  margin-top: 10px;
`;

const LeadSearchEmptyTitle = styled.div`
  font-family: NunitoSans; font-size: 14px; font-weight: 700; color: ${styles.colors.black600};
  margin-bottom: 6px;
`;

const LeadSearchEmptyText = styled.div`
  font-family: NunitoSans; font-size: 12px; line-height: 1.55; color: ${styles.colors.black400};
`;

const HintText = styled.span`font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black300};`;

const Spinner = styled.span`
  width: 12px; height: 12px; border: 2px solid rgba(255,255,255,0.35); border-top-color: white;
  border-radius: 50%; display: inline-block; animation: spin 0.7s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// The button Spinner is white — invisible on the panel's own background.
const InlineSpinner = styled(Spinner)`
  border-color: ${styles.colors.black200};
  border-top-color: ${styles.colors.primary600};
`;

// Sub-tabs
const SubTabBar = styled.div`
  display: flex; gap: 4px; margin-bottom: 10px;
  border-bottom: 1px solid #eef2f7; padding-bottom: 0;
`;

const SubTab = styled.button.attrs({ type: 'button' as const })<{ $active: boolean }>`
  display: inline-flex; align-items: center; gap: 6px;
  height: 32px; padding: 0 12px; border: none; background: transparent; cursor: pointer;
  font-family: NunitoSans; font-size: 12px; font-weight: 600; white-space: nowrap;
  color: ${({ $active }) => $active ? styles.colors.primary600 : styles.colors.black400};
  border-bottom: 2px solid ${({ $active }) => $active ? styles.colors.primary600 : 'transparent'};
  margin-bottom: -1px; transition: all 0.14s;
  &:hover { color: ${styles.colors.primary600}; }
`;

const SubTabCount = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px;
  background: ${styles.colors.primary600}; color: ${styles.colors.white};
  border-radius: 20px; font-size: 10px; font-weight: 800; font-family: NunitoSans;
`;

/* No max-height / inner scroll: a 380px box with a pale scrollbar showed ~6 rows and
   hid the rest of the page (and its pagination bar), so leads looked missing. The wizard's
   FormBody already scrolls; the list grows with it. */
const EnrolledWrap = styled.div`
  display: flex; flex-direction: column;
  border: 1px solid #eef2f7; border-radius: 10px; overflow: hidden;
  min-height: 60px;
`;

const EnrolledHeaderRow = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 10px 12px 10px 0px;
  background: #fafbff; border-bottom: 1px solid #eef2f7;
  position: sticky; top: 0; z-index: 1;
`;

const EnrolledHeaderLabel = styled.span`
  flex: 1;
  min-width: 0;
  font-family: NunitoSans; font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: 0.09em; color: ${styles.colors.black300};
`;

const BulkActionsSlot = styled.div`
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LeadRowActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

// Reserves the retry button's footprint even when no button is rendered, so the
// delete button lines up across rows regardless of lead status.
const ActionSlot = styled.div`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const EnrolledItem = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 8px 12px 8px 8px;
  border-bottom: 1px solid #f4f6fb;
  &:last-child { border-bottom: none; }
  &:hover { background: #fafbff; }
`;

// Contacts
const ContactsPanel = styled.div`min-height: 300px;`;

const SearchToolbar = styled.div`
  padding: 12px 14px;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  background: #f8f9fc;
`;

const ContactsEmptyState = styled.div`
  margin-top: 12px;
  display: flex; flex-direction: column; align-items: center; text-align: center;
  gap: 8px; padding: 40px 24px;
  border: 1px dashed #dfe4ee; border-radius: 12px;
  background: linear-gradient(180deg, #fafbff 0%, ${styles.colors.white} 100%);
`;

const ContactsEmptyIcon = styled.div`
  display: flex; align-items: center; justify-content: center;
  width: 48px; height: 48px; border-radius: 14px; margin-bottom: 2px;
  background: ${styles.colors.primary100};
  .svg { width: 22px; height: 22px; }
  .path { stroke: ${styles.colors.primary600} !important; fill: none !important; }
`;

const ContactsEmptyTitle = styled.span`
  font-family: NunitoSans; font-size: 14px; font-weight: 800; color: ${styles.colors.black600};
`;

const ContactsEmptyText = styled.span`
  font-family: NunitoSans; font-size: 12.5px; line-height: 1.55; color: ${styles.colors.black400};
  max-width: 380px;
`;

const FilterSection = styled.div`display: flex; flex-direction: column; gap: 5px;`;

const FilterLabel = styled.span`
  font-family: NunitoSans; font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: 0.08em; color: ${styles.colors.black300};
`;



const ContactResults = styled.div`
  margin-top: 8px; border: 1px solid #eef2f7; border-radius: 12px; overflow: hidden;
  min-height: 120px; max-height: 440px; overflow-y: auto;
  scrollbar-width: thin; scrollbar-color: #dde3ee transparent;
`;

const ContactResultHeader = styled(Row)`
  justify-content: space-between;
  padding: 10px 14px; background: #fafbff; border-bottom: 1px solid #eef2f7; flex-shrink: 0;
  position: sticky; top: 0; z-index: 1;
`;

const ResultCount = styled.span`
  font-family: NunitoSans; font-size: 12.5px; font-weight: 800; color: ${styles.colors.black600};
`;

const ResultDim = styled.span`
  font-weight: 600; color: ${styles.colors.black300};
`;

const SelectedPill = styled.span`
  padding: 3px 10px; border-radius: 20px; font-family: NunitoSans;
  font-size: 11px; font-weight: 800; white-space: nowrap;
  color: ${styles.colors.primary600}; background: ${styles.colors.primary100};
`;

const LoadMoreRow = styled.div`
  display: flex; justify-content: center;
  padding: 12px; border-top: 1px solid #eef2f7;
  & > button { align-self: center; }
`;

const ContactCheckbox = styled.input`
  width: 15px; height: 15px; cursor: pointer; accent-color: ${styles.colors.primary600};
`;

const ContactRow = styled.div<{ $selected: boolean; $disabled: boolean }>`
  display: flex; align-items: center; gap: 12px; padding: 10px 14px;
  background: ${({ $selected }) => $selected ? styles.colors.primary100 : 'transparent'};
  opacity: ${({ $disabled }) => $disabled ? 0.5 : 1};
  border-bottom: 1px solid #f4f6fb;
  transition: background 0.1s;
  &:last-child { border-bottom: none; }
  &:hover { background: ${({ $disabled }) => $disabled ? 'transparent' : '#fafbff'}; }
`;

const ContactAvatar = styled.div<{ $hasImage: boolean }>`
  width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
  ${({ $hasImage }) => $hasImage ? 'background-size: cover; background-position: center;' : `background: ${styles.colors.primary100};`}
  color: ${styles.colors.primary600}; font-family: NunitoSans; font-size: 10px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  border: 1.5px solid ${({ $hasImage }) => $hasImage ? 'transparent' : styles.colors.primary200};
`;

const ContactName = styled.a`
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: ${styles.colors.black600};
  text-decoration: none; display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  &:hover { color: ${styles.colors.primary600}; }
`;

const ContactSub = styled.span`
  font-family: NunitoSans; font-size: 11px; color: ${styles.colors.black300};
  display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const MiniTag = styled.span<{ $color: string; $bg: string }>`
  padding: 2px 7px; border-radius: 20px; font-family: NunitoSans;
  font-size: 10px; font-weight: 700; white-space: nowrap;
  color: ${({ $color }) => $color}; background: ${({ $bg }) => $bg};
`;

// CSV
const CsvPanel = styled.div`display: flex; flex-direction: column; gap: 12px; min-height: 300px;`;

const CsvHeader = styled.p`
  margin: 0; font-family: NunitoSans; font-size: 12.5px; line-height: 1.5;
  color: ${styles.colors.black400};
`;

const CsvLayout = styled.div`
  display: flex; gap: 12px; align-items: stretch;
  ${campaignMobile`flex-direction: column;`}
`;

const CsvTextarea = styled.textarea`
  flex: 1; width: 100%; padding: 14px 16px; resize: none; min-height: 240px;
  border: 1.5px solid #dde3ee; border-radius: 12px;
  font-family: 'Courier New', monospace; font-size: 13px; line-height: 1.7;
  color: ${styles.colors.black600}; outline: none; box-sizing: border-box;
  background: ${styles.colors.white};
  &:focus { border-color: ${styles.colors.primary600}; box-shadow: 0 0 0 3px rgba(89,100,255,0.08); }
  &::placeholder { color: #b0b8cc; font-family: NunitoSans; font-size: 12px; line-height: 1.8; }
`;

const CsvActions = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
`;

const CsvDetectedBadge = styled.span`
  margin-left: auto; padding: 3px 10px;
  background: ${styles.colors.green200}; color: ${styles.colors.green600};
  border-radius: 20px; font-family: NunitoSans; font-size: 11px; font-weight: 700;
`;

const SnSearchUrlSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const SnSourceDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 18px 0 14px;
  color: ${styles.colors.black300};
  font-family: NunitoSans;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e5e9f2;
  }
`;

const SnListGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;
  margin-top: 14px;
`;

const SnListCard = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 12px 14px;
  text-align: left;
  cursor: pointer;
  border-radius: 10px;
  border: 1.5px solid ${({ $active }) => ($active ? styles.colors.primary600 : '#dde3ee')};
  background: ${({ $active }) => ($active ? 'rgba(89,100,255,0.06)' : styles.colors.white)};
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover:not(:disabled) { border-color: ${styles.colors.primary600}; }
  &:disabled { opacity: 0.65; cursor: not-allowed; }
`;

const SnListName = styled.span`
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  color: ${styles.colors.black600};
  word-break: break-word;
`;

const SnListMeta = styled.span`
  font-family: NunitoSans;
  font-size: 11px;
  color: ${styles.colors.black400};
`;

const PostUrlInvalidBadge = styled(CsvDetectedBadge)`
  background: ${styles.colors.red200};
  color: ${styles.colors.red600};
`;

const PostsTrialLimitCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 14px 12px;
  background: #F6F8FB;
  border: 1px solid #E8EBF2;
  border-radius: 12px;
`;

const PostsTrialLimitTitle = styled.div`
  font-family: NunitoSans;
  font-size: 14px;
  font-weight: 800;
  line-height: 20px;
  color: ${styles.colors.black600};
`;

const PostsTrialLimitText = styled.div`
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  color: ${styles.colors.black300};
`;

const PostsTrialLimitLink = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  padding: 0;
  margin-top: 4px;
  align-self: flex-start;
  cursor: pointer;
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  color: ${styles.colors.primary600};
  &:hover { color: ${styles.colors.primary400}; }
`;

const PostsTrialHint = styled.span`
  font-family: NunitoSans;
  font-size: 12px;
  font-weight: 600;
  color: ${styles.colors.black300};
`;

const PostUrlLayout = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 10px;
  min-height: 0;

  ${ActionBtn} {
    flex-shrink: 0;
    align-self: stretch;
    height: auto;
    min-height: 34px;
    white-space: nowrap;
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const PostUrlInput = styled.input<{ $valid: boolean; $dirty: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  width: auto;
  padding: 8px 10px;
  box-sizing: border-box;
  border: 1.5px solid ${({ $valid, $dirty }) => (
    !$dirty ? '#dde3ee' : $valid ? styles.colors.green600 : styles.colors.red600
  )};
  border-radius: 12px;
  font-family: 'Courier New', monospace; font-size: 13px; line-height: 1.5;
  color: ${styles.colors.black600}; outline: none;
  background: ${styles.colors.white};
  &:focus {
    border-color: ${styles.colors.primary600};
    box-shadow: 0 0 0 3px rgba(89,100,255,0.08);
  }
  &::placeholder { color: #b0b8cc; font-family: NunitoSans; font-size: 12px; }
`;

const CsvDropZone = styled.button.attrs({ type: 'button' as const })`
  width: 190px; flex-shrink: 0; align-self: stretch;
  border: 2px dashed #c8d0e8; border-radius: 12px;
  background: #f8f9ff; cursor: pointer; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 8px; padding: 16px;
  transition: all 0.15s;
  &:hover {
    border-color: ${styles.colors.primary600}; background: ${styles.colors.primary100};
    transform: translateY(-1px);
    .path { stroke: ${styles.colors.primary600} !important; }
  }
  ${campaignMobile`width: 100%; min-height: 108px;`}
`;

const CsvDropIcon = styled.span`
  display: flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; border-radius: 12px;
  background: ${styles.colors.white}; box-shadow: 0 2px 6px rgba(20,30,80,0.06);
  .svg { width: 22px; height: 22px; }
  .path { stroke: #8892b0 !important; fill: none !important; }
`;

const CsvDropLabel = styled.span`
  font-family: NunitoSans; font-size: 14px; font-weight: 800; color: ${styles.colors.black600};
`;

const CsvDropSub = styled.span`
  font-family: NunitoSans; font-size: 12px; line-height: 1.5; text-align: center; color: ${styles.colors.black400};
`;

// Table
const TableAndSidebar = styled.div<{ $inScrollBody?: boolean }>`
  display: flex;
  width: 100%;
  min-height: 0;
  overflow: hidden;
  ${({ $inScrollBody }) => $inScrollBody ? `
    flex: 0 0 auto;
    overflow: visible;
  ` : `
    flex: 1;
  `}
  ${campaignMobile`flex-direction: column;`}
`;

const DesktopTableWrap = styled.div`
  display: block;
  ${campaignMobile`display: none;`}
`;

const MobileLeadList = styled.div`
  display: none;
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px 16px;
  ${campaignMobile`display: flex;`}
`;

const MobileLeadListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 0 8px;
  border-bottom: 1px solid #eef2f7;
`;

const MobileLeadCard = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  border-radius: 12px;
  border: 1.5px solid ${({ $selected }) => $selected ? styles.colors.primary300 : '#eef2f7'};
  background: ${({ $selected }) => $selected ? styles.colors.primary100 : styles.colors.white};
  cursor: pointer;
`;

const MobileLeadCheckbox = styled.div`flex-shrink: 0; padding-top: 2px;`;

const MobileLeadBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MobileLeadMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
`;

const MobileLeadActions = styled.div`flex-shrink: 0;`;

const SidebarSlot = styled.div`
  flex-shrink: 0;
  height: 100%;
  ${campaignMobile`
    position: fixed;
    inset: 0;
    z-index: 220;
    height: auto;
    background: rgba(10, 15, 30, 0.45);
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  `}
`;

const SidebarInner = styled.div`
  height: 100%;
  ${campaignMobile`
    width: 100%;
    max-height: 92vh;
    display: flex;
    flex-direction: column;
  `}
`;

const TableArea = styled.div<{ $inScrollBody?: boolean; $stale?: boolean }>`
  width: 100%;
  min-width: 0;
  overflow-x: hidden;
  ${({ $stale }) => $stale ? `
    opacity: 0.45;
    pointer-events: none;
    transition: opacity 0.15s ease;
  ` : 'transition: opacity 0.15s ease;'}
  ${({ $inScrollBody }) => $inScrollBody ? `
    flex: 1 1 auto;
    overflow-y: visible;
  ` : `
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #dde3ee transparent;
    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-thumb { background: #dde3ee; border-radius: 3px; }
  `}
`;

const EmptyBox = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 48px 24px; gap: 8px;
`;

const EmptyIllustration = styled.div`
  width: 48px; height: 48px; border-radius: 14px; background: ${styles.colors.primary100};
  display: flex; align-items: center; justify-content: center; margin-bottom: 4px;
  .svg { width: 22px; height: 22px; } .path { stroke: ${styles.colors.primary300} !important; fill: none !important; }
`;

const EmptyTitle = styled.span`font-family: NunitoSans; font-size: 15px; font-weight: 700; color: ${styles.colors.black500};`;

const Table = styled.table`width: 100%; border-collapse: collapse; table-layout: fixed;`;

const THead = styled.thead`
  position: sticky; top: 0; z-index: 2; background: ${styles.colors.white};
  overflow: visible;
  &::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: #eef2f7; }
`;

const Th = styled.th`
  padding: 10px 12px; text-align: left;
  font-family: NunitoSans; font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: 0.09em; color: ${styles.colors.black300};
  &:first-child { padding-left: 16px; }
`;

const ThCheckbox = styled(Th)`
  width: 36px;
  min-width: 36px;
  max-width: 36px;
  padding: 10px 2px 10px 12px;
  box-sizing: border-box;
  position: relative;
  overflow: visible;
  &:first-child { padding-left: 12px; }
`;

const CheckboxHeaderWrap = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: normal;
`;

const ThActions = styled(Th)`
  width: 148px;
  min-width: 148px;
  max-width: 148px;
  padding: 10px 6px;
  box-sizing: border-box;
  text-align: center;
  vertical-align: middle;
`;

const TdActions = styled.td`
  width: 148px;
  min-width: 148px;
  max-width: 148px;
  padding: 10px 6px;
  box-sizing: border-box;
  text-align: center;
  vertical-align: middle;
`;

const TRow = styled.tr<{ $selected?: boolean }>`
  border-bottom: 1px solid #f4f6fb; transition: background 0.1s; cursor: pointer;
  background: ${({ $selected }) => $selected ? '#f0f4ff' : 'transparent'};
  &:hover { background: ${({ $selected }) => $selected ? '#e8eeff' : '#fafbff'}; }
  &:last-child { border-bottom: none; }
`;

const Td = styled.td`
  padding: 10px 12px; vertical-align: middle; overflow: hidden;
  &:first-child { padding-left: 16px; }
`;

const TdCheckbox = styled(Td)`
  width: 36px;
  min-width: 36px;
  max-width: 36px;
  padding: 10px 2px 10px 12px;
  box-sizing: border-box;
  &:first-child { padding-left: 12px; }
`;

const EnrolledCheckboxCell = styled.div`
  width: 36px;
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 4px;
  min-height: 20px;
`;

const Avatar = styled.div<{ $hasImage: boolean }>`
  width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0;
  ${({ $hasImage }) => $hasImage ? 'background-size: cover; background-position: center;' : `background: ${styles.colors.primary100};`}
  color: ${styles.colors.primary600}; font-family: NunitoSans; font-size: 11px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid ${({ $hasImage }) => $hasImage ? 'transparent' : styles.colors.primary200};
`;

const LeadName = styled.span`
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: ${styles.colors.black600};
  display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0;
  a { color: inherit; text-decoration: none; &:hover { color: ${styles.colors.primary600}; } }
`;

const LeadSub = styled.span`
  font-family: NunitoSans; font-size: 11px; color: ${styles.colors.black300};
  display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0;
`;

const CellText = styled.span<{ $muted?: boolean }>`
  font-family: NunitoSans; font-size: 12px; min-width: 0;
  color: ${({ $muted }) => $muted ? styles.colors.black300 : styles.colors.black500};
  display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const EmailFlag = styled.span<{ $tone: 'bad' | 'muted' }>`
  font-family: NunitoSans; font-size: 10px; font-weight: 600; line-height: 1.3;
  color: ${({ $tone }) => ($tone === 'bad' ? '#b91c1c' : styles.colors.black300)};
`;

const EmailCell = styled.div`
  display: flex; flex-direction: column; gap: 1px; min-width: 0;
  cursor: text; border-radius: 4px; padding: 2px 4px; margin: -2px -4px;
  &:hover { background: #f1f5f9; }
`;

const AddEmailBtn = styled.button`
  border: none; background: none; padding: 2px 4px; margin: -2px -4px; cursor: pointer;
  border-radius: 4px;
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black300};
  &:hover { background: #f1f5f9; color: ${styles.colors.primary600}; }
`;

const EmailEditRow = styled.div`
  display: flex; align-items: center; gap: 4px; min-width: 0;
`;

const EmailInput = styled.input`
  flex: 1; min-width: 0; box-sizing: border-box;
  border: 1px solid ${styles.colors.primary600}; border-radius: 4px; padding: 4px 6px;
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black500};
  &:focus { outline: none; }
`;

const EmailIconBtn = styled.button`
  border: none; background: none; padding: 2px; cursor: pointer;
  display: flex; align-items: center; flex: 0 0 auto;
  svg, .svg { width: 14px; height: 14px; display: block; }
  /* The tick is drawn with fill, not stroke — recolouring via stroke does nothing. */
  .path { fill: ${styles.colors.primary600}; }
`;

const SourceBadge = styled.span<{ $color: string; $bg: string }>`
  padding: 3px 8px; border-radius: 20px; font-family: NunitoSans; font-size: 10px; font-weight: 700;
  color: ${({ $color }) => $color}; background: ${({ $bg }) => $bg}; white-space: nowrap;
`;

const DateCell = styled.span`font-family: NunitoSans; font-size: 11px; color: ${styles.colors.black300};`;

const IconActionBtn = styled.button.attrs({ type: 'button' as const })<{ $danger?: boolean; $accent?: boolean; $stroke?: boolean }>`
  width: 32px; height: 32px; border: none; background: transparent;
  border-radius: 7px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
  .svg { width: 18px; height: 18px; }
  .path {
    fill: ${({ $danger, $accent, $stroke }) =>
      $stroke ? 'none' : $danger ? styles.colors.red600 : $accent ? styles.colors.primary600 : styles.colors.black300} !important;
    ${({ $stroke, $danger, $accent }) => $stroke ? `stroke: ${
      $danger ? styles.colors.red600 : $accent ? styles.colors.primary600 : styles.colors.black300
    } !important;` : ''}
  }
  &:hover {
    background: ${({ $danger, $accent }) =>
      $danger ? styles.colors.red200 : $accent ? styles.colors.primary100 : styles.colors.black100};
    .path {
      fill: ${({ $danger, $accent, $stroke }) =>
        $stroke ? 'none' : $danger ? styles.colors.red600 : $accent ? styles.colors.primary600 : styles.colors.black600} !important;
      ${({ $stroke, $danger, $accent }) => $stroke ? `stroke: ${
        $danger ? styles.colors.red600 : $accent ? styles.colors.primary600 : styles.colors.black600
      } !important;` : ''}
    }
  }
  &:disabled { cursor: default; opacity: 0.5; }
  &:disabled:hover {
    background: transparent;
    .path {
      fill: ${({ $danger, $accent, $stroke }) =>
        $stroke ? 'none' : $danger ? styles.colors.red600 : $accent ? styles.colors.primary600 : styles.colors.black300} !important;
      ${({ $stroke, $danger, $accent }) => $stroke ? `stroke: ${
        $danger ? styles.colors.red600 : $accent ? styles.colors.primary600 : styles.colors.black300
      } !important;` : ''}
    }
  }
`;

const PanelFooter = styled.div`
  padding: 10px 24px; border-top: 1px solid #eef2f7; background: #fafbff; flex-shrink: 0;
`;

// Status picker
const StatusWrap = styled.div`position: relative; display: inline-block;`;

const StatusBadgeBtn = styled.button<{ $bg: string; $color: string }>`
  height: 26px; padding: 0 10px; background: ${({ $bg }) => $bg}; color: ${({ $color }) => $color};
  border: none; border-radius: 20px; cursor: pointer; font-family: NunitoSans; font-size: 11px; font-weight: 700;
  text-transform: none; letter-spacing: normal;
  display: inline-flex; align-items: center; gap: 5px; white-space: nowrap;
  &:hover { filter: brightness(0.95); }
  &:disabled { cursor: default; opacity: 0.6; }
`;

const StatusDot = styled.span<{ $color: string }>`
  width: 6px; height: 6px; border-radius: 50%; background: ${({ $color }) => $color}; flex-shrink: 0;
`;

const StatusChevron = styled.span`font-size: 10px; opacity: 0.6; margin-left: 1px;`;

const StatusMenu = styled.div`
  position: fixed; z-index: 500;
  background: ${styles.colors.white}; border: 1px solid #dde3ee; border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12); padding: 4px; min-width: 130px;
`;

const StatusMenuItem = styled.div<{ $active: boolean }>`
  display: flex; align-items: center; gap: 8px; padding: 7px 10px; border-radius: 8px; cursor: pointer;
  font-family: NunitoSans; font-size: 12px; font-weight: 600;
  text-transform: none; letter-spacing: normal;
  color: ${({ $active }) => $active ? styles.colors.primary600 : styles.colors.black600};
  background: ${({ $active }) => $active ? styles.colors.primary100 : 'transparent'};
  &:hover { background: ${styles.colors.primary100}; color: ${styles.colors.primary600}; }
`;

const StyledCheckbox = styled(Checkbox)`
  height: fit-content;
`;

export default CampaignLeadsView;

/**
 * The campaign page's Leads tab: the status counts as one slim row of chips (count and
 * label side by side) instead of tall stacked tiles. Same items, same click-to-filter.
 * Defined last so it can restyle the funnel pieces above without changing them.
 */
const CompactFunnel = styled.div<{ $compact?: boolean }>`
  flex-shrink: 0;
  ${({ $compact }) => $compact && css`
    ${FunnelRow} { padding: 8px 20px; gap: 2px; flex-wrap: wrap; }
    ${FunnelItem}, ${TotalItem} {
      flex: 0 0 auto; flex-direction: row; align-items: baseline; gap: 5px;
      padding: 4px 10px; border-radius: 100px;
      &:hover { transform: none; }
    }
    ${TotalItem} { margin-left: 6px; border-width: 1px; }
    ${FunnelCount} { font-size: 14px; }
    ${FunnelLabel} { font-size: 10px; letter-spacing: 0.06em; white-space: nowrap; }
    ${FunnelArrow} { margin: 0 1px; }
    ${OutcomeGroup} { flex: 0 0 auto; margin-left: 6px; padding: 1px 4px; border-radius: 100px; }
  `}
`;

